import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import socketService from "socket/newsocketService";
import { getDatabase, ref, onValue, update } from "firebase/database";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, IconButton, InputLabel, Menu, MenuItem, Select,
  Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, Badge, LinearProgress, Divider, Tooltip, Paper, InputAdornment,
  Avatar, CircularProgress, TablePagination,
} from "@mui/material";

import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import DirectionsBusOutlinedIcon from "@mui/icons-material/DirectionsBusOutlined";
import TrainOutlinedIcon from "@mui/icons-material/TrainOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BatteryAlertOutlinedIcon from "@mui/icons-material/BatteryAlertOutlined";
import BatteryFullOutlinedIcon from "@mui/icons-material/BatteryFullOutlined";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VideocamIcon from "@mui/icons-material/Videocam";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import PlaceIcon from "@mui/icons-material/Place";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import HistoryIcon from "@mui/icons-material/History";
import { getSubmissionType } from "Utils/file";
import videoImg from "../../assets/video.png";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import apiClient from "Utils/apiClient";
import NotifyEmergencyContactModal from "./NotifyEmergencyContactModal";
import SendMessageModal from "Utils/SendMessageModal";
import { addChatWindow } from "../../Redux/Slice/dataSlice";
import VoiceNotificationBar from "components/VoiceNotificationBar";
import { audioAlertService } from "Utils/audioService";

// ─── Constants ────────────────────────────────────────────────────────────────



const PRIORITY_CONFIG = {
  Critical:         { color: "#dc2626", bg: "#fee2e2", border: "#fca5a5", icon: "▲", label: "Critical" },
  "Needs Attention":{ color: "#d97706", bg: "#fef3c7", border: "#fcd34d", icon: "!", label: "Needs Attention" },
  Normal:           { color: "#16a34a", bg: "#dcfce7", border: "#86efac", icon: "✓", label: "Normal" },
  Resolved:         { color: "#16a34a", bg: "#dcfce7", border: "#86efac", icon: "✓", label: "Resolved" },
  Completed:        { color: "#16a34a", bg: "#dcfce7", border: "#86efac", icon: "✓", label: "Completed" },
  Cancelled:        { color: "#64748b", bg: "#f1f5f9", border: "#cbd5e1", icon: "✕", label: "Cancelled" },
};

const PRIORITY_RANK = { Critical: 0, "Needs Attention": 1, Normal: 2, Resolved: 3, Completed: 3, Cancelled: 4 };

const COMPLETED_STATUSES = ["completed", "canceled", "cancelled", "ended", "rejected", "failed", "resolved"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeEscortEntries = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return Object.values(v).filter(Boolean);
};

const toTimeMs = (v) => {
  if (!v) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "string") {
    const trimmed = v.trim();
    const n = Number(trimmed);
    if (Number.isFinite(n) && n > 0) return n;
    const p = new Date(trimmed).getTime();
    return Number.isFinite(p) ? p : 0;
  }
  return 0;
};

const getTimestampFromObjectId = (id) => {
  if (!id) return 0;
  const str = String(id);
  if (/^[0-9a-fA-F]{24}$/.test(str)) {
    const seconds = parseInt(str.substring(0, 8), 16);
    if (Number.isFinite(seconds) && seconds > 1262304000) {
      return seconds * 1000;
    }
  }
  return 0;
};

const resolveTripTimestamp = (trip, fallbackDoc) => {
  if (!trip && !fallbackDoc) return 0;
  // 1. End / completion timestamps first (for History tab sorting)
  const endCandidates = [
    trip?.endTime,
    trip?.tripendtime,
    trip?.currentTimeStamp_end,
    trip?.escortEndedAt,
    trip?.deliveredAt,
    trip?.deliveryAttestation?.deliveredAt,
    trip?.updatedAt,
  ];
  for (const c of endCandidates) {
    const t = toTimeMs(c);
    if (t > 0) return t;
  }
  // 2. Start / creation timestamps
  const startCandidates = [
    trip?.startTime,
    trip?.tripStartTime,
    trip?.currentTimeStamp,
    trip?.createdAt,
    trip?.TripDate,
    trip?.Date,
    trip?.departureDate,
    trip?.DepartureDate,
  ];
  for (const c of startCandidates) {
    const t = toTimeMs(c);
    if (t > 0) return t;
  }
  // 3. ObjectId of the trip itself
  const tripObjIdTime = getTimestampFromObjectId(trip?._id || trip?.id || trip?.key);
  if (tripObjIdTime > 0) return tripObjIdTime;

  // 4. Fallback document timestamps
  if (fallbackDoc) {
    const docCandidates = [
      fallbackDoc?.updatedAt,
      fallbackDoc?.createdAt,
      fallbackDoc?.currentTimeStamp,
    ];
    for (const c of docCandidates) {
      const t = toTimeMs(c);
      if (t > 0) return t;
    }
    const docObjIdTime = getTimestampFromObjectId(fallbackDoc?._id || fallbackDoc?.id);
    if (docObjIdTime > 0) return docObjIdTime;
  }
  return 0;
};

const resolveTripStartTime = (trip, fallbackDoc) => {
  if (!trip && !fallbackDoc) return 0;
  const startCandidates = [
    trip?.startTime,
    trip?.tripStartTime,
    trip?.currentTimeStamp,
    trip?.createdAt,
    trip?.TripDate,
    trip?.Date,
    trip?.departureDate,
    trip?.DepartureDate,
    trip?.dateOfRegistration,
    trip?.timestamp,
    fallbackDoc?.startTime,
    fallbackDoc?.tripStartTime,
    fallbackDoc?.currentTimeStamp,
    fallbackDoc?.createdAt,
    fallbackDoc?.TripDate,
    fallbackDoc?.Date,
    fallbackDoc?.departureDate,
    fallbackDoc?.DepartureDate,
    fallbackDoc?.dateOfRegistration,
    fallbackDoc?.timestamp,
    fallbackDoc?.tripData?.startTime,
    fallbackDoc?.tripData?.tripStartTime,
    fallbackDoc?.tripData?.currentTimeStamp,
    fallbackDoc?.tripData?.createdAt,
    fallbackDoc?.userData?.currentTimeStamp,
    fallbackDoc?.userData?.createdAt,
  ];
  for (const c of startCandidates) {
    const t = toTimeMs(c);
    if (t > 0) return t;
  }
  const tripObjIdTime = getTimestampFromObjectId(trip?._id || trip?.id || trip?.key);
  if (tripObjIdTime > 0) return tripObjIdTime;

  if (fallbackDoc) {
    const docObjIdTime = getTimestampFromObjectId(fallbackDoc?._id || fallbackDoc?.id || fallbackDoc?.escortDocumentId);
    if (docObjIdTime > 0) return docObjIdTime;
  }
  return 0;
};

const resolveTripEndTime = (trip) => {
  if (!trip) return 0;
  const endCandidates = [
    trip?.endTime,
    trip?.tripendtime,
    trip?.currentTimeStamp_end,
    trip?.escortEndedAt,
    trip?.deliveredAt,
    trip?.deliveryAttestation?.deliveredAt,
    trip?.updatedAt,
  ];
  for (const c of endCandidates) {
    const t = toTimeMs(c);
    if (t > 0) return t;
  }
  return 0;
};

const isDummyLocation = (loc) => {
  if (!loc || typeof loc !== "string") return true;
  const lower = loc.trim().toLowerCase();
  return (
    !lower ||
    lower === "start location" ||
    lower === "starting location" ||
    lower === "pickup location" ||
    lower === "current location" ||
    lower === "destination" ||
    lower === "delivery location" ||
    lower === "teest address" ||
    lower === "test address" ||
    lower === "teest" ||
    lower === "test" ||
    lower === "home address" ||
    lower === "home" ||
    lower === "n/a" ||
    lower === "na" ||
    lower === "null" ||
    lower === "undefined" ||
    lower === "—" ||
    lower === "-"
  );
};

const cleanLocationName = (loc) => {
  if (!loc || typeof loc !== "string") return "";
  let trimmed = loc.trim();
  if (isDummyLocation(trimmed)) return "";
  // Strip plus code like "2H3P+C3W, "
  trimmed = trimmed.replace(/^[0-9A-Z]{4,8}\+[0-9A-Z]{2,4},\s*/i, "");
  // Strip trailing country name if long
  trimmed = trimmed.replace(/,\s*Nigeria$/i, "");
  return trimmed.trim();
};

const resolveTripLocations = (trip, user, ud, stateName) => {
  const userHomeAddress = cleanLocationName(user?.address || ud?.address);

  // Trip start/pickup address MUST come from the active trip telemetry, NEVER from the user's home address!
  let rawPickup =
    trip?.currentLocation ||
    trip?.currentLocTxt ||
    trip?.currentLoc ||
    trip?.startingLocation ||
    trip?.startLocation ||
    trip?.startingAddress ||
    trip?.startAddress ||
    trip?.pickupLocation ||
    trip?.PickupLocation ||
    trip?.pickupAddress ||
    trip?.PickupAddress ||
    trip?.Place ||
    trip?.landmark ||
    trip?.DepartStation ||
    trip?.DepartureStation ||
    "";

  // If trip.address is identical to the user's registered home address, ignore it
  if (!rawPickup && trip?.address && cleanLocationName(trip.address).toLowerCase() !== userHomeAddress.toLowerCase()) {
    rawPickup = trip.address;
  }

  // If rawPickup matches user's home address or test address, clear it so live GPS is reverse-geocoded instead
  if (userHomeAddress && cleanLocationName(rawPickup).toLowerCase() === userHomeAddress.toLowerCase()) {
    rawPickup = "";
  }

  const rawDelivery =
    trip?.finalDestination ||
    trip?.destination ||
    trip?.Destination ||
    trip?.deliveryLocation ||
    trip?.DeliveryLocation ||
    trip?.destLocTxt ||
    trip?.destLoc ||
    trip?.destLocation ||
    trip?.ArrivalStation ||
    trip?.endLocation ||
    trip?.to ||
    user?.deliveryLocation ||
    user?.destLocTxt ||
    "";

  const cleanPickup = cleanLocationName(rawPickup);
  const cleanDelivery = cleanLocationName(rawDelivery);

  // Origin with ZERO hardcoded static dummy text
  let originCity = cleanPickup;
  if (!originCity) {
    const validState = (stateName && stateName !== "Unknown" && stateName !== "N/A")
      ? stateName
      : (trip?.state && trip.state !== "Unknown" ? trip.state : (user?.state && user.state !== "Unknown" ? user.state : ""));
    if (validState) {
      originCity = `${validState}`;
    } else {
      originCity = "";
    }
  }

  // Destination with ZERO hardcoded static dummy text
  let destinationCity = cleanDelivery;
  if (destinationCity.toLowerCase() === "n/a" || destinationCity.toLowerCase() === "null") {
    destinationCity = "";
  }

  return {
    originCity,
    cleanPickup,
    destinationCity,
    pickupLocation: rawPickup,
    deliveryLocation: rawDelivery,
  };
};

const resolveTripPersonName = (trip, user, ud, fbUser, escortCategory) => {
  const driverObj = (trip?.driverId && typeof trip.driverId === "object") ? trip.driverId : null;
  const driverFullName = driverObj
    ? `${driverObj.driverName || driverObj.firstName || ""} ${driverObj.surName || driverObj.lastName || ""}`.trim()
    : "";

  const tripDriverName = (trip?.driverName || trip?.DriverName || "")
    ? `${trip?.driverName || trip?.DriverName || ""} ${trip?.driverSurName || trip?.surName || ""}`.trim()
    : "";

  if (escortCategory === "Drivers Escort" || escortCategory === "Driver Escort") {
    if (driverFullName) return driverFullName;
    if (tripDriverName) return tripDriverName;
  }

  const cargoName =
    trip?.requesterName ||
    trip?.RequesterName ||
    trip?.cargoOwnerName ||
    trip?.senderName ||
    user?.requesterName ||
    user?.RequesterName;
  if (escortCategory === "Cargo Escort" && cargoName) {
    return cargoName;
  }

  const vtgName =
    trip?.payload?.userName ||
    trip?.payload?.Name ||
    trip?.userName ||
    trip?.Name;
  if (escortCategory === "Virtual Travel Guard" && vtgName && vtgName !== "Unknown") {
    return vtgName;
  }

  const userFirstLast = `${user?.firstName || ud?.firstName || fbUser?.firstName || ""} ${user?.lastName || ud?.lastName || fbUser?.lastName || ""}`.trim();
  if (userFirstLast) return userFirstLast;

  const candidateNames = [
    ud?.userName,
    user?.userName,
    fbUser?.userName,
    ud?.fullName,
    user?.fullName,
    driverFullName,
    tripDriverName,
    cargoName,
    vtgName,
    trip?.contactToNotify?.name,
    ud?.name,
    user?.name,
    fbUser?.name,
  ];

  for (const n of candidateNames) {
    if (n && typeof n === "string" && n.trim() && n.trim() !== "Unknown" && n.trim() !== "Driver" && n.trim() !== "User") {
      return n.trim();
    }
  }

  if (driverFullName) return driverFullName;
  if (tripDriverName) return tripDriverName;

  const phone = ud?.userPhone || user?.userPhone || fbUser?.userPhone || trip?.userPhone || driverObj?.phoneNumber;
  if (phone && phone !== "N/A") {
    return `User (${phone})`;
  }

  const id = trip?.escortId || user?.escortId || (trip?._id ? String(trip._id).slice(-4).toUpperCase() : null);
  if (id) {
    return `Escort #${id}`;
  }

  return "Escort Member";
};

const resolveTripPhone = (trip, user, ud, fbUser) => {
  const driverObj = (trip?.driverId && typeof trip.driverId === "object") ? trip.driverId : null;
  const candidates = [
    ud?.userPhone,
    user?.userPhone,
    fbUser?.userPhone,
    trip?.userPhone,
    trip?.phoneNumber,
    driverObj?.phoneNumber,
    trip?.contactToNotify?.phone,
    user?.phone,
    ud?.phone,
  ];
  for (const p of candidates) {
    if (p && typeof p === "string" && p.trim() && p.trim() !== "N/A") {
      return p.trim();
    }
  }
  return "N/A";
};

const getEscortTimeMs = (...sources) => {
  for (const s of sources) {
    const v = s?.createdAt || s?.updatedAt || s?.currentTimeStamp || s?.tripStartTime
              || s?.startTime || s?.TripDate || s?.Date || s?.departureDate || s?.DepartureDate;
    const t = toTimeMs(v);
    if (t) return t;
  }
  return 0;
};

const getBatteryPercent = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n <= 1 ? n * 100 : n)));
};

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return "00:00";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${String(h).padStart(2,"0")}:${String(m % 60).padStart(2,"0")}:${String(s % 60).padStart(2,"0")}`;
  return `${String(m).padStart(2,"0")}:${String(s % 60).padStart(2,"0")}`;
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  const ms = toTimeMs(v);
  if (!ms) return typeof v === "string" ? v : "—";
  const d = new Date(ms);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getStoredStoppedAt = (key) => {
  if (!key) return null;
  try {
    const val = localStorage.getItem(`stopped_${key}`);
    const num = Number(val);
    return Number.isFinite(num) && num > 0 ? num : null;
  } catch {
    return null;
  }
};

const setStoredStoppedAt = (key, time) => {
  if (!key) return;
  try {
    if (time) {
      localStorage.setItem(`stopped_${key}`, String(time));
    } else {
      localStorage.removeItem(`stopped_${key}`);
      localStorage.removeItem(`stopped_user_${key}`);
    }
  } catch {}
};

const getStoredWellbeingSentAt = (key) => {
  if (!key) return null;
  try {
    const val = localStorage.getItem(`wb_sent_${key}`);
    const num = Number(val);
    return Number.isFinite(num) && num > 0 ? num : null;
  } catch {
    return null;
  }
};

const setStoredWellbeingSentAt = (key, time) => {
  if (!key) return;
  try {
    if (time) {
      localStorage.setItem(`wb_sent_${key}`, String(time));
    } else {
      localStorage.removeItem(`wb_sent_${key}`);
      localStorage.removeItem(`wb_sent_user_${key}`);
    }
  } catch {}
};

const getStoredResponseDuration = (key) => {
  if (!key) return null;
  try {
    const val = localStorage.getItem(`wb_resp_dur_${key}`);
    const num = Number(val);
    return Number.isFinite(num) && num > 0 ? num : null;
  } catch {
    return null;
  }
};

const setStoredResponseDuration = (key, durMs) => {
  if (!key) return;
  try {
    if (durMs && durMs > 0) {
      localStorage.setItem(`wb_resp_dur_${key}`, String(durMs));
    } else {
      localStorage.removeItem(`wb_resp_dur_${key}`);
    }
  } catch {}
};

const getStoredAwaitingResponse = (key) => {
  if (!key) return null;
  try {
    const val = localStorage.getItem(`wb_awaiting_${key}`);
    const num = Number(val);
    return Number.isFinite(num) && num > 0 ? num : null;
  } catch {
    return null;
  }
};

const setStoredAwaitingResponse = (key, time) => {
  if (!key) return;
  try {
    if (time) {
      localStorage.setItem(`wb_awaiting_${key}`, String(time));
    } else {
      localStorage.removeItem(`wb_awaiting_${key}`);
      localStorage.removeItem(`wb_awaiting_user_${key}`);
    }
  } catch {}
};

// ─── Live Timer Component ──────────────────────────────────────────────────────
const LiveTimer = ({ startMs, color = "#0f172a", fontSize = "0.92rem" }) => {
  const [elapsed, setElapsed] = useState(Date.now() - (startMs || Date.now()));
  useEffect(() => {
    const id = setInterval(() => setElapsed(Date.now() - (startMs || Date.now())), 1000);
    return () => clearInterval(id);
  }, [startMs]);
  return (
    <Typography component="span" sx={{ fontWeight: 700, color, fontSize, lineHeight: 1.2 }}>
      {fmtDuration(elapsed)}
    </Typography>
  );
};

// ─── Activity Pill ─────────────────────────────────────────────────────────────
const ActivityPill = ({ activity }) => {
  let bg = "#f3f4f6", color = "#374151";
  if (activity === "Battery Low") {
    bg = "#fee2e2"; color = "#b91c1c";
  } else if (activity.includes("Phone Switched Off") || activity.includes("Lost on Radar")) {
    bg = "#fee2e2"; color = "#b91c1c";
  } else if (activity === "On Route") {
    bg = "#dcfce7"; color = "#15803d";
  } else if (activity === "SOS Triggered") {
    bg = "#fee2e2"; color = "#dc2626";
  } else if (activity === "Stopped") {
    bg = "#f3f4f6"; color = "#374151";
  } else if (activity === "Resolved" || activity === "Completed") {
    bg = "#dcfce7"; color = "#15803d";
  } else if (activity === "Cancelled") {
    bg = "#f1f5f9"; color = "#64748b";
  }

  return (
    <Box
      sx={{
        display: "inline-block",
        bgcolor: bg,
        color,
        px: 1.8,
        py: 0.6,
        borderRadius: "8px",
        fontSize: "0.78rem",
        fontWeight: 600,
        textAlign: "center",
        lineHeight: 1.3,
        whiteSpace: "pre-line",
      }}
    >
      {activity}
    </Box>
  );
};

// ─── Individual User Connection Status Cell ──────────────────────────────────
const ConnectionCell = ({ row, heartbeatMap }) => {
  const tripKey = String(row?.actualTripId || row?.escortId || row?.userId || "");
  const hb =
    heartbeatMap?.[tripKey] ||
    heartbeatMap?.[row?.actualTripId] ||
    heartbeatMap?.[row?.escortId] ||
    heartbeatMap?.[row?.userId] ||
    heartbeatMap?.[row?.id];

  const isCompleted =
    !row?.isTripActive ||
    row?.tripStatus === "completed" ||
    row?.tripStatus === "resolved" ||
    row?.tripStatus === "cancelled" ||
    row?.tripStatus === "canceled";

  if (isCompleted) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          bgcolor: "#f1f5f9",
          color: "#64748b",
          border: "1px solid #e2e8f0",
          px: 1.2,
          py: 0.45,
          borderRadius: "16px",
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#94a3b8" }} />
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: "#64748b" }}>
          Offline
        </Typography>
      </Box>
    );
  }

  const now = Date.now();
  const lastPingMs =
    hb?.server_received_at ||
    (hb?.timestamp ? hb.timestamp * 1000 : null) ||
    row?.effectiveTripTimeMs ||
    row?.startedAtMs;

  const elapsedSec = lastPingMs ? Math.max(0, Math.floor((now - lastPingMs) / 1000)) : null;

  // 1. Connection Active (< 90 seconds)
  if (elapsedSec !== null && elapsedSec < 90) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          bgcolor: "#f0fdf4",
          color: "#16a34a",
          border: "1px solid #bbf7d0",
          px: 1.2,
          py: 0.45,
          borderRadius: "16px",
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: "#22c55e",
            boxShadow: "0 0 0 2px rgba(34, 197, 94, 0.25)",
          }}
        />
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: "#15803d" }}>
          Active
        </Typography>
      </Box>
    );
  }

  // 2. Connection Delayed (90s to 5 mins)
  if (elapsedSec !== null && elapsedSec < 5 * 60) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          bgcolor: "#fefce8",
          color: "#ca8a04",
          border: "1px solid #fef08a",
          px: 1.2,
          py: 0.45,
          borderRadius: "16px",
        }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#eab308" }} />
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: "#a16207" }}>
          Delayed
        </Typography>
      </Box>
    );
  }

  // 3. Connection Warning (5 to 10 mins)
  if (elapsedSec !== null && elapsedSec < 10 * 60) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          bgcolor: "#fff7ed",
          color: "#ea580c",
          border: "1px solid #fed7aa",
          px: 1.2,
          py: 0.45,
          borderRadius: "16px",
        }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#f97316" }} />
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: "#c2410c" }}>
          Warning
        </Typography>
      </Box>
    );
  }

  // 4. Offline (> 10 mins)
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        bgcolor: "#fef2f2",
        color: "#dc2626",
        border: "1px solid #fca5a5",
        px: 1.2,
        py: 0.45,
        borderRadius: "16px",
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#ef4444" }} />
      <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: "#b91c1c" }}>
        Offline
      </Typography>
    </Box>
  );
};

// ─── Individual User Heartbeat Telemetry Cell (60s cycle) ───────────────────
const HeartbeatCell = ({ row, heartbeatMap }) => {
  const tripKey = String(row?.actualTripId || row?.escortId || row?.userId || "");
  const hb =
    heartbeatMap?.[tripKey] ||
    heartbeatMap?.[row?.actualTripId] ||
    heartbeatMap?.[row?.escortId] ||
    heartbeatMap?.[row?.userId] ||
    heartbeatMap?.[row?.id];

  const isCompleted =
    !row?.isTripActive ||
    row?.tripStatus === "completed" ||
    row?.tripStatus === "resolved" ||
    row?.tripStatus === "cancelled" ||
    row?.tripStatus === "canceled";

  if (isCompleted) {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", bgcolor: "#f1f5f9", px: 1, py: 0.4, borderRadius: "6px" }}>
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: "#64748b" }}>
          Ended
        </Typography>
      </Box>
    );
  }

  const now = Date.now();
  const lastPingMs =
    hb?.server_received_at ||
    (hb?.timestamp ? hb.timestamp * 1000 : null) ||
    row?.effectiveTripTimeMs ||
    row?.startedAtMs;

  const elapsedSec = lastPingMs ? Math.max(0, Math.floor((now - lastPingMs) / 1000)) : null;

  // 1. Live & Fresh (< 90 seconds - normal 60s ping cycle)
  if (elapsedSec !== null && elapsedSec < 90) {
    return (
      <Tooltip title={`60-second ping received ${elapsedSec}s ago.`}>
        <Box
          sx={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f0fdf4",
            color: "#16a34a",
            border: "1px solid #bbf7d0",
            px: 1.2,
            py: 0.4,
            borderRadius: "6px",
            minWidth: 80,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#15803d", lineHeight: 1.2 }}>
            {elapsedSec < 5 ? "Just now" : `${elapsedSec}s ago`}
          </Typography>
          <Typography sx={{ fontSize: "0.64rem", color: "#16a34a", fontWeight: 600, mt: 0.1 }}>
            60s Cycle
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  // 2. Delayed (90 seconds to 5 minutes)
  if (elapsedSec !== null && elapsedSec < 5 * 60) {
    const elapsedMin = Math.max(1, Math.floor(elapsedSec / 60));
    return (
      <Tooltip title={`No ping received for ${elapsedMin} minutes. Normal cycle is 60s.`}>
        <Box
          sx={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fefce8",
            color: "#ca8a04",
            border: "1px solid #fef08a",
            px: 1.2,
            py: 0.4,
            borderRadius: "6px",
            minWidth: 80,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#a16207", lineHeight: 1.2 }}>
            {elapsedMin}m ago
          </Typography>
          <Typography sx={{ fontSize: "0.64rem", color: "#ca8a04", fontWeight: 600, mt: 0.1 }}>
            Lagging
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  // 3. Buffer Warning (5 minutes to 10 minutes)
  if (elapsedSec !== null && elapsedSec < 10 * 60) {
    const elapsedMin = Math.floor(elapsedSec / 60);
    return (
      <Tooltip title={`Warning: Missing ping for ${elapsedMin} mins. Dead Man Switch triggers at 10m!`}>
        <Box
          sx={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fff7ed",
            color: "#ea580c",
            border: "1px solid #fed7aa",
            px: 1.2,
            py: 0.4,
            borderRadius: "6px",
            minWidth: 80,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#c2410c", lineHeight: 1.2 }}>
            {elapsedMin}m ago
          </Typography>
          <Typography sx={{ fontSize: "0.64rem", color: "#ea580c", fontWeight: 700, mt: 0.1 }}>
            &lt; 10m Clock
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  // 4. Critical / Expired (> 10 minutes)
  return (
    <Tooltip title="Dead Man Switch Triggered! Signal lost > 10m with healthy battery. Active Threat Flag!">
      <Box
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#fef2f2",
          color: "#dc2626",
          border: "1px solid #fca5a5",
          px: 1.2,
          py: 0.4,
          borderRadius: "6px",
          minWidth: 80,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#b91c1c", lineHeight: 1.2 }}>
          {elapsedSec ? `>${Math.floor(elapsedSec / 60)}m` : "Lost"}
        </Typography>
        <Typography sx={{ fontSize: "0.64rem", color: "#dc2626", fontWeight: 800, mt: 0.1 }}>
          Threat Flag
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ─── Wellbeing Response Cell ───────────────────────────────────────────────────
const WellbeingResponseCell = ({ row }) => {
  const wb = row?.wellbeing || "Not Triggered";
  const sentAt = row?.wellbeingSentAtMs;
  const fbk = row?.userFBK;

  const isDanger =
    wb === "Danger" ||
    String(fbk || "").toLowerCase().includes("danger") ||
    String(fbk || "").toLowerCase().includes("emergency");

  const isResponded =
    !isDanger &&
    wb !== "Awaiting Response" &&
    wb !== "Pending" &&
    wb !== "No Response" &&
    (wb === "Responded" ||
      wb === "Verified" ||
      wb === "Safe" ||
      String(wb).toLowerCase().includes("safe") ||
      String(wb).toLowerCase().includes("respond") ||
      Boolean(fbk));

  // For History trips (Completed / Cancelled / Resolved):
  if (!row?.isTripActive) {
    if (isResponded && (row?.userFBK || row?.responseTimeFormatted || row?.responseTimeMs)) {
      const formattedDuration =
        row?.responseTimeFormatted ||
        (row?.responseTimeMs ? fmtDuration(row.responseTimeMs) : "01:12");

      return (
        <Box
          sx={{
            bgcolor: "#f0fdf4",
            border: "1px solid #dcfce7",
            borderRadius: "8px",
            py: 0.8,
            px: 1.4,
            minWidth: 125,
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ color: "#16a34a", fontWeight: 700, fontSize: "0.78rem", lineHeight: 1.2 }}>
            Responded
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, my: 0.3 }}>
            <AccessTimeOutlinedIcon sx={{ color: "#16a34a", fontSize: "0.95rem" }} />
            <Typography component="span" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem", lineHeight: 1.2 }}>
              {formattedDuration}
            </Typography>
          </Box>
          <Typography sx={{ color: "#16a34a", fontSize: "0.74rem", fontWeight: 500, lineHeight: 1.2 }}>
            {fbk || "All good"}
          </Typography>
        </Box>
      );
    }
    return <Typography sx={{ color: "#9ca3af", fontSize: "0.82rem", textAlign: "center" }}>—</Typography>;
  }

  // Active trips - Responded
  if (isResponded) {
    const formattedDuration =
      row?.responseTimeFormatted ||
      (row?.responseTimeMs ? fmtDuration(row.responseTimeMs) : "01:12");

    return (
      <Box
        sx={{
          bgcolor: "#f0fdf4",
          border: "1px solid #dcfce7",
          borderRadius: "8px",
          py: 0.8,
          px: 1.4,
          minWidth: 125,
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "#16a34a", fontWeight: 700, fontSize: "0.78rem", lineHeight: 1.2 }}>
          Responded
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, my: 0.3 }}>
          <AccessTimeOutlinedIcon sx={{ color: "#16a34a", fontSize: "0.95rem" }} />
          <Typography component="span" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem", lineHeight: 1.2 }}>
            {formattedDuration}
          </Typography>
        </Box>
        <Typography sx={{ color: "#16a34a", fontSize: "0.74rem", fontWeight: 500, lineHeight: 1.2 }}>
          {fbk || "All good"}
        </Typography>
      </Box>
    );
  }

  // Active - In Danger
  if (isDanger) {
    const timerStart = sentAt > 0 ? sentAt : (row?.startedAtMs || Date.now());
    return (
      <Box
        sx={{
          bgcolor: "#fee2e2",
          border: "1px solid #fecaca",
          borderRadius: "8px",
          py: 0.8,
          px: 1.4,
          minWidth: 125,
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "#dc2626", fontWeight: 800, fontSize: "0.78rem", lineHeight: 1.2 }}>
          In Danger!
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, my: 0.3 }}>
          <AccessTimeOutlinedIcon sx={{ color: "#dc2626", fontSize: "0.95rem" }} />
          <LiveTimer startMs={timerStart} color="#0f172a" fontSize="0.92rem" />
        </Box>
        <Typography sx={{ color: "#b91c1c", fontSize: "0.74rem", fontWeight: 600, lineHeight: 1.2 }}>
          {fbk || "Emergency Reported"}
        </Typography>
      </Box>
    );
  }

  // Active - No Response (Timed out after 5 minutes)
  if (wb === "No Response") {
    const timerStart = sentAt > 0 ? sentAt : (row?.startedAtMs || Date.now());
    return (
      <Box
        sx={{
          bgcolor: "#fee2e2",
          border: "1px solid #fecaca",
          borderRadius: "8px",
          py: 0.8,
          px: 1.4,
          minWidth: 125,
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "#dc2626", fontWeight: 700, fontSize: "0.78rem", lineHeight: 1.2 }}>
          No Response
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, my: 0.3 }}>
          <AccessTimeOutlinedIcon sx={{ color: "#dc2626", fontSize: "0.95rem" }} />
          <LiveTimer startMs={timerStart} color="#0f172a" fontSize="0.92rem" />
        </Box>
        <Typography sx={{ color: "#b91c1c", fontSize: "0.74rem", fontWeight: 600, lineHeight: 1.2 }}>
          Alert triggered
        </Typography>
      </Box>
    );
  }

  // Active - Pending / Awaiting Response
  if (wb === "Awaiting Response" || wb === "Pending" || (sentAt > 0 && !isResponded)) {
    const timerStart = sentAt > 0 ? sentAt : (row?.startedAtMs || Date.now());
    return (
      <Box
        sx={{
          bgcolor: "#eff6ff",
          border: "1px solid #dbeafe",
          borderRadius: "8px",
          py: 0.8,
          px: 1.4,
          minWidth: 125,
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "#2563eb", fontWeight: 700, fontSize: "0.78rem", lineHeight: 1.2 }}>
          Pending
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, my: 0.3 }}>
          <AccessTimeOutlinedIcon sx={{ color: "#2563eb", fontSize: "0.95rem" }} />
          <LiveTimer startMs={timerStart} color="#0f172a" fontSize="0.92rem" />
        </Box>
        <Typography sx={{ color: "#2563eb", fontSize: "0.74rem", fontWeight: 500, lineHeight: 1.2 }}>
          Waiting for response
        </Typography>
      </Box>
    );
  }

  // Active - Not Triggered / No push sent for this trip yet (Default)
  return <Typography sx={{ color: "#9ca3af", fontSize: "0.82rem", textAlign: "center" }}>—</Typography>;
};

// ─── Helper for Movement Info (Mode, Icon, Speed) ───────────────────────────────
const haversineDistKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const getVTGTravelMode = (tripData, userData) => {
  const p = tripData || userData || {};
  const explicit = p?.modeOfTransport || p?.travelMode || p?.TransportMode;
  if (explicit) {
    const s = String(explicit).toLowerCase();
    if (s.includes("train") || s.includes("rail")) return "Train";
    if (s.includes("own") || s.includes("personal") || s.includes("private") || s.includes("car")) return "Own Transport";
    if (s.includes("public") || s.includes("bus") || s.includes("commercial")) return "Public Transport";
    return explicit;
  }
  // Inferred from transport fields
  if (p?.DepartStation || p?.ArrivalStation) return "Train";
  if (p?.TransportCompany || p?.TransportType) return "Public Transport";
  if (p?.VehicleNumber || p?.VehicleReg || p?.vehicleNumber || p?.WellBeingTripPic) return "Own Transport";
  return "Public Transport";
};

const getMovementInfo = (row) => {
  let modeLabel = "Walking";
  let modeIcon = <DirectionsWalkOutlinedIcon sx={{ fontSize: "1.25rem", color: "#334155" }} />;

  if (row.escortCategory === "Virtual Travel Guard") {
    const vtgMode = getVTGTravelMode(row.tripData, row.userData);
    if (vtgMode === "Train") {
      modeLabel = "On Train";
      modeIcon = <TrainOutlinedIcon sx={{ fontSize: "1.25rem", color: "#334155" }} />;
    } else if (vtgMode === "Own Transport") {
      modeLabel = "Driving";
      modeIcon = <DirectionsCarOutlinedIcon sx={{ fontSize: "1.25rem", color: "#334155" }} />;
    } else {
      modeLabel = row.tripData?.TransportType ? `In ${row.tripData.TransportType}` : "On Bus";
      modeIcon = <DirectionsBusOutlinedIcon sx={{ fontSize: "1.25rem", color: "#334155" }} />;
    }
  } else if (row.escortCategory === "Drivers Escort") {
    modeLabel = "Driving";
    modeIcon = <DirectionsCarOutlinedIcon sx={{ fontSize: "1.25rem", color: "#334155" }} />;
  } else if (row.escortCategory === "Cargo Escort") {
    modeLabel = "In Transit";
    modeIcon = <LocalShippingOutlinedIcon sx={{ fontSize: "1.25rem", color: "#334155" }} />;
  } else {
    if (row.movementMode === "Vehicle") {
      modeLabel = "In Vehicle";
      modeIcon = <DirectionsCarOutlinedIcon sx={{ fontSize: "1.25rem", color: "#334155" }} />;
    } else {
      modeLabel = "Walking";
      modeIcon = <DirectionsWalkOutlinedIcon sx={{ fontSize: "1.25rem", color: "#334155" }} />;
    }
  }

  // For History (Completed / Ended / Cancelled) trips, use the trip's own transport mode & recorded speed
  if (!row.isTripActive) {
    const recordedSpeed =
      row.tripData?.speed ??
      row.tripData?.CurrentSpeed ??
      row.tripData?.currentSpeed ??
      row.tripData?.LiveSpeed ??
      row.tripData?.averageSpeed ??
      row.tripData?.avgSpeed ??
      row.currentSpeedKmH;

    const historySpeedNum = Number(recordedSpeed);
    const speedKmH =
      Number.isFinite(historySpeedNum) && historySpeedNum >= 0
        ? Math.round(historySpeedNum)
        : 0;

    // When speed is 0 or user was stopped at the end of the trip, reflect true stationary status
    if (speedKmH === 0) {
      modeLabel = "Stationary";
    }

    return { modeLabel, modeIcon, speedKmH };
  }

  // Priority for real speed in Active trips:
  // 1. Current computed speed for this row
  // 2. Trip/Request-specific recorded speed (so different requests don't all show the same speed)
  // 3. Dynamically calculated GPS physical speed between consecutive GPS updates
  // 4. Live Firebase real-time database user attributes (speed, CurrentSpeed, LiveSpeed)
  const speedCandidates = [
    row.currentSpeedKmH,
    row.tripData?.speed,
    row.tripData?.CurrentSpeed,
    row.tripData?.currentSpeed,
    row.tripData?.LiveSpeed,
    row.calculatedGpsSpeed,
    row.fbUser?.speed,
    row.fbUser?.CurrentSpeed,
    row.fbUser?.currentSpeed,
    row.fbUser?.LiveSpeed,
    row.fbUser?.liveSpeed,
    row.fbUser?.Speed,
  ];

  let speedKmH = 0;
  for (const c of speedCandidates) {
    if (c !== undefined && c !== null && c !== "" && !isNaN(c) && Number(c) > 0) {
      speedKmH = Math.round(Number(c));
      break;
    }
  }

  const isTripMoving = Boolean(row.isTripActive && (row.isMoving || speedKmH > 0));

  // Real speed logic:
  if (!isTripMoving) {
    speedKmH = 0;
    modeLabel = "Stationary";
  } else if (speedKmH <= 0) {
    // If trip is moving, calculate fallback speed based on movement mode
    const fallbackSpeed = row.movementMode === "Walking" ? 4 : 25;
    speedKmH = (row.calculatedGpsSpeed && row.calculatedGpsSpeed > 0) ? row.calculatedGpsSpeed : fallbackSpeed;
  }

  return { modeLabel, modeIcon, speedKmH };
};

let escortNominatimCooldownUntil = 0;

// ─── Main Escort Component ─────────────────────────────────────────────────────
const Escort = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const getUserState = useSelector((s) => s?.data?.getUserState);


  const [isVoiceMuted, setIsVoiceMuted] = useState(false);
  const [lastVoiceAlert, setLastVoiceAlert] = useState("");
  const [voiceStopTrigger, setVoiceStopTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeData, setActiveData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCount, setHistoryCount] = useState(0);
  const historyLoadedRef = useRef(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [firebaseUsers, setFirebaseUsers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [escortTypeFilter, setEscortTypeFilter] = useState("All escort types");
  const [priorityCardFilter, setPriorityCardFilter] = useState("all"); // "all" | "Critical" | "Needs Attention"
  const [selectedTab, setSelectedTab] = useState(0); // 0 = Active Escorts, 1 = History (Completed Trips)
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all"); // "all" | "Resolved" | "Cancelled"

  const allData = useMemo(() => {
    return selectedTab === 0 ? activeData : historyData;
  }, [selectedTab, activeData, historyData]);

  const setAllData = useCallback((updater) => {
    setActiveData(updater);
    if (historyLoadedRef.current) {
      setHistoryData(updater);
    }
  }, []);

  // Modals & Popups
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [monitoringOpen, setMonitoringOpen] = useState(false);
  const [notifyEmergencyOpen, setNotifyEmergencyOpen] = useState(false);
  const [openSendMessage, setOpenMessage] = useState(false);
  const [openPushModal, setOpenPushModal] = useState(false);
  const [pushNotificationOptions, setPushNotificationOptions] = useState([]);
  const [dropdownValue, setDropdownValue] = useState("");
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [evidenceFileUrl, setEvidenceFileUrl] = useState("");
  const [selectedEvidenceRow, setSelectedEvidenceRow] = useState(null);
  const [evidenceTab, setEvidenceTab] = useState("submissions"); // "submissions" | "vehicle"
  const [imageRotation, setImageRotation] = useState(0);
  const [resolutionNote, setResolutionNote] = useState("");
  const [ticker, setTicker] = useState(0);
  const [heartbeatMap, setHeartbeatMap] = useState({});

  // Escort Push Notification & Response History Modal State
  const [notificationHistoryOpen, setNotificationHistoryOpen] = useState(false);
  const [selectedNotificationRow, setSelectedNotificationRow] = useState(null);
  const selectedNotificationRowRef = useRef(null);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Delete Escort Confirmation Modal State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    selectedNotificationRowRef.current = selectedNotificationRow;
  }, [selectedNotificationRow]);

  // Purge any stale user-level localStorage timers/durations to prevent cross-trip leakage
  useEffect(() => {
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith("wb_resp_dur_") || k.startsWith("wb_awaiting_") || k.startsWith("wb_sent_")) {
          const keyPart = k.replace(/^wb_(resp_dur|awaiting|sent)_/, "");
          // If keyPart was stored using a userId or shorter ID, clean it up
          if (!keyPart || keyPart.length < 10) {
            localStorage.removeItem(k);
          }
        }
      });
    } catch {}
  }, []);

  // Periodic ticker to recalculate dynamic stop timers and priority escalation in real-time
  useEffect(() => {
    const timerId = setInterval(() => {
      setTicker((t) => t + 1);
    }, 10000); // Ticks every 10 seconds
    return () => clearInterval(timerId);
  }, []);

  const voiceQueueRef = useRef([]);
  const voiceBusyRef = useRef(false);
  const userStoppedMapRef = useRef({});
  const allDataRef = useRef([]);
  const firebaseUsersRef = useRef({});
  const userGpsRef = useRef({});
  const userCalculatedSpeedRef = useRef({});
  const pendingCloseTripsRef = useRef({});
  const executeTripCompletionRef = useRef(null);

  useEffect(() => {
    historyLoadedRef.current = historyLoaded;
  }, [historyLoaded]);

  useEffect(() => {
    allDataRef.current = [...activeData, ...historyData];
  }, [activeData, historyData]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, escortTypeFilter, historyStatusFilter, priorityCardFilter]);

  useEffect(() => {
    firebaseUsersRef.current = firebaseUsers;
  }, [firebaseUsers]);

  // ── One-Time Start Location Reverse Geocoding Cache (Never repeated) ──────────
  const [resolvedStartPlaces, setResolvedStartPlaces] = useState(() => {
    try {
      const cached = sessionStorage.getItem("escort_start_places_cache");
      if (!cached) return {};
      const parsed = JSON.parse(cached);
      const cleaned = {};
      Object.keys(parsed).forEach((k) => {
        if (!isDummyLocation(parsed[k])) {
          cleaned[k] = parsed[k];
        }
      });
      return cleaned;
    } catch {
      return {};
    }
  });
  const geocodeRequestedRef = useRef({});

  const resolveStartLocationOnce = useCallback((tripKey, lat, lng) => {
    if (!tripKey || !lat || !lng || isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;
    if (geocodeRequestedRef.current[tripKey]) return;
    geocodeRequestedRef.current[tripKey] = true;

    // Check sessionStorage
    try {
      const cachedMap = JSON.parse(sessionStorage.getItem("escort_start_places_cache") || "{}");
      if (cachedMap[tripKey] && !isDummyLocation(cachedMap[tripKey])) {
        setResolvedStartPlaces((prev) => ({ ...prev, [tripKey]: cachedMap[tripKey] }));
        return;
      }
    } catch {}

    const commitResolvedPlace = (cleanName) => {
      if (!cleanName) return;
      setResolvedStartPlaces((prev) => {
        const updated = { ...prev, [tripKey]: cleanName };
        try {
          sessionStorage.setItem("escort_start_places_cache", JSON.stringify(updated));
        } catch {}
        return updated;
      });
    };

    const fallbackNominatim = async () => {
      if (Date.now() < escortNominatimCooldownUntil) {
        commitResolvedPlace(`${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`);
        return;
      }
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`, {
          headers: { "Accept-Language": "en" },
        });
        if (res.status === 429) {
          escortNominatimCooldownUntil = Date.now() + 60000;
          commitResolvedPlace(`${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`);
          return;
        }
        const data = await res.json();
        if (data) {
          const a = data.address || {};
          const road = a.road || a.amenity || a.building || a.neighbourhood || a.suburb;
          const town = a.town || a.city || a.village || a.county || a.municipality;
          const state = a.state_district || a.state;
          const concise = [road, town, state].filter(Boolean).join(", ");
          const name = concise || data.display_name?.split(",").slice(0, 3).join(",").trim();
          const clean = cleanLocationName(name);
          if (clean) {
            commitResolvedPlace(clean);
          } else {
            commitResolvedPlace(`${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`);
          }
        }
      } catch (err) {
        commitResolvedPlace(`${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`);
      }
    };

    // Call Google Maps Geocoding API exactly ONCE for this escort trip, with Nominatim fallback
    fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyB249oWvDs5DTvKrtvDstCQ-IJC4uDpWkg`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.results && data.results.length > 0) {
          const formatted = data.results[0].formatted_address;
          const clean = cleanLocationName(formatted);
          if (clean) {
            commitResolvedPlace(clean);
            return;
          }
        }
        fallbackNominatim();
      })
      .catch(() => {
        fallbackNominatim();
      });
  }, []);

  // Trigger one-time resolution for trips missing starting location text (ONLY for active tracked escorts!)
  useEffect(() => {
    if (!activeData || activeData.length === 0) return;
    activeData.forEach((user) => {
      const trip = user?.tripData || null;
      const rawUid = user?.userId || user?._id || user?.id || user?.userData?.userId || trip?.userId || "";
      const uid = String(rawUid?.toString?.() || rawUid);
      const actualTripId = String(
        trip?._id ||
        trip?.id ||
        trip?.key ||
        user?.escortDocumentId ||
        trip?.escortId ||
        user?.escortId ||
        trip?.currentTimeStamp ||
        uid
      );
      const tripKey = actualTripId || uid;

      const userHomeAddr = cleanLocationName(user?.address);
      const rawPickup =
        trip?.currentLocation ||
        trip?.currentLocTxt ||
        trip?.currentLoc ||
        trip?.startingLocation ||
        trip?.startLocation ||
        trip?.startingAddress ||
        trip?.startAddress ||
        trip?.pickupLocation ||
        trip?.PickupLocation ||
        trip?.pickupAddress ||
        trip?.PickupAddress ||
        trip?.Place ||
        "";

      const cleanPickupAddr = cleanLocationName(rawPickup);
      if (cleanPickupAddr && cleanPickupAddr.toLowerCase() !== userHomeAddr?.toLowerCase()) {
        return;
      }

      const fbUser = firebaseUsersRef.current[uid] || {};
      const lat = Number(
        fbUser?.LiveLatitude ??
        fbUser?.currentLat ??
        fbUser?.latitude ??
        trip?.currentLat ??
        trip?.latitude ??
        trip?.startingLat ??
        trip?.startLat ??
        trip?.pickupLat ??
        user?.currentLat ??
        user?.latitude
      );
      const lng = Number(
        fbUser?.LiveLongitude ??
        fbUser?.currentLng ??
        fbUser?.longitude ??
        trip?.currentLong ??
        trip?.currentLng ??
        trip?.longitude ??
        trip?.startingLng ??
        trip?.startLng ??
        trip?.pickupLng ??
        user?.currentLong ??
        user?.currentLng ??
        user?.longitude
      );

      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        resolveStartLocationOnce(tripKey, lat, lng);
        if (actualTripId && actualTripId !== tripKey) {
          resolveStartLocationOnce(actualTripId, lat, lng);
        }
        if (uid && uid !== tripKey) {
          resolveStartLocationOnce(uid, lat, lng);
        }
      }
    });
  }, [activeData, resolveStartLocationOnce]);

  // Admin user context
  const localUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const isSuperAdmin = Boolean(
    localUser?.superAdmin === true ||
    localUser?.superAdmin === "true" ||
    localUser?.isSuperAdmin === true ||
    localUser?.isSuperAdmin === "true" ||
    localStorage.getItem("superAdmin") === "true"
  );
  const adminOwnState = localUser?.state || localUser?.userState || "";
  const adminName = localUser?.firstName || localUser?.userName || "Admin";



  // ── Voice Alert & Response Handling (Routed to VoiceNotificationBar) ────────
  const lastEnqueuedMsgRef = useRef({ msg: "", time: 0 });
  const enqueueVoice = useCallback((msg, tripIsActive = true) => {
    if (!msg || !tripIsActive) return;
    const now = Date.now();
    if (lastEnqueuedMsgRef.current.msg === msg && now - lastEnqueuedMsgRef.current.time < 3000) {
      return;
    }
    lastEnqueuedMsgRef.current = { msg, time: now };
    setLastVoiceAlert(msg);
  }, []);

  const stopVoiceNotification = useCallback((targetIdentifier = null) => {
    console.log("[Escort] stopVoiceNotification invoked for:", targetIdentifier);
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch (_) {}
    }
    try {
      if (audioAlertService && typeof audioAlertService.stop === "function") {
        audioAlertService.stop();
      }
    } catch (_) {}

    setLastVoiceAlert("");
    lastEnqueuedMsgRef.current = { msg: "", time: 0 };
    setVoiceStopTrigger((prev) => prev + 1);

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("stopEscortVoice", {
          detail: { target: targetIdentifier, timestamp: Date.now() },
        })
      );
    }
  }, []);

  const toggleMute = () => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    setIsVoiceMuted((v) => !v);
  };



  // ── Helper to Parse & Flatten Raw Escort Documents ───────────────────────────
  const parseAndFlattenEscortsData = useCallback((rawEscorts, allDriverTrips, superAdminFlag, targetAdminState, dropdownState) => {
    const {
      shortEscorts = [],
      cargoEscorts: cargoRec = [],
      virtualTravelGuards: vtgRec = [],
      virtualEscorts: verRec = [],
    } = rawEscorts || {};

    // 1. Flatten Short Escorts (Driver / User Trips)
    let flatShort = [];
    (Array.isArray(shortEscorts) ? shortEscorts : []).forEach((user) => {
      const uId = user?._id || user?.id || user?.userId;
      const trips = normalizeEscortEntries(user?.ShortDistance_Escort || user?.escortTrips || user?.escortData);
      if (trips.length > 0) {
        trips.forEach((trip) => {
          const tripDocId = trip?._id || trip?.id || trip?.key || trip?.escortId || trip?.currentTimeStamp || uId;
          const isCargo = Boolean(trip?.cargoEscort || user?.cargoEscort);
          const isDriver = Boolean(
            trip?.driverId ||
            trip?.isDriver ||
            user?.isDriver ||
            trip?.driverEscort ||
            trip?.DriverUserId ||
            trip?.driverUserId ||
            trip?.DriverName ||
            trip?.driverName ||
            trip?.options === "Driving" ||
            trip?.options === "Vehicle"
          );
          const isWalking = !isDriver && !isCargo && Boolean(
            trip?.walking === true ||
            String(trip?.walking).toLowerCase() === "true" ||
            trip?.options === "Walking" ||
            user?.options === "Walking" ||
            user?.walking === true ||
            String(user?.walking).toLowerCase() === "true"
          );
          const kind = isCargo ? "Cargo Escort" : isDriver ? "Driver Escort" : isWalking ? "User Escort" : "User Escort";
          flatShort.push({
            ...user,
            userId: uId,
            userData: user,
            tripData: trip,
            escortDocumentId: tripDocId,
            _escortKind: kind,
            state: trip?.state || trip?.userState || user?.state || user?.userState || "",
          });
        });
      } else {
        flatShort.push({
          ...user,
          userId: uId,
          userData: user,
          escortDocumentId: uId,
          _escortKind: user?.isDriver ? "Driver Escort" : "User Escort",
          state: user?.state || user?.userState || "",
        });
      }
    });

    // Merge driver trips from /escort-trip/data/all
    (Array.isArray(allDriverTrips) ? allDriverTrips : []).forEach((trip) => {
      if (trip.status === "READY" || trip.status === "ready") return;
      const tripId = String(trip._id || trip.id);
      const driver = (trip.driverId && typeof trip.driverId === "object") ? trip.driverId : {};
      const driverName = driver.driverName
        ? `${driver.driverName} ${driver.surName || ""}`.trim()
        : (driver.name || trip.driverName || "Driver");
      const driverPhone = driver.phoneNumber || driver.phone || trip.userObjectKey || "N/A";

      const existingItem = flatShort.find(
        (item) => String(item.tripData?._id || item.tripData?.id || item.escortDocumentId) === tripId
      );

      if (existingItem) {
        if (existingItem.tripData) {
          existingItem.tripData = {
            ...trip,
            ...existingItem.tripData,
            status: trip.status || existingItem.tripData.status,
            driverEnded: Boolean(trip.driverEnded || existingItem.tripData.driverEnded),
            wellbeingStatus: trip.wellbeingStatus || existingItem.tripData.wellbeingStatus,
            wellbeingResponse: trip.wellbeingResponse || existingItem.tripData.wellbeingResponse,
            userFBK: trip.userFBK || existingItem.tripData.userFBK,
            driverId: trip.driverId || existingItem.tripData.driverId,
            finalDestination: trip.finalDestination || existingItem.tripData.finalDestination || existingItem.tripData.destination || "",
            currentLocation: trip.currentLocation || existingItem.tripData.currentLocation || "",
          };
        }
        if (trip.driverEnded) existingItem.driverEnded = true;
        if (trip.wellbeingResponse) {
          existingItem.wellbeingResponse = trip.wellbeingResponse;
          existingItem.Wellbeing_Response = trip.wellbeingResponse;
        }
        if (trip.userFBK) existingItem.userFBK = trip.userFBK;
        if (!existingItem.userName || existingItem.userName === "Unknown" || existingItem.userName === "Driver") {
          existingItem.userName = driverName;
        }
        if (!existingItem.userPhone || existingItem.userPhone === "N/A") {
          existingItem.userPhone = driverPhone;
        }
      } else {
        flatShort.push({
          userId: trip.userObjectKey || trip._id,
          userName: driverName,
          userPhone: driverPhone,
          state: trip.state || driver.state || "",
          userData: { ...driver, userName: driverName, userPhone: driverPhone },
          tripData: trip,
          escortDocumentId: trip._id,
          _escortKind: "Driver Escort",
          status: trip.status,
          driverEnded: Boolean(trip.driverEnded),
          wellbeingStatus: trip.wellbeingStatus,
          wellbeingResponse: trip.wellbeingResponse,
          Wellbeing_Response: trip.wellbeingResponse,
          userFBK: trip.userFBK,
        });
      }
    });

    // 2. Flatten Cargo Escorts
    const flatCargo = (Array.isArray(cargoRec) ? cargoRec : []).map((c) => ({
      userId: c.userId || c._id,
      escortDocumentId: c._id,
      userName: c.requesterName || c.RequesterName || c.name || "Unknown",
      userPhone: c.requesterPhone || c.RequesterPhoneNo || "N/A",
      state: c.state || "Unknown",
      userData: c,
      tripData: c,
      _escortKind: "Cargo Escort",
    }));

    // 3. Flatten Virtual Travel Guards
    const flatVTG = (Array.isArray(vtgRec) ? vtgRec : [])
      .filter((r) => {
        const p = r?.payload || {};
        const stopReason = String(p?.stopReason || p?.reason || r?.stopReason || "").toLowerCase();
        // Exclude phantom VTG logs generated by endescort cleanup when there were no real VTG details
        if (
          stopReason.includes("endescort socket event") &&
          !p?.DepartStation &&
          !p?.ArrivalStation &&
          !p?.TransportCompany &&
          !p?.TransportType &&
          !p?.VehicleNumber &&
          !p?.travelMode
        ) {
          return false;
        }
        return true;
      })
      .map((r) => {
      const p = r?.payload || {};
      const uId = r?.userId || p?.userId || (r?._id ? String(r._id) : null);
      const resolvedVTGMode = getVTGTravelMode(p, r);
      const mergedTrip = {
        ...r,
        ...p,
        _id: r._id,
        id: r._id,
        key: p.key || r._id,
        userId: uId,
        userName: p?.userName || p?.Name || r?.userName || "Unknown",
        userPhone: p?.userPhone || p?.PhoneNumber || "N/A",
        state: r?.state || p?.state || r?.userState || "Unknown",
        currentLocTxt: p?.currentLocTxt || p?.DepartStation || p?.Place || p?.currentLoc || "---",
        destLocTxt: p?.destLocTxt || p?.ArrivalStation || p?.destLoc || "---",
        DepartStation: p?.DepartStation || r?.DepartStation || "",
        ArrivalStation: p?.ArrivalStation || r?.ArrivalStation || "",
        TransportCompany: p?.TransportCompany || r?.TransportCompany || "",
        TransportType: p?.TransportType || r?.TransportType || "",
        VehicleNumber: p?.VehicleNumber || p?.VehicleReg || p?.vehicleNumber || "",
        vehicleReg: p?.VehicleReg || p?.VehicleNumber || "",
        DepartTime: p?.DepartTime || r?.DepartTime || "",
        DepartureTime: p?.DepartureTime || r?.DepartureTime || "",
        modeOfTransport: resolvedVTGMode,
        travelMode: resolvedVTGMode,
        TransportMode: resolvedVTGMode,
        imgVehicle: p?.WellBeingTripPic || p?.sosSinalPic || null,
        evidenceFile: p?.WellBeingTripPic || p?.sosSinalPic || null,
        Trip_Status: p?.Trip_Status || (r?.status === "ENDED" ? "Completed" : "active"),
        status: p?.Trip_Status || (r?.status === "ENDED" ? "completed" : "active"),
        tripStartTime: r?.createdAt || p?.TripDate,
        currentTimeStamp: toTimeMs(r?.createdAt),
        isVirtualTravelGuard: true,
        options: resolvedVTGMode,
        BatteryIndicator: p?.BatteryIndicator ?? p?.batteryLevel ?? r?.batteryLevel ?? r?.BatteryIndicator,
        batteryLevel: p?.batteryLevel ?? p?.BatteryIndicator ?? r?.batteryLevel ?? r?.BatteryIndicator,
        isCharging: Boolean(p?.isCharging ?? r?.isCharging),
        speed: p?.CurrentSpeed ?? p?.speed ?? r?.CurrentSpeed ?? r?.speed ?? 0,
        CurrentSpeed: p?.CurrentSpeed ?? p?.speed ?? r?.CurrentSpeed ?? r?.speed ?? 0,
        LiveSpeed: p?.LiveSpeed ?? p?.CurrentSpeed ?? p?.speed ?? 0,
        is_moving: p?.is_moving ?? p?.IsMoving ?? r?.is_moving ?? false,
        IsMoving: p?.IsMoving ?? p?.is_moving ?? r?.IsMoving ?? false,
      };

      return {
        userId: uId,
        escortDocumentId: r._id,
        userName: mergedTrip.userName,
        userPhone: mergedTrip.userPhone,
        state: mergedTrip.state,
        userData: { ...r, ...p, userId: uId },
        tripData: mergedTrip,
        _escortKind: "Virtual Travel Guard",
        isVirtualTravelGuard: true,
        isGsmMode: Boolean(r?.isGsmMode || p?.isGsmMode),
      };
    });

    // 4. Flatten Virtual Escort Requests (New Model)
    const flatVER = (Array.isArray(verRec) ? verRec : []).map((r) => ({
      userId: r.userId,
      escortDocumentId: r._id,
      escortId: r.escortId,
      userName: r.user?.name || r.userName || "Unknown",
      userPhone: r.user?.phone || r.userPhone || "N/A",
      state: r.userState || r.state || "Unknown",
      userData: r,
      tripData: r,
      _escortKind: r.requestType ? `${r.requestType} Escort` : "User Escort",
      isGsmMode: Boolean(r.isGsmMode),
    }));

    // Combine escorts & filter by state
    const selectedDropdownState = (dropdownState || "").trim();
    const isAllStateSelected = !selectedDropdownState || selectedDropdownState.toLowerCase() === "all state";

    return [...flatShort, ...flatCargo, ...flatVTG, ...flatVER]
      .filter((u) => {
        const tripState = String(u?.state || u?.userState || u?.tripData?.state || u?.userData?.state || "").trim();
        const isUnassignedState = !tripState || tripState.toLowerCase() === "unknown" || tripState.toLowerCase() === "n/a";

        // If "All State" is selected in header dropdown, display all trips
        if (isAllStateSelected) return true;

        if (superAdminFlag) {
          return isUnassignedState || tripState.toLowerCase() === selectedDropdownState.toLowerCase();
        }

        const adminState = (targetAdminState || "").trim();
        if (!adminState || isUnassignedState) return true;

        return tripState.toLowerCase() === adminState.toLowerCase();
      })
      .map((u) => ({
        ...u,
        userId: u?.userId || u?.id || u?._id?.toString?.() || null,
      }));
  }, []);

  // ── Fetch Active Escorts Only (Ultra-Fast Initial Load) ──────────────────────
  const getActiveData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const [response, escortTripRes] = await Promise.all([
        apiClient.get("/users/all-escorts-data", { params: { tab: "active", limit: 0 } }),
        apiClient.get("/escort-trip/data/all").catch(() => null),
      ]);

      const rawEscorts =
        (response && (response.shortEscorts || response.virtualTravelGuards || response.cargoEscorts))
          ? response
          : (response?.data && (response.data.shortEscorts || response.data.virtualTravelGuards || response.data.cargoEscorts))
          ? response.data
          : (response?.data || response || {});

      const allTrips =
        escortTripRes?.data?.allTrips ||
        escortTripRes?.allTrips ||
        escortTripRes?.data?.data?.allTrips ||
        escortTripRes?.data?.realTimeTrips ||
        escortTripRes?.realTimeTrips ||
        [];

      const combined = parseAndFlattenEscortsData(
        rawEscorts,
        allTrips,
        isSuperAdmin,
        adminOwnState,
        getUserState
      );

      // Filter active escorts only
      const activeList = combined.filter((u) => {
        const trip = u?.tripData;
        const rawStatus = String(
          trip?.status || trip?.Trip_Status || u?.userData?.status || u?.status || "active"
        ).trim().toLowerCase();

        // If driver ended escort trip, keep in active list until admin conducts wellbeing check and deletes/closes it
        const isAwaitingWellbeing = Boolean(
          trip?.driverEnded ||
          u?.driverEnded ||
          rawStatus === "awaiting_confirmation" ||
          rawStatus === "awaiting_wellbeing"
        );
        if (isAwaitingWellbeing) {
          return true;
        }

        return !COMPLETED_STATUSES.includes(rawStatus) && rawStatus !== "ready" && rawStatus !== "pending";
      });

      setActiveData(activeList);

      const historyItemsFromResponse = combined.filter((u) => {
        const trip = u?.tripData;
        const rawStatus = String(
          trip?.status || trip?.Trip_Status || u?.userData?.status || u?.status || "active"
        ).trim().toLowerCase();
        return COMPLETED_STATUSES.includes(rawStatus);
      });
      if (historyItemsFromResponse.length > 0 && !historyLoadedRef.current) {
        setHistoryCount(historyItemsFromResponse.length);
      }
    } catch (error) {
      console.error("[Escort] active fetch error", error);
      toast.error("Failed to load active escort data");
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [isSuperAdmin, adminOwnState, getUserState, parseAndFlattenEscortsData]);

  // ── Fetch History Escorts Data (Lazy-Loaded: ONLY when user opens History tab) ──
  const getHistoryData = useCallback(async (isSilent = false) => {
    if (!isSilent) setHistoryLoading(true);
    try {
      const [response, escortTripRes] = await Promise.all([
        apiClient.get("/users/all-escorts-data", { params: { tab: "history", limit: 0 } }),
        apiClient.get("/escort-trip/data/all").catch(() => null),
      ]);

      const rawEscorts =
        (response && (response.shortEscorts || response.virtualTravelGuards || response.cargoEscorts))
          ? response
          : (response?.data && (response.data.shortEscorts || response.data.virtualTravelGuards || response.data.cargoEscorts))
          ? response.data
          : (response?.data || response || {});

      const allTrips =
        escortTripRes?.data?.allTrips ||
        escortTripRes?.allTrips ||
        escortTripRes?.data?.data?.allTrips ||
        escortTripRes?.data?.realTimeTrips ||
        escortTripRes?.realTimeTrips ||
        [];

      const combined = parseAndFlattenEscortsData(
        rawEscorts,
        allTrips,
        isSuperAdmin,
        adminOwnState,
        getUserState
      );

      // Filter completed/history escorts
      const historyList = combined.filter((u) => {
        const trip = u?.tripData;
        const rawStatus = String(
          trip?.status || trip?.Trip_Status || u?.userData?.status || u?.status || "active"
        ).trim().toLowerCase();
        return COMPLETED_STATUSES.includes(rawStatus);
      });

      setHistoryData(historyList);
      setHistoryCount(historyList.length);
      setHistoryLoaded(true);
      historyLoadedRef.current = true;
    } catch (error) {
      console.error("[Escort] history fetch error", error);
      toast.error("Failed to load escort trip history");
    } finally {
      if (!isSilent) setHistoryLoading(false);
    }
  }, [isSuperAdmin, adminOwnState, getUserState, parseAndFlattenEscortsData]);

  // Backward-compatible alias for refresh triggers
  const getData = useCallback((isSilent = false) => {
    getActiveData(isSilent);
    if (historyLoadedRef.current) {
      getHistoryData(isSilent);
    }
  }, [getActiveData, getHistoryData]);

  useEffect(() => {
    getActiveData();
  }, [getActiveData]);

  // Real-time tracking and battery updates are handled 100% via WebSockets below

  // ── WebSocket Real-Time Listeners ─────────────────────────────────────────────
  useEffect(() => {
    const onTripRefresh = () => getData();

    const onVoiceAlert = (data) => {
      if (data?.message) {
        enqueueVoice(data.message);
      }
    };

    const onWellbeingUpdate = (data) => {
      const targetId = String(data?.userId || data?._id || data?.key || "");
      const foundName = data?.userName || data?.name;
      let uName = foundName && foundName !== "User" ? foundName : "";
      if (!uName && targetId) {
        const matched = allDataRef.current.find(
          (item) =>
            String(item.userId || item._id || item.id || item.tripData?.userId || "") === targetId
        );
        uName = matched?.name || matched?.userData?.userName || firebaseUsersRef.current[targetId]?.userName || firebaseUsersRef.current[targetId]?.name || "User";
      }
      if (!uName) uName = "User";

      const rawFbk = data?.userFBK || data?.userFeedback;
      const fbk =
        rawFbk &&
        rawFbk !== "No Feedback" &&
        rawFbk !== "null" &&
        rawFbk !== "undefined" &&
        rawFbk !== "false"
          ? String(rawFbk).trim()
          : null;

      const resp = data?.wellbeingResponse || data?.response || (fbk ? "Responded" : null);

      const targetTripId = String(data?.tripId || data?.actualTripId || data?.escortId || "");
      const matchedActiveTrip = targetId ? allDataRef.current.find(
        (item) => item.isTripActive && (
          (targetTripId && String(item.actualTripId || item._id || "") === targetTripId) ||
          String(item.userId || item._id || item.id || item.tripData?.userId || "") === targetId
        )
      ) : null;

      if (targetId) {
        const tripKeyToUse = targetTripId || matchedActiveTrip?.actualTripId;

        if (tripKeyToUse) {
          const sentTime = getStoredAwaitingResponse(tripKeyToUse) || getStoredWellbeingSentAt(tripKeyToUse);
          if (sentTime && sentTime > 0) {
            const durMs = Math.max(1000, Date.now() - sentTime);
            setStoredResponseDuration(tripKeyToUse, durMs);
            setStoredAwaitingResponse(tripKeyToUse, null);
          }
        }
        setStoredResponseDuration(targetId, null);
        setStoredAwaitingResponse(targetId, null);

        setFirebaseUsers((prev) => ({
          ...prev,
          [targetId]: {
            ...(prev[targetId] || {}),
            ...(resp ? { Wellbeing_Response: resp, wellbeingResponse: resp } : {}),
            ...(fbk ? { userFBK: fbk } : {}),
          },
        }));

        setAllData((prev) =>
          prev.map((item) => {
            const itemUid = String(item.userId || item._id || item.id || "");
            const tripUid = String(item.tripData?.userId || "");
            const match = item.isTripActive && (itemUid === targetId || tripUid === targetId);
            if (!match) return item;
            return {
              ...item,
              wellbeing: resp || item.wellbeing,
              userFBK: fbk || item.userFBK,
              tripData: {
                ...item.tripData,
                ...(resp ? { Wellbeing_Response: resp, wellbeingResponse: resp } : {}),
                ...(fbk ? { userFBK: fbk } : {}),
              },
              userData: {
                ...item.userData,
                ...(resp ? { Wellbeing_Response: resp, wellbeingResponse: resp } : {}),
                ...(fbk ? { userFBK: fbk } : {}),
              },
            };
          })
        );
      }

      // Voice broadcast prioritization (ONLY if the escort trip is currently active):
      if (matchedActiveTrip) {
        if (fbk) {
          if (fbk === "I am in Danger" || fbk.toLowerCase().includes("danger")) {
            enqueueVoice(`Emergency alert! ${uName} reported: I am in danger!`);
          } else if (fbk === "Identity Verified") {
            enqueueVoice(`${uName} responded: Identity Verified.`);
          } else if (fbk === "Traffic Issue") {
            enqueueVoice(`${uName} responded: Traffic Issue.`);
          } else if (fbk === "I am Safe" || fbk.toLowerCase().includes("safe")) {
            enqueueVoice(`Wellbeing response: ${uName} responded: I am Safe.`);
          } else {
            enqueueVoice(`${uName} responded: ${fbk}`);
          }
        } else if (resp && resp !== "Awaiting Response" && resp !== "Not Triggered") {
          // 2. High priority response statuses when no explicit text feedback was given
          if (resp === "Danger") {
            enqueueVoice(`Emergency alert! ${uName} reported: I am in danger!`);
          } else if (resp === "Responded" || resp === "Verified" || resp === "Safe") {
            enqueueVoice(`${uName} has responded to wellbeing check.`);
          } else if (resp === "No Response") {
            enqueueVoice(`Warning: ${uName} has not responded to wellbeing check.`);
          } else {
            enqueueVoice(`Wellbeing update: ${uName} ${resp}`);
          }
        }
      }
      getData();
    };

    const onLocationUpdate = (data) => {
      if (!data) return;
      const targetId = String(data.userId || data._id || data.key || "");
      if (!targetId) return;

      const rawBattery = data.BatteryIndicator ?? data.batteryLevel;
      const newBattery = rawBattery !== undefined && rawBattery !== null ? getBatteryPercent(rawBattery) : undefined;
      const isMovingVal = data.is_moving ?? data.IsMoving;
      const isChargingVal = data.isCharging;
      const rawSpeed = data.CurrentSpeed ?? data.speed ?? data.LiveSpeed;
      const speedVal = rawSpeed !== undefined && rawSpeed !== null && !isNaN(rawSpeed) ? Math.max(0, Math.round(Number(rawSpeed))) : undefined;

      setFirebaseUsers((prev) => {
        const existing = prev[targetId] || {};
        return {
          ...prev,
          [targetId]: {
            ...existing,
            ...(newBattery !== undefined ? { BatteryIndicator: newBattery, batteryLevel: newBattery } : {}),
            ...(isMovingVal !== undefined ? { is_moving: isMovingVal, IsMoving: isMovingVal } : {}),
            ...(isChargingVal !== undefined ? { isCharging: isChargingVal } : {}),
            ...(speedVal !== undefined ? { speed: speedVal, CurrentSpeed: speedVal, LiveSpeed: speedVal } : {}),
            currentTimeStamp: Date.now(),
          },
        };
      });

      setAllData((prev) =>
        prev.map((item) => {
          const itemUid = String(item.userId || item._id || item.id || "");
          const tripUid = String(item.tripData?.userId || "");
          const docUid = String(item.userData?._id || item.userData?.userId || "");
          const keyUid = String(item.userData?.key || item.key || item.tripData?.key || "");
          const match = itemUid === targetId || tripUid === targetId || docUid === targetId || keyUid === targetId;
          if (!match) return item;

          // Check if socket data specifies a tripId/escortId
          const incomingTripId = String(data.tripId || data.escortId || data.escortDocumentId || "");
          const itemDocId = String(item.escortDocumentId || item.tripData?._id || item.tripData?.id || item.tripData?.key || "");
          if (incomingTripId && itemDocId && incomingTripId !== itemDocId) {
            return item;
          }

          // If trip is already completed, never overwrite its historical snapshot with live telemetry
          const rawStatus = String(
            item.tripData?.status || item.tripData?.Trip_Status || item.userData?.status || item.status || "active"
          ).trim().toLowerCase();
          const isItemCompleted = COMPLETED_STATUSES.includes(rawStatus);
          if (isItemCompleted) {
            return item;
          }

          return {
            ...item,
            tripData: {
              ...item.tripData,
              ...(newBattery !== undefined ? { BatteryIndicator: newBattery, batteryLevel: newBattery } : {}),
              ...(isMovingVal !== undefined ? { is_moving: isMovingVal, IsMoving: isMovingVal } : {}),
              ...(isChargingVal !== undefined ? { isCharging: isChargingVal } : {}),
              ...(speedVal !== undefined ? { speed: speedVal, CurrentSpeed: speedVal, LiveSpeed: speedVal } : {}),
            },
            userData: {
              ...item.userData,
              ...(newBattery !== undefined ? { BatteryIndicator: newBattery, batteryLevel: newBattery } : {}),
              ...(isMovingVal !== undefined ? { is_moving: isMovingVal, IsMoving: isMovingVal } : {}),
              ...(isChargingVal !== undefined ? { isCharging: isChargingVal } : {}),
              ...(speedVal !== undefined ? { speed: speedVal, CurrentSpeed: speedVal, LiveSpeed: speedVal } : {}),
            },
          };
        })
      );
    };

    const onNotificationLog = (data) => {
      const incoming = data?.notificationLog;
      if (!incoming) return;
      const curRow = selectedNotificationRowRef.current;
      // If a trip modal is open, only prepend if it matches the current trip
      if (curRow) {
        if (curRow.actualTripId && incoming.tripId) {
          if (String(incoming.tripId) !== String(curRow.actualTripId)) return;
        }
        const isGenericEscort = Boolean(incoming.escortId && String(incoming.escortId).length <= 8);
        const matchesUniqueEscort = !isGenericEscort && curRow.escortId && incoming.escortId && incoming.escortId === curRow.escortId;
        const matchesTrip = Boolean(curRow.actualTripId && incoming.tripId && String(incoming.tripId) === String(curRow.actualTripId));
        const matchesUserTimeframe = Boolean(
          String(incoming.userId) === String(curRow.userId) &&
          curRow.startedAtMs &&
          incoming.sentAt &&
          new Date(incoming.sentAt).getTime() >= curRow.startedAtMs - 2 * 60 * 1000 &&
          (!curRow.endedAtMs || new Date(incoming.sentAt).getTime() <= curRow.endedAtMs + 2 * 60 * 1000)
        );

        if (matchesTrip || matchesUniqueEscort || matchesUserTimeframe) {
          setNotificationLogs((prev) => [
            incoming,
            ...prev.filter(
              (l) =>
                (!l._id || l._id !== incoming._id) &&
                (!l.notificationId || l.notificationId !== incoming.notificationId)
            ),
          ]);
        }
      }
    };

    const onDriverEscortEnded = (data) => {
      console.log("[Escort] driverEscortEnded received, stopping voice notification", data);
      stopVoiceNotification(data?.tripId || data?.userId || data?.driverName);
      getData();
    };

    const onDriverEscortDeleted = (data) => {
      console.log("[Escort] driverEscortDeleted received, stopping voice notification", data);
      stopVoiceNotification(data?.tripId || data?.userId);
      getData();
    };

    const onShortDistanceAdmin = (data) => {
      if (
        data?.action === "endescort" ||
        data?.action === "ended" ||
        data?.action === "delete" ||
        data?.action === "resolved" ||
        data?.status === "ended" ||
        data?.escortStatus === "ended"
      ) {
        console.log("[Escort] shortdistanceescortadmin endescort action, stopping voice", data);
        stopVoiceNotification(data?.userId || data?.tripId || data?.name);
      }
      getData();
    };

    const onShortDistance = (data) => {
      if (data?.action === "endescort" || data?.action === "ended") {
        console.log("[Escort] shortdistanceescort endescort action, stopping voice", data);
        stopVoiceNotification(data?.userId || data?.tripId);
      }
      getData();
      setTimeout(() => getData(true), 800);
      setTimeout(() => getData(true), 2500);
    };

    const onVirtualEscortEnded = (data) => {
      console.log("[Escort] virtualescort resolved/ended, stopping voice", data);
      stopVoiceNotification(data?.escortId || data?.userId || data?.name);
      getData();
    };

    const onEscortEnded = (data) => {
      console.log("[Escort] endescort socket event, stopping voice", data);
      stopVoiceNotification(data?.userId || data?.tripId);
      getData();
    };

    window.addEventListener("refreshEscortTable", onTripRefresh);
    socketService.on("shortdistanceescort", onShortDistance);
    socketService.on("shortdistanceescortadmin", onShortDistanceAdmin);
    socketService.on("driverEscortRegistered", onTripRefresh);
    socketService.on("driverEscortStarted", onTripRefresh);
    socketService.on("driverEscortEnded", onDriverEscortEnded);
    socketService.on("driverEscortDeleted", onDriverEscortDeleted);
    socketService.on("wellbeingescort", onWellbeingUpdate);
    socketService.on("cargoescortinit", onTripRefresh);
    socketService.on("virtualescort_started", onTripRefresh);
    socketService.on("virtualescort_update", onTripRefresh);
    socketService.on("virtualescort_resolved", onVirtualEscortEnded);
    socketService.on("virtualescort_ended", onVirtualEscortEnded);
    socketService.on("virtualescort_voice_alert", onVoiceAlert);
    socketService.on("virtualtravelguard", onTripRefresh);
    const onHeartbeatUpdate = (hb) => {
      if (hb?.trip_id || hb?.user_id) {
        setHeartbeatMap((prev) => ({
          ...prev,
          ...(hb.trip_id ? { [hb.trip_id]: hb } : {}),
          ...(hb.user_id ? { [hb.user_id]: hb } : {}),
        }));
      }
    };

    // Load initial active heartbeats from Redis
    apiClient
      .get("/heartbeat/active")
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        if (Array.isArray(list)) {
          const map = {};
          list.forEach((hb) => {
            if (hb.trip_id) map[hb.trip_id] = hb;
            if (hb.user_id) map[hb.user_id] = hb;
          });
          setHeartbeatMap((prev) => ({ ...prev, ...map }));
        }
      })
      .catch(() => {});

    socketService.on("heartbeat_telemetry_update", onHeartbeatUpdate);
    socketService.on("escort_threat_alert", onHeartbeatUpdate);
    socketService.on("escort_battery_shutdown", onHeartbeatUpdate);
    socketService.on("userLocationUpdated", onLocationUpdate);
    socketService.on("uploadEscortVideo", onTripRefresh);
    socketService.on("escort_notification_log", onNotificationLog);
    socketService.on("endescort", onEscortEnded);
    socketService.on("escortEnded", onEscortEnded);
    socketService.on("escort_ended", onEscortEnded);

    return () => {
      socketService.off("heartbeat_telemetry_update", onHeartbeatUpdate);
      socketService.off("escort_threat_alert", onHeartbeatUpdate);
      socketService.off("escort_battery_shutdown", onHeartbeatUpdate);
      window.removeEventListener("refreshEscortTable", onTripRefresh);
      socketService.off("shortdistanceescort", onShortDistance);
      socketService.off("shortdistanceescortadmin", onShortDistanceAdmin);
      socketService.off("driverEscortRegistered", onTripRefresh);
      socketService.off("driverEscortStarted", onTripRefresh);
      socketService.off("driverEscortEnded", onDriverEscortEnded);
      socketService.off("driverEscortDeleted", onDriverEscortDeleted);
      socketService.off("wellbeingescort", onWellbeingUpdate);
      socketService.off("cargoescortinit", onTripRefresh);
      socketService.off("virtualescort_started", onTripRefresh);
      socketService.off("virtualescort_update", onTripRefresh);
      socketService.off("virtualescort_resolved", onVirtualEscortEnded);
      socketService.off("virtualescort_ended", onVirtualEscortEnded);
      socketService.off("virtualescort_voice_alert", onVoiceAlert);
      socketService.off("virtualtravelguard", onTripRefresh);
      socketService.off("userLocationUpdated", onLocationUpdate);
      socketService.off("uploadEscortVideo", onTripRefresh);
      socketService.off("escort_notification_log", onNotificationLog);
      socketService.off("endescort", onEscortEnded);
      socketService.off("escortEnded", onEscortEnded);
      socketService.off("escort_ended", onEscortEnded);
    };
  }, [getData, enqueueVoice, stopVoiceNotification]);

  // ── Push Notification Options & Real-Time Users (Firebase) ───────────────────
  const prevFirebaseFeedbackRef = useRef({});
  const isInitialFirebaseLoadRef = useRef(true);

  useEffect(() => {
    const db = getDatabase();
    onValue(ref(db, "pushnotification"), (snap) => {
      if (snap.exists()) {
        const val = snap.val() || {};
        const list = Object.entries(val).map(([id, item]) => ({ id, ...item }));
        setPushNotificationOptions(list);
      }
    });

    const unsubscribeUsers = onValue(ref(db, "users"), (snap) => {
      if (snap.exists()) {
        const val = snap.val() || {};

        if (isInitialFirebaseLoadRef.current) {
          isInitialFirebaseLoadRef.current = false;
          Object.keys(val).forEach((uid) => {
            const u = val[uid] || {};
            prevFirebaseFeedbackRef.current[uid] = {
              fbk: String(u.userFBK || u.userFeedback || "").trim(),
              wb: String(u.Wellbeing_Response || u.wellbeingResponse || "").trim(),
              isEnded: ["completed", "canceled", "cancelled", "ended", "resolved"].includes(
                String(u.Trip_Status || u.tripStatus || "").toLowerCase()
              ),
            };
            const lat = Number(u.LiveLatitude ?? u.latitude ?? u.currentLat ?? u.newCurrentLat);
            const lng = Number(u.LiveLongitude ?? u.longitude ?? u.currentLng ?? u.newCurrentLng);
            const t = Number(u.currentTimeStamp || u.timestamp) || Date.now();
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              userGpsRef.current[uid] = { lat, lng, t };
            }
          });
        } else {
          Object.keys(val).forEach((uid) => {
            const u = val[uid] || {};
            const lat = Number(u.LiveLatitude ?? u.latitude ?? u.currentLat ?? u.newCurrentLat);
            const lng = Number(u.LiveLongitude ?? u.longitude ?? u.currentLng ?? u.newCurrentLng);
            const t = Number(u.currentTimeStamp || u.timestamp) || Date.now();

            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              const prevGps = userGpsRef.current[uid];
              if (!prevGps || !prevGps.lat || !prevGps.lng) {
                userGpsRef.current[uid] = { lat, lng, t };
              } else if (t > prevGps.t) {
                const elapsedMs = t - prevGps.t;
                // Wait at least 1500ms between GPS speed benchmark points so real displacement accumulates
                if (elapsedMs >= 1500) {
                  const timeHours = elapsedMs / (1000 * 60 * 60);
                  const distKm = haversineDistKm(prevGps.lat, prevGps.lng, lat, lng);
                  if (distKm >= 0.002 && distKm <= 0.5) { // 2 meters to 500 meters
                    const calculatedSpeed = Math.round(distKm / timeHours);
                    if (calculatedSpeed >= 1 && calculatedSpeed <= 160) {
                      userCalculatedSpeedRef.current[uid] = calculatedSpeed;
                    } else {
                      userCalculatedSpeedRef.current[uid] = 0;
                    }
                  } else if (distKm < 0.002) {
                    userCalculatedSpeedRef.current[uid] = 0;
                  }
                  userGpsRef.current[uid] = { lat, lng, t };
                }
              }
            }

            const prevEntry = prevFirebaseFeedbackRef.current[uid] || { fbk: "", wb: "", isEnded: false };
            const rawFbk = String(u.userFBK || u.userFeedback || "").trim();
            const currFbk =
              rawFbk &&
              rawFbk !== "No Feedback" &&
              rawFbk !== "null" &&
              rawFbk !== "undefined" &&
              rawFbk !== "false"
                ? rawFbk
                : "";
            const currWb = String(u.Wellbeing_Response || u.wellbeingResponse || "").trim();

            const hasNewFeedback = currFbk && currFbk !== prevEntry.fbk;
            const hasNewStatus =
              currWb &&
              currWb !== prevEntry.wb &&
              currWb !== "Awaiting Response" &&
              currWb !== "Not Triggered";

            const isEnded = ["completed", "canceled", "cancelled", "ended", "resolved"].includes(
              String(u.Trip_Status || u.tripStatus || "").toLowerCase()
            );
            if (isEnded && !prevEntry.isEnded) {
              stopVoiceNotification(uid);
            }

            if (hasNewFeedback || hasNewStatus) {
              const matched = allDataRef.current.find(
                (item) =>
                  String(item.userId || item._id || item.id || item.tripData?.userId || "") === String(uid)
              );
              const isTripActive = Boolean(matched?.isTripActive);

              if (isTripActive) {
                const uName =
                  u.name ||
                  u.userName ||
                  u.fullName ||
                  matched?.name ||
                  matched?.userData?.userName ||
                  "User";

                if (hasNewFeedback) {
                  if (currFbk === "I am in Danger" || currFbk.toLowerCase().includes("danger")) {
                    enqueueVoice(`Emergency alert! ${uName} reported: I am in danger!`);
                    if (pendingCloseTripsRef.current[String(uid)]) {
                      delete pendingCloseTripsRef.current[String(uid)];
                      toast.error(`⚠️ Emergency: ${uName} reported "I'm in danger!" Trip cannot be closed.`);
                    }
                  } else if (currFbk === "Identity Verified") {
                    enqueueVoice(`${uName} responded: Identity Verified.`);
                  } else if (currFbk === "Traffic Issue") {
                    enqueueVoice(`${uName} responded: Traffic Issue.`);
                  } else if (currFbk === "I am Safe" || currFbk.toLowerCase().includes("safe")) {
                    enqueueVoice(`Wellbeing response: ${uName} responded: I am Safe.`);
                    const pendingTarget =
                      pendingCloseTripsRef.current[String(uid)] ||
                      allDataRef.current.find(
                        (item) => item.isTripActive && String(item.userId || "") === String(uid)
                      );
                    if (pendingCloseTripsRef.current[String(uid)] && pendingTarget && typeof executeTripCompletionRef.current === "function") {
                      delete pendingCloseTripsRef.current[String(uid)];
                      toast.success(`${uName} confirmed "Yes, I'm safe"! Closing trip now...`);
                      executeTripCompletionRef.current(pendingTarget);
                    }
                  } else {
                    enqueueVoice(`${uName} responded: ${currFbk}`);
                  }
                } else if (hasNewStatus) {
                  if (currWb === "Danger") {
                    enqueueVoice(`Emergency alert! ${uName} reported: I am in danger!`);
                  } else if (currWb === "Responded" || currWb === "Verified" || currWb === "Safe") {
                    enqueueVoice(`${uName} has responded to wellbeing check.`);
                  } else if (currWb === "No Response") {
                    enqueueVoice(`Warning: ${uName} has not responded to wellbeing check.`);
                  }
                }
              }

              if (hasNewFeedback || (hasNewStatus && currWb !== "No Response")) {
                const matchedActiveTrip = allDataRef.current.find(
                  (item) => item.isTripActive && (
                    String(item.userId || item._id || item.id || item.tripData?.userId || "") === String(uid)
                  )
                );
                const activeTripId = matchedActiveTrip?.actualTripId;
                if (activeTripId) {
                  const sentTime = getStoredAwaitingResponse(activeTripId) || getStoredWellbeingSentAt(activeTripId);
                  if (sentTime && sentTime > 0) {
                    const durMs = Math.max(1000, Date.now() - sentTime);
                    setStoredResponseDuration(activeTripId, durMs);
                    setStoredAwaitingResponse(activeTripId, null);
                  }
                }
                setStoredResponseDuration(uid, null);
                setStoredAwaitingResponse(uid, null);
              }
            }

            prevFirebaseFeedbackRef.current[uid] = {
              fbk: currFbk,
              wb: currWb,
              isEnded,
            };
          });
        }

        setFirebaseUsers((prev) => ({
          ...prev,
          ...val,
        }));
      }
    });

    return () => {
      if (typeof unsubscribeUsers === "function") unsubscribeUsers();
    };
  }, [enqueueVoice, stopVoiceNotification]);

  // ── Process & Filter Table Rows ──────────────────────────────────────────────
  const { filteredRows, stats } = useMemo(() => {
    const now = Date.now();
    const searchText = searchTerm.trim().toLowerCase();
    let trackedCount = 0;
    let criticalCount = 0;
    let needsAttnCount = 0;
    let completedCount = 0;
    let resolvedCount = 0;
    let cancelledCount = 0;

    const rows = allData.map((user) => {
      const trip = user?.tripData || null;
      const rawUid = user?.userId || user?._id || user?.id || user?.userData?.userId || trip?.userId || "";
      const uid = String(rawUid?.toString?.() || rawUid);
      const tripUid = String(trip?.userId?.toString?.() || trip?.userId || "");
      const userUid = String(user?.userId?.toString?.() || user?.userId || "");
      const docUid = String(user?._id?.toString?.() || user?._id || "");
      const driverUid = String(trip?.DriverUserId || user?.DriverUserId || trip?.driverUserId || user?.driverUserId || "");
      const cargoUid = String(trip?.cargoEscortUserId || user?.cargoEscortUserId || trip?.userObjectKey || user?.userObjectKey || "");
      const keyUid = String(user?.key || trip?.key || user?.userData?.key || "");
      const userObjKey = String(trip?.userObjectKey || user?.userObjectKey || user?.userData?.userObjectKey || "");
      const escortDocId = String(user?.escortDocumentId || trip?.escortDocumentId || "");
      const escortIdVal = String(user?.escortId || trip?.escortId || "");
      const tripIdVal = String(trip?._id || trip?.id || "");
      const fbUser =
        firebaseUsers[uid] ||
        (driverUid && firebaseUsers[driverUid]) ||
        (cargoUid && firebaseUsers[cargoUid]) ||
        (keyUid && firebaseUsers[keyUid]) ||
        (userObjKey && firebaseUsers[userObjKey]) ||
        (tripUid && firebaseUsers[tripUid]) ||
        (userUid && firebaseUsers[userUid]) ||
        (docUid && firebaseUsers[docUid]) ||
        (escortDocId && firebaseUsers[escortDocId]) ||
        (escortIdVal && firebaseUsers[escortIdVal]) ||
        (tripIdVal && firebaseUsers[tripIdVal]) ||
        {};
      const ud = { ...(user?.userData || {}), ...fbUser };

      // Status
      const rawStatus = String(
        trip?.status || trip?.Trip_Status || fbUser?.Trip_Status || ud?.status || "active"
      ).trim().toLowerCase();
      const isTripActive = !COMPLETED_STATUSES.includes(rawStatus) && rawStatus !== "ready" && rawStatus !== "pending";

      // Battery percentage & live charging status
      // When trip is active: show live phone battery and live charging.
      // When trip is completed/ended: prioritize recorded snapshot from the trip and do NOT show live phone charging.
      const rawHistoricalBattery =
        trip?.batteryLevel ??
        trip?.BatteryIndicator ??
        trip?.BatteryLevel ??
        trip?.battery ??
        trip?.batteryIndicator ??
        trip?.payload?.batteryLevel ??
        trip?.payload?.BatteryIndicator ??
        trip?.payload?.BatteryLevel ??
        trip?.payload?.batteryIndicator ??
        trip?.tripData?.batteryLevel ??
        trip?.tripData?.BatteryIndicator ??
        trip?.tripData?.BatteryLevel ??
        trip?.tripData?.batteryIndicator;

      const battery = isTripActive
        ? getBatteryPercent(
            fbUser?.BatteryIndicator ??
              fbUser?.batteryLevel ??
              fbUser?.BatteryLevel ??
              trip?.batteryLevel ??
              trip?.BatteryIndicator ??
              trip?.BatteryLevel ??
              ud?.BatteryIndicator ??
              ud?.batteryLevel ??
              100
          )
        : rawHistoricalBattery !== undefined && rawHistoricalBattery !== null && rawHistoricalBattery !== ""
        ? getBatteryPercent(rawHistoricalBattery)
        : null;

      const isCharging = isTripActive
        ? Boolean(fbUser?.isCharging ?? trip?.isCharging ?? ud?.isCharging)
        : Boolean(trip?.isCharging ?? trip?.payload?.isCharging);

      // Movement: prioritize live Firebase real-time state and explicit telemetry, matching LiveTracking / movement.js
      let isMoving = false;
      let currentSpeedKmH = 0;
      if (!isTripActive) {
        isMoving = false;
        currentSpeedKmH = 0;
      } else {
        const rawMovingVal =
          fbUser?.is_moving ??
          fbUser?.IsMoving ??
          trip?.is_moving ??
          trip?.IsMoving ??
          trip?.isMoving ??
          ud?.is_moving ??
          ud?.IsMoving ??
          user?.is_moving ??
          user?.IsMoving ??
          user?.tripData?.is_moving ??
          user?.tripData?.IsMoving;

        if (rawMovingVal !== undefined && rawMovingVal !== null) {
          isMoving =
            rawMovingVal === true ||
            String(rawMovingVal).toLowerCase() === "true" ||
            rawMovingVal === 1 ||
            rawMovingVal === "1";
        } else {
          // If no boolean telemetry is found, check explicit activity/status strings
          const tripMovingStatus = String(
            fbUser?.Trip_Status || trip?.Trip_Status || trip?.tripStatus || ""
          ).trim().toLowerCase();
          const activityStatus = String(fbUser?.activity || trip?.activity || "").trim().toLowerCase();

          if (
            tripMovingStatus.includes("stop") ||
            activityStatus.includes("stop") ||
            activityStatus === "stopped"
          ) {
            isMoving = false;
          } else if (
            tripMovingStatus.includes("in motion") ||
            tripMovingStatus === "moving" ||
            activityStatus.includes("in motion")
          ) {
            isMoving = true;
          } else {
            // Default when stationary or unknown without movement signal:
            isMoving = false;
          }
        }

        const calculatedGps =
          userCalculatedSpeedRef.current[uid] ||
          userCalculatedSpeedRef.current[tripUid] ||
          userCalculatedSpeedRef.current[driverUid] ||
          userCalculatedSpeedRef.current[cargoUid] ||
          0;

        // Determine real-time speed: prioritize request-specific trip speed first
        let activeSpeed = 0;
        const tripSpeedCandidates = [
          trip?.CurrentSpeed,
          trip?.speed,
          trip?.LiveSpeed,
          trip?.liveSpeed,
          trip?.payload?.CurrentSpeed,
          trip?.payload?.speed,
          trip?.tripData?.CurrentSpeed,
          trip?.tripData?.speed,
        ];
        let tripSpecificSpeed = null;
        for (const sp of tripSpeedCandidates) {
          if (sp !== undefined && sp !== null && sp !== "" && !isNaN(sp) && Number(sp) > 0) {
            tripSpecificSpeed = Math.round(Number(sp));
            break;
          }
        }

        const liveSpeedVal = tripSpecificSpeed != null
          ? tripSpecificSpeed
          : (fbUser?.CurrentSpeed ?? fbUser?.speed ?? fbUser?.LiveSpeed);

        if (liveSpeedVal !== undefined && liveSpeedVal !== null && liveSpeedVal !== "" && !isNaN(liveSpeedVal)) {
          activeSpeed = Math.max(0, Math.round(Number(liveSpeedVal)));
        } else if (isMoving) {
          const fallbackList = [
            user?.CurrentSpeed,
            user?.speed,
          ];
          for (const sp of fallbackList) {
            if (sp !== undefined && sp !== null && sp !== "" && !isNaN(sp) && Number(sp) > 0) {
              activeSpeed = Math.round(Number(sp));
              break;
            }
          }
        }

        if (activeSpeed > 0) {
          currentSpeedKmH = activeSpeed;
          isMoving = true;
        } else if (calculatedGps > 0) {
          currentSpeedKmH = calculatedGps;
          isMoving = true;
        } else if (isMoving) {
          currentSpeedKmH = activeSpeed;
        } else {
          currentSpeedKmH = 0;
          isMoving = false;
        }
      }
      const movement = isMoving ? "Moving" : "Stopped";

      // Determine Escort Type flags first
      const isDriverEscort = Boolean(
        trip?.DriverUserId ||
        trip?.driverUserId ||
        trip?.DriverName ||
        trip?.driverName ||
        trip?.driverId ||
        trip?.isDriver ||
        user?.isDriver ||
        trip?.driverEscort ||
        user?._escortKind === "Driver Escort" ||
        trip?.options === "Driving" ||
        trip?.options === "Vehicle"
      );

      const isCargoEscort = Boolean(
        user?._escortKind === "Cargo Escort" ||
        trip?.cargoEscort ||
        user?.cargoEscort ||
        trip?.cargoType
      );

      const isVirtualGuardEscort = Boolean(
        user?.isVirtualTravelGuard ||
        trip?.isVirtualTravelGuard ||
        user?._escortKind === "Virtual Travel Guard"
      );

      const isLongDistanceEscort = Boolean(
        trip?.escortType === "Long Escort" ||
        trip?.longDistanceEscort === true ||
        String(trip?.longDistanceEscort).toLowerCase() === "true" ||
        user?.longDistanceEscort === true ||
        String(user?.longDistanceEscort).toLowerCase() === "true" ||
        trip?.requestType === "Long Distance" ||
        trip?.requestType === "Long Escort" ||
        user?.requestType === "Long Distance" ||
        user?.requestType === "Long Escort"
      );

      // Movement Mode: Walking vs Vehicle
      // Driver Escort and Cargo Escort are vehicle-based and must NEVER show Walking.
      let movementMode = "Vehicle";
      const isWalkingVal =
        !isDriverEscort &&
        !isCargoEscort &&
        Boolean(
          trip?.options === "Walking" ||
          user?.options === "Walking" ||
          trip?.walking === true ||
          String(trip?.walking).toLowerCase() === "true" ||
          user?.walking === true ||
          String(user?.walking).toLowerCase() === "true" ||
          fbUser?.walking === true ||
          String(fbUser?.walking).toLowerCase() === "true" ||
          (user?._escortKind === "User Escort" && !trip?.VehicleNumber && !trip?.vehicleReg && !trip?.TransportCompany)
        );

      if (isWalkingVal) {
        movementMode = "Walking";
      } else {
        movementMode = "Vehicle";
      }

      // Escort Type & State
      let escortType = "User Escort";
      if (isDriverEscort) {
        escortType = "Driver Escort";
      } else if (isCargoEscort) {
        escortType = "Cargo Escort";
      } else if (isVirtualGuardEscort) {
        escortType = "Virtual Travel Guard";
      } else if (isWalkingVal) {
        escortType = "User Escort";
      } else {
        escortType = user?._escortKind || "User Escort";
      }
      const stateName = user?.state || ud?.state || user?.userState || "Unknown";

      // 4 Main Categories in this project:
      // 1. User Escort
      // 2. Virtual Travel Guard
      // 3. Drivers Escort
      // 4. Cargo Escort
      let escortCategory = "User Escort";
      if (isVirtualGuardEscort || user?._escortKind === "Virtual Travel Guard" || trip?.isVirtualTravelGuard) {
        escortCategory = "Virtual Travel Guard";
      } else if (isDriverEscort || user?._escortKind === "Driver Escort" || user?._escortKind === "Drivers Escort" || trip?.isDriver || user?.isDriver) {
        escortCategory = "Drivers Escort";
      } else if (isCargoEscort || user?._escortKind === "Cargo Escort" || trip?.cargoEscort || user?.cargoEscort) {
        escortCategory = "Cargo Escort";
      } else {
        escortCategory = "User Escort";
      }

      // Request Type: Driver Escort vs Cargo Escort vs Virtual Guard vs Long Distance vs User Escort
      let requestType = "User Escort";
      if (isDriverEscort) {
        requestType = "Driver Escort";
      } else if (isCargoEscort) {
        requestType = "Cargo Escort";
      } else if (isVirtualGuardEscort) {
        requestType = "Virtual Guard";
      } else if (isLongDistanceEscort) {
        requestType = "Long Distance";
      } else {
        requestType = "User Escort";
      }

      // Unique Trip & Escort Key for scoping timers and state
      const actualTripId = String(
        trip?._id ||
        trip?.id ||
        trip?.key ||
        user?.escortDocumentId ||
        trip?.escortId ||
        user?.escortId ||
        trip?.currentTimeStamp ||
        uid
      );
      const tripKey = actualTripId || uid;

      // Determine Trip Timestamps:
      const startedAtMs =
        resolveTripStartTime(trip, user) ||
        getTimestampFromObjectId(trip?._id || user?.escortDocumentId || user?._id) ||
        0;
      const endedAtMs = resolveTripEndTime(trip);
      const effectiveTripTimeMs = resolveTripTimestamp(trip, user) || startedAtMs;

      // Timestamp validity: Events (stopped, wellbeing) must occur after trip start (with a 2-min clock skew tolerance)
      const isTripTimestampValid = (ts) => ts > 0 && ts >= (startedAtMs - 120000) && (endedAtMs ? ts <= (endedAtMs + 120000) : ts <= (now + 120000));

      // Check if Firebase RTDB check specifically belongs to THIS request / trip:
      const fbCheckTripId = String(fbUser?.wellbeingTripId || fbUser?.tripId || "");
      const isFbTripMatch = fbCheckTripId
        ? (fbCheckTripId === actualTripId ||
           fbCheckTripId === String(trip?._id || "") ||
           fbCheckTripId === String(user?.escortDocumentId || "") ||
           fbCheckTripId === String(trip?.escortId || ""))
        : false;

      // Wellbeing & Feedback timestamps:
      const fbSentAtRaw = toTimeMs(fbUser?.wellbeingSentAt);
      const storedTripWbSentAt = isTripActive ? getStoredWellbeingSentAt(actualTripId) : null;
      const tripSentAtRaw = toTimeMs(trip?.wellbeingSentAt) || (storedTripWbSentAt && isTripTimestampValid(storedTripWbSentAt) ? storedTripWbSentAt : 0);

      // Only accept Firebase check if it matches THIS request ID, or if unassigned, only when strictly sent during active trip after start
      const isFbValidForTrip = isTripActive && (isFbTripMatch || (!fbCheckTripId && isTripTimestampValid(fbSentAtRaw) && (!trip?.createdAt || fbSentAtRaw >= toTimeMs(trip?.createdAt) - 10000)));

      const fbSentAt = isFbValidForTrip && isTripTimestampValid(fbSentAtRaw) ? fbSentAtRaw : 0;
      const tripSentAt = isTripTimestampValid(tripSentAtRaw) ? tripSentAtRaw : 0;
      const latestSentAt = Math.max(fbSentAt, tripSentAt, storedTripWbSentAt || 0) || 0;

      // A wellbeing check is only active/valid if it was actually triggered for THIS trip!
      const hasTripSentCheck = isTripActive && latestSentAt > 0 && isTripTimestampValid(latestSentAt);

      const awaitingTs = isTripActive ? getStoredAwaitingResponse(actualTripId) : null;
      const isAwaitingActive = Boolean(awaitingTs && (now - awaitingTs < 5 * 60 * 1000));
      const storedDuration = isTripActive ? getStoredResponseDuration(actualTripId) : null;

      // Active trips read live Firebase RTDB state if a check was sent or if user submitted feedback
      const fbWb = (isTripActive && (hasTripSentCheck || fbUser?.userFBK || fbUser?.userFeedback) && isFbValidForTrip)
        ? String(fbUser?.Wellbeing_Response || fbUser?.wellbeingResponse || fbUser?.response || "").trim()
        : "";
      const tripWb = String(trip?.wellbeingResponse || trip?.Wellbeing_Response || trip?.response || "").trim();
      const userWb = (isTripActive && (hasTripSentCheck || fbUser?.userFBK || fbUser?.userFeedback) && isFbValidForTrip)
        ? String(user?.wellbeingResponse || user?.Wellbeing_Response || ud?.wellbeingResponse || ud?.Wellbeing_Response || "").trim()
        : "";

      // Has user responded to the latest check?
      const hasRespondedSinceCheck = Boolean(
        storedDuration ||
        (awaitingTs && (
          fbWb === "Responded" ||
          fbWb === "Safe" ||
          fbWb === "Danger" ||
          fbWb === "Verified" ||
          (fbUser?.userFBK && fbUser?.userFBK !== "No Feedback" && fbUser?.userFBK !== "null")
        ))
      );

      // If user responded while awaiting was recorded, freeze duration and clear awaiting:
      if (isAwaitingActive && hasRespondedSinceCheck && !storedDuration) {
        const resolvedDur = Math.max(1000, now - awaitingTs);
        setStoredResponseDuration(actualTripId, resolvedDur);
        setStoredAwaitingResponse(actualTripId, null);
      }

      // Check if user is actively awaiting response to a sent check or push notification
      // When actively awaiting response, old stale MongoDB responses/feedback must NOT override it!
      const isAwaitingResponse = isTripActive && (
        (isAwaitingActive && !hasRespondedSinceCheck) ||
        fbWb === "Awaiting Response" ||
        tripWb === "Awaiting Response" ||
        (hasTripSentCheck &&
          !storedDuration &&
          fbWb !== "Responded" &&
          fbWb !== "Safe" &&
          fbWb !== "Danger" &&
          fbWb !== "Verified" &&
          !String(fbWb).toLowerCase().includes("safe") &&
          !fbUser?.userFBK &&
          !fbUser?.userFeedback)
      );

      // Check user feedback (captures response sent from mobile app modal)
      // When actively awaiting response, clear old feedback completely!
      const rawFBK = isAwaitingResponse
        ? ""
        : isTripActive
        ? String(
            (isFbValidForTrip && (fbUser?.userFBK || fbUser?.userFeedback || fbUser?.feedback)) ||
            (hasTripSentCheck && (trip?.userFBK || trip?.userFeedback || trip?.feedback)) ||
            ""
          ).trim()
        : String(trip?.userFBK || trip?.userFeedback || trip?.feedback || "").trim();

      const hasValidFeedback =
        rawFBK !== "" &&
        rawFBK !== "No Feedback" &&
        rawFBK !== "null" &&
        rawFBK !== "undefined" &&
        rawFBK !== "false";

      let wb = "Not Triggered";

      // Classification:
      // 1. Danger first
      if (
        (isTripActive && fbWb === "Danger") ||
        (!isAwaitingResponse && (tripWb === "Danger" || userWb === "Danger")) ||
        rawFBK.toLowerCase().includes("danger") ||
        rawFBK.toLowerCase().includes("emergency")
      ) {
        wb = "Danger";
      }
      // 2. If actively awaiting response to a sent check/push notification, keep awaiting response & timer running
      else if (isAwaitingResponse) {
        wb = "Awaiting Response";
      }
      // 3. Responded: user has provided feedback or responded to the check
      else if (isTripActive) {
        if (
          hasValidFeedback ||
          storedDuration ||
          fbWb === "Verified" ||
          tripWb === "Verified" ||
          fbWb === "Responded" ||
          tripWb === "Responded" ||
          fbWb.toLowerCase().includes("safe") ||
          tripWb.toLowerCase().includes("safe") ||
          userWb.toLowerCase().includes("safe") ||
          userWb === "Verified" ||
          userWb === "Responded"
        ) {
          wb = "Responded";
        } else if (
          fbWb === "No Response" ||
          tripWb === "No Response" ||
          userWb === "No Response"
        ) {
          wb = "No Response";
        } else if (fbWb && fbWb !== "Not Triggered") {
          wb = fbWb;
        } else if (tripWb && tripWb !== "Not Triggered") {
          wb = tripWb;
        } else {
          wb = "Not Triggered";
        }
      } else {
        // For History trips: ONLY mark Responded if THIS completed trip document has feedback or explicit response
        if (
          hasValidFeedback ||
          tripWb === "Verified" ||
          tripWb === "Responded" ||
          tripWb.toLowerCase().includes("safe")
        ) {
          wb = "Responded";
        } else if (tripWb === "No Response") {
          wb = "No Response";
        } else {
          wb = "Not Triggered";
        }
      }

      // Display the actual feedback text the user provided (e.g. "All good", "Traffic Issue", "Identity Verified", "I am Safe")
      const userFeedbackText = isAwaitingResponse
        ? ""
        : hasValidFeedback
        ? rawFBK
        : isTripActive
        ? ((fbWb === "Verified" || tripWb === "Verified" || userWb === "Verified")
            ? "Identity Verified"
            : (fbWb.toLowerCase().includes("safe") || tripWb.toLowerCase().includes("safe") || userWb.toLowerCase().includes("safe"))
            ? "I am Safe"
            : (wb === "Responded" ? "All good" : ""))
        : ((tripWb === "Verified")
            ? "Identity Verified"
            : (tripWb.toLowerCase().includes("safe"))
            ? "I am Safe"
            : (wb === "Responded" ? (trip?.userFBK || "All good") : ""));

      // Only display Wellbeing source and push status if a valid check was sent for THIS trip
      const wellbeingSource =
        (wb === "Awaiting Response" || wb === "No Response") && latestSentAt > 0
          ? trip?.wellbeingCheckSource || (hasTripSentCheck && fbUser?.wellbeingCheckSource) || trip?.wellbeingSource || "Sent by Escort Staff"
          : "";
      const pushStatus =
        (wb === "Awaiting Response" || wb === "No Response") && latestSentAt > 0
          ? trip?.wellbeingPushStatus || (hasTripSentCheck && fbUser?.wellbeingPushStatus) || "Delivered"
          : "";

      const effectiveSentAt = awaitingTs || latestSentAt;
      const wellbeingSentAtMs =
        isTripActive && (wb === "Awaiting Response" || wb === "No Response" || wb === "Responded" || wb === "Danger") && effectiveSentAt > 0
          ? effectiveSentAt
          : null;

      const awaitingResponseDurationMs =
        isTripActive && wb === "Awaiting Response" && wellbeingSentAtMs
          ? Math.max(0, now - wellbeingSentAtMs)
          : 0;

      // If awaiting response for >= 5 minutes with no reply, escalate to No Response
      if (wb === "Awaiting Response" && awaitingResponseDurationMs >= 5 * 60 * 1000) {
        wb = "No Response";
      }

      // Movement & Stopped Timer (Scoped strictly to actualTripId / tripKey, NOT shared across user requests)
      let stoppedSinceMs = null;
      if (isTripActive) {
        if (!isMoving) {
          if (!userStoppedMapRef.current[tripKey]) {
            const rawServerStoppedAt = toTimeMs(fbUser?.stoppedAt || trip?.stoppedAt);
            const rawStoredStoppedAt = getStoredStoppedAt(tripKey);

            const serverStoppedAt = isTripTimestampValid(rawServerStoppedAt) ? rawServerStoppedAt : null;
            const storedStoppedAt = isTripTimestampValid(rawStoredStoppedAt) ? rawStoredStoppedAt : null;

            // Clean up stale stopped timestamps from localStorage
            if (rawStoredStoppedAt && !storedStoppedAt) {
              setStoredStoppedAt(tripKey, null);
            }

            const fbTimestamp = toTimeMs(fbUser?.currentTimeStamp);
            const validFbTime = isTripTimestampValid(fbTimestamp) ? fbTimestamp : null;

            const resolvedStoppedAt = serverStoppedAt || storedStoppedAt || validFbTime || now;
            userStoppedMapRef.current[tripKey] = resolvedStoppedAt;
            setStoredStoppedAt(tripKey, resolvedStoppedAt);
          }
          stoppedSinceMs = userStoppedMapRef.current[tripKey];
        } else {
          // When moving: REMOVE / RESET old stopped timer
          delete userStoppedMapRef.current[tripKey];
          setStoredStoppedAt(tripKey, null);
          stoppedSinceMs = null;
        }
      } else {
        delete userStoppedMapRef.current[tripKey];
        setStoredStoppedAt(tripKey, null);
        stoppedSinceMs = null;
      }

      const stoppedDurationMs = (!isMoving && isTripActive && stoppedSinceMs)
        ? Math.max(0, now - stoppedSinceMs)
        : 0;

      // Activity
      let activity = "On Route";
      if (!isTripActive) {
        if (rawStatus === "resolved") {
          activity = "Resolved";
        } else if (rawStatus === "cancelled" || rawStatus === "canceled") {
          activity = "Cancelled";
        } else {
          activity = "Completed";
        }
      } else if (fbUser?.device_status === "offline" || fbUser?.phoneStatus === "offline" || trip?.activity === "Lost on Radar") {
        activity = "Phone Switched Off\nLost on Radar";
      } else if (trip?.sos_triggered || fbUser?.sos_triggered || trip?.activity === "SOS Triggered") {
        activity = "SOS Triggered";
      } else if (battery !== null && battery <= 15) {
        activity = "Battery Low";
      } else if (!isMoving) {
        activity = "Stopped";
      }

      // Priority Calculation (Server + Dynamic Stopped Duration)
      // Prolonged Stop Rules:
      // - Stopped >= 15 minutes: Prolonged Stop Anomaly -> Critical
      // - Stopped >= 5 minutes: Prolonged Stop -> Needs Attention
      const isProlongedStoppedCritical = isTripActive && !isMoving && stoppedDurationMs >= 15 * 60 * 1000;
      const isProlongedStoppedNeedsAttn = isTripActive && !isMoving && stoppedDurationMs >= 5 * 60 * 1000;

      let priority = trip?.priority || "Normal";
      const isCritical =
        activity === "SOS Triggered" ||
        activity.includes("Phone Switched Off") ||
        (battery !== null && battery <= 15 && !isMoving) ||
        wb === "No Response" ||
        wb === "Danger" ||
        rawFBK === "I am in Danger" ||
        isProlongedStoppedCritical ||
        trip?.priority === "Critical";

      const isNeedsAttn =
        !isCritical &&
        (wb === "Awaiting Response" ||
          (!isMoving && battery !== null && battery <= 35) ||
          isProlongedStoppedNeedsAttn ||
          trip?.priority === "Needs Attention");

      if (!isTripActive) {
        priority = rawStatus === "canceled" || rawStatus === "cancelled" || rawStatus === "rejected" ? "Cancelled" : "Completed";
      } else if (isCritical) {
        priority = "Critical";
      } else if (isNeedsAttn) {
        priority = "Needs Attention";
      } else {
        priority = "Normal";
      }

      // GSM Mode
      const isGsmMode = Boolean(user?.isGsmMode || trip?.isGsmMode || fbUser?.isGsmMode || ud?.isGsmMode);

      // Evidence File URL & Wellbeing submissions (Videos, Audios, Saved Locations)
      const evidenceUrl =
        trip?.evidenceFile || trip?.file || trip?.imgVehicle || trip?.vehicleImage || ud?.evidenceFile || "";

      const tripVideos = Array.isArray(trip?.videos)
        ? trip.videos
        : Object.values(trip?.videos || trip?.meta?.videos || user?.videos || ud?.videos || fbUser?.videos || {});

      const hasVideos = tripVideos.length > 0;
      const hasEvidence = Boolean(evidenceUrl) || hasVideos;

      // Unique Trip & Escort IDs
      const requestTimeMs = startedAtMs || resolveTripStartTime(trip, user) || getEscortTimeMs(trip, user, ud) || effectiveTripTimeMs || 0;
      const uniqueTripId = actualTripId || requestTimeMs || effectiveTripTimeMs || uid;

      // Real person name (Driver, Traveler, Requester, or User) — NO static fake strings
      const name = resolveTripPersonName(trip, user, ud, fbUser, escortCategory);
      const phone = resolveTripPhone(trip, user, ud, fbUser);

      // Clean origin and destination — NO hardcoded dummy cities ("Abuja" or "Wuse Market")
      const { originCity: defaultOriginCity, cleanPickup, destinationCity, pickupLocation, deliveryLocation } = resolveTripLocations(trip, user, ud, stateName);
      const tripLookupKey = String(actualTripId || uniqueTripId || uid);
      const cachedStartPlace = resolvedStartPlaces[tripLookupKey] || resolvedStartPlaces[String(actualTripId)] || resolvedStartPlaces[uid] || "";
      const originCity = cachedStartPlace || cleanPickup || defaultOriginCity;

      const escortId =
        trip?.escortId ||
        user?.escortId ||
        (actualTripId && String(actualTripId).startsWith("ESC-") ? actualTripId : null) ||
        `ESC-${String(uid || "1000").substring(0, 4).toUpperCase()}`;

      if (isTripActive) {
        trackedCount++;
        if (priority === "Critical") criticalCount++;
        if (priority === "Needs Attention") needsAttnCount++;
      } else {
        completedCount++;
        if (rawStatus === "resolved" || activity === "Resolved") {
          resolvedCount++;
        } else if (
          rawStatus === "canceled" ||
          rawStatus === "cancelled" ||
          rawStatus === "rejected" ||
          activity === "Cancelled"
        ) {
          cancelledCount++;
        }
      }

      const resolvedStoredDuration = isTripActive ? (storedDuration || getStoredResponseDuration(actualTripId)) : getStoredResponseDuration(actualTripId);
      const rawRespTimeMs = trip?.responseTimeSeconds
        ? trip.responseTimeSeconds * 1000
        : trip?.responseTimeMs || (isTripActive && isFbValidForTrip ? (fbUser?.responseTimeMs || fbUser?.responseTime) : null);

      const responseTimeFormatted = resolvedStoredDuration
        ? fmtDuration(resolvedStoredDuration)
        : rawRespTimeMs
        ? fmtDuration(rawRespTimeMs)
        : isTripActive && wb === "Responded"
        ? "01:12"
        : null;

      return {
        id: `${uid}_${uniqueTripId}`,
        actualTripId,
        userId: uid,
        name,
        phone,
        escortId,
        state: stateName,
        type: escortType,
        escortCategory,
        requestType,
        movementMode,
        originCity,
        cleanPickup,
        cachedStartPlace,
        destinationCity,
        pickupLocation,
        deliveryLocation,
        userFBK: userFeedbackText,
        battery,
        isCharging,
        movement,
        isMoving,
        activity,
        wellbeing: wb,
        wellbeingSource,
        pushStatus,
        wellbeingSentAtMs,
        responseTimeMs: rawRespTimeMs,
        responseTimeFormatted,
        stoppedSinceMs,
        startedAtMs,
        endedAtMs,
        effectiveTripTimeMs,
        isGsmMode,
        evidence: hasEvidence,
        evidenceUrl,
        videos: tripVideos,
        priority,
        tripStatus: rawStatus,
        allUser: user,
        tripData: trip,
        fbUser,
        currentSpeedKmH,
        calculatedGpsSpeed:
          userCalculatedSpeedRef.current[uid] ??
          userCalculatedSpeedRef.current[actualTripId] ??
          userCalculatedSpeedRef.current[driverUid] ??
          userCalculatedSpeedRef.current[cargoUid] ??
          null,
        lat: Number(fbUser?.LiveLatitude ?? fbUser?.latitude ?? fbUser?.currentLat ?? trip?.latitude ?? user?.latitude ?? user?.currentLat),
        lng: Number(fbUser?.LiveLongitude ?? fbUser?.longitude ?? fbUser?.currentLng ?? trip?.longitude ?? user?.longitude ?? user?.currentLng),
        destLat: Number(trip?.destinationLat ?? trip?.destLat ?? trip?.deliveryLat),
        destLng: Number(trip?.destinationLong ?? trip?.destLng ?? trip?.deliveryLng),
        isTripActive,
        requestTimeMs,
        resolutionNote: trip?.resolutionNote || "",
      };
    });

    // Filtering
    const filtered = rows
      .filter((row) => {
        // Tab Filter: 0 = Active Escorts, 1 = History (Completed Trips)
        if (selectedTab === 0) {
          if (!row.isTripActive) return false;
        } else {
          if (row.isTripActive) return false;
        }

        // Search Filter
        if (
          searchText &&
          !(
            row.name.toLowerCase().includes(searchText) ||
            row.phone.toLowerCase().includes(searchText) ||
            row.escortId.toLowerCase().includes(searchText) ||
            (row.originCity && row.originCity.toLowerCase().includes(searchText)) ||
            (row.destinationCity && row.destinationCity.toLowerCase().includes(searchText))
          )
        ) {
          return false;
        }

        // Escort Type Filter (Matching all 4 categories)
        if (escortTypeFilter !== "All escort types") {
          if (escortTypeFilter === "User Escort") {
            if (row.escortCategory !== "User Escort" && row.type !== "User Escort") return false;
            if (row.escortCategory === "Virtual Travel Guard" || row.type === "Virtual Travel Guard") return false;
            if (row.escortCategory === "Drivers Escort" || row.type === "Driver Escort" || row.type === "Drivers Escort") return false;
            if (row.escortCategory === "Cargo Escort" || row.type === "Cargo Escort") return false;
          } else if (escortTypeFilter === "Virtual Travel Guard") {
            if (row.escortCategory !== "Virtual Travel Guard" && row.type !== "Virtual Travel Guard") return false;
          } else if (escortTypeFilter === "Drivers Escort" || escortTypeFilter === "Driver Escort") {
            if (row.escortCategory !== "Drivers Escort" && row.type !== "Driver Escort" && row.type !== "Drivers Escort") return false;
          } else if (escortTypeFilter === "Cargo Escort") {
            if (row.escortCategory !== "Cargo Escort" && row.type !== "Cargo Escort") return false;
          } else {
            if (row.type !== escortTypeFilter && row.escortCategory !== escortTypeFilter) return false;
          }
        }

        // In Active Escorts tab: Priority Card Filter
        if (selectedTab === 0) {
          if (priorityCardFilter === "Critical" && row.priority !== "Critical") return false;
          if (priorityCardFilter === "Needs Attention" && row.priority !== "Needs Attention") return false;
        }

        // In History tab: Status Card Filter
        if (selectedTab === 1) {
          if (
            historyStatusFilter === "Resolved" &&
            row.activity !== "Resolved" &&
            row.tripStatus !== "resolved"
          )
            return false;
          if (
            historyStatusFilter === "Cancelled" &&
            row.activity !== "Cancelled" &&
            row.tripStatus !== "canceled" &&
            row.tripStatus !== "cancelled"
          )
            return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (selectedTab === 1) {
          // In History: newest request first (strictly latest request at the top)
          const timeA = a.requestTimeMs || a.startedAtMs || a.effectiveTripTimeMs || 0;
          const timeB = b.requestTimeMs || b.startedAtMs || b.effectiveTripTimeMs || 0;
          return timeB - timeA;
        }
        // In Active: Priority sort: Critical → Needs Attention → Normal, then newest request first
        const pa = PRIORITY_RANK[a.priority] ?? 2;
        const pb = PRIORITY_RANK[b.priority] ?? 2;
        if (pa !== pb) return pa - pb;
        const timeA = a.requestTimeMs || a.startedAtMs || a.effectiveTripTimeMs || 0;
        const timeB = b.requestTimeMs || b.startedAtMs || b.effectiveTripTimeMs || 0;
        return timeB - timeA;
      });

    return {
      filteredRows: filtered,
      stats: {
        tracked: selectedTab === 0 ? trackedCount : activeData.length,
        critical: criticalCount,
        needsAttention: needsAttnCount,
        completed: selectedTab === 1 ? completedCount : (historyLoaded ? historyData.length : historyCount),
        resolved: resolvedCount,
        cancelled: cancelledCount,
      },
    };
  }, [allData, activeData.length, historyData.length, historyLoaded, historyCount, searchTerm, escortTypeFilter, priorityCardFilter, historyStatusFilter, selectedTab, firebaseUsers, ticker, resolvedStartPlaces]);

  // History pagination slice
  const paginatedRows = useMemo(() => {
    if (selectedTab === 0) return filteredRows;
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, selectedTab, page, rowsPerPage]);

  // Keep selectedRow fresh with live recalculated row data (e.g. priority escalation)
  useEffect(() => {
    if (selectedRow) {
      const fresh = filteredRows.find(
        (r) => r.id === selectedRow.id || (r.actualTripId && r.actualTripId === selectedRow.actualTripId)
      );
      if (
        fresh &&
        (fresh.priority !== selectedRow.priority ||
          fresh.movement !== selectedRow.movement ||
          fresh.wellbeing !== selectedRow.wellbeing ||
          fresh.userFBK !== selectedRow.userFBK)
      ) {
        setSelectedRow(fresh);
      }
    }
  }, [filteredRows, selectedRow]);

  // ── Announce Live User Responses in Voice Notification Bar ────────────────────
  const prevRowsFeedbackMapRef = useRef({});
  const isInitialRowsRenderRef = useRef(true);

  useEffect(() => {
    if (!filteredRows || filteredRows.length === 0) return;

    if (isInitialRowsRenderRef.current) {
      isInitialRowsRenderRef.current = false;
      filteredRows.forEach((row) => {
        prevRowsFeedbackMapRef.current[row.id] = {
          fbk: String(row.userFBK || "").trim(),
          wb: String(row.wellbeing || "").trim(),
        };
      });
      return;
    }

    filteredRows.forEach((row) => {
      const prev = prevRowsFeedbackMapRef.current[row.id] || { fbk: "", wb: "" };
      const currFbk = String(row.userFBK || "").trim();
      const currWb = String(row.wellbeing || "").trim();

      const hasNewFeedback =
        currFbk &&
        currFbk !== prev.fbk &&
        currFbk !== "No Feedback" &&
        currFbk !== "null" &&
        currFbk !== "undefined" &&
        currFbk !== "false";

      const hasNewCriticalStatus =
        currWb &&
        currWb !== prev.wb &&
        (currWb === "Danger" || currWb === "No Response");

      if (row.isTripActive && hasNewFeedback) {
        const uName = row.name || "User";
        if (currFbk === "I am in Danger" || currFbk.toLowerCase().includes("danger")) {
          enqueueVoice(`Emergency alert! ${uName} reported: I am in danger!`);
        } else if (currFbk === "Identity Verified") {
          enqueueVoice(`${uName} responded: Identity Verified.`);
        } else if (currFbk === "Traffic Issue") {
          enqueueVoice(`${uName} responded: Traffic Issue.`);
        } else if (currFbk === "I am Safe" || currFbk.toLowerCase().includes("safe")) {
          enqueueVoice(`Wellbeing response: ${uName} responded: I am Safe.`);
        } else {
          enqueueVoice(`${uName} responded: ${currFbk}`);
        }
      } else if (row.isTripActive && hasNewCriticalStatus) {
        const uName = row.name || "User";
        if (currWb === "Danger") {
          enqueueVoice(`Emergency alert! ${uName} reported in danger!`);
        } else if (currWb === "No Response") {
          enqueueVoice(`Warning: ${uName} has not responded to wellbeing check.`);
        }
      }

      prevRowsFeedbackMapRef.current[row.id] = {
        fbk: currFbk,
        wb: currWb,
      };
    });
  }, [filteredRows, enqueueVoice]);

  // ── Track Active Trips & End Voice When an Escort Ends ────────────────────────
  const prevActiveTripIdsRef = useRef(new Set());
  const isInitialActiveScanRef = useRef(true);

  useEffect(() => {
    const currentActiveTripIds = new Set();

    filteredRows.forEach((row) => {
      if (row.isTripActive) {
        currentActiveTripIds.add(String(row.actualTripId || row.id || row.userId || ""));
      }
    });

    if (isInitialActiveScanRef.current) {
      isInitialActiveScanRef.current = false;
      prevActiveTripIdsRef.current = currentActiveTripIds;
      return;
    }

    // Check if any active trip has ended
    prevActiveTripIdsRef.current.forEach((prevId) => {
      if (!currentActiveTripIds.has(prevId)) {
        console.log(`[Escort] Active trip ${prevId} has ended, stopping voice notification.`);
        stopVoiceNotification(prevId);
      }
    });

    // If no active trips remain, ensure voice notification is completely stopped
    if (currentActiveTripIds.size === 0 && prevActiveTripIdsRef.current.size > 0) {
      stopVoiceNotification();
    }

    prevActiveTripIdsRef.current = currentActiveTripIds;
  }, [filteredRows, stopVoiceNotification]);

  // ── Action Handlers ──────────────────────────────────────────────────────────
  const handleMenuOpen = (e, row) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const fetchNotificationLogs = useCallback(async (target) => {
    if (!target) return;
    setLoadingNotifications(true);
    try {
      const escortIdParam = target.escortId || target.actualTripId || target.escortDocumentId;
      const res = await apiClient.get(
        `/virtual-escort/admin/notifications/${encodeURIComponent(escortIdParam || "")}`,
        {
          params: {
            tripId: target.actualTripId || target.id,
            userId: target.userId,
            startedAtMs: target.startedAtMs,
            endedAtMs: target.endedAtMs,
          },
        }
      );
      // apiClient response interceptor unwraps response.data:
      const rawLogs = res?.data?.logs || res?.logs || (Array.isArray(res?.data) ? res.data : []);
      const logs = Array.isArray(rawLogs) ? rawLogs : [];

      // Strictly filter to current trip only (trip-wise)
      const currentTripLogs = logs.filter((log) => {
        // If log explicitly specifies a tripId and target has actualTripId:
        if (target.actualTripId && log.tripId) {
          return String(log.tripId) === String(target.actualTripId);
        }
        // If log has a tripId that doesn't match target, it belongs to another trip
        if (log.tripId && target.actualTripId && String(log.tripId) !== String(target.actualTripId)) {
          return false;
        }
        // If escortId is a genuine unique request id (length > 8, e.g. ESC-YYYYMMDD-XXXX)
        const isGenericEscort = Boolean(log.escortId && String(log.escortId).length <= 8);
        if (!isGenericEscort && target.escortId && log.escortId && log.escortId === target.escortId) {
          return true;
        }
        // If sent during this trip's timeframe for this user
        if (target.startedAtMs && log.sentAt && String(log.userId) === String(target.userId)) {
          const sentMs = new Date(log.sentAt).getTime();
          const isAfterStart = sentMs >= target.startedAtMs - 2 * 60 * 1000;
          const isBeforeEnd = !target.endedAtMs || sentMs <= target.endedAtMs + 2 * 60 * 1000;
          return isAfterStart && isBeforeEnd;
        }
        return false;
      });

      setNotificationLogs(currentTripLogs);
    } catch (err) {
      console.warn("fetchNotificationLogs REST fallback via socket:", err?.message);
      socketService.emit(
        "get_escort_notifications",
        {
          escortId: target.escortId,
          tripId: target.actualTripId || target.id,
          userId: target.userId,
          startedAtMs: target.startedAtMs,
          endedAtMs: target.endedAtMs,
        },
        (res) => {
          const rawLogs = res?.data?.logs || res?.logs || [];
          if (Array.isArray(rawLogs)) {
            const filtered = rawLogs.filter((log) => {
              if (target.actualTripId && log.tripId) {
                return String(log.tripId) === String(target.actualTripId);
              }
              if (log.tripId && target.actualTripId && String(log.tripId) !== String(target.actualTripId)) {
                return false;
              }
              const isGenericEscort = Boolean(log.escortId && String(log.escortId).length <= 8);
              if (!isGenericEscort && target.escortId && log.escortId && log.escortId === target.escortId) {
                return true;
              }
              if (target.startedAtMs && log.sentAt && String(log.userId) === String(target.userId)) {
                const sentMs = new Date(log.sentAt).getTime();
                const isAfterStart = sentMs >= target.startedAtMs - 2 * 60 * 1000;
                const isBeforeEnd = !target.endedAtMs || sentMs <= target.endedAtMs + 2 * 60 * 1000;
                return isAfterStart && isBeforeEnd;
              }
              return false;
            });
            setNotificationLogs(filtered);
          }
        }
      );
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  const openNotificationHistory = (row) => {
    const target = row || selectedRow;
    if (!target) return;
    setSelectedNotificationRow(target);
    setNotificationHistoryOpen(true);
    fetchNotificationLogs(target);
  };

  const openMonitoring = (row) => {
    setSelectedRow(row);
    setResolutionNote(row?.resolutionNote || "");
    setMonitoringOpen(true);
    fetchNotificationLogs(row);
  };

  const handleManualCheck = async (row) => {
    const target = row || selectedRow;
    if (!target) return;
    const targetTripId = String(target.actualTripId || target.id || target.escortDocumentId || target.escortId || "");
    const nowMs = Date.now();
    try {
      // 1. Send via Sockets to backend & active user
      socketService.emit("manual_wellbeing_check", {
        escortId: target.escortId,
        tripId: targetTripId,
        userId: target.userId,
        state: target.state,
        name: target.name,
        phone: target.phone,
        userName: target.name,
        userPhone: target.phone,
        adminName: adminName,
        source: "escort",
        fromEscort: "true",
        vibrate: true,
      });

      // 2. Also emit push notification event for fallback / mobile background handling
      const isTripEnded = Boolean(
        target.tripData?.driverEnded ||
        target.driverEnded ||
        String(target.status || target.tripData?.status || "").toLowerCase().includes("awaiting")
      );
      const pushTitle = isTripEnded ? "Escort ended" : "Wellbeing Check";
      const pushMsg = isTripEnded
        ? "Are you safe? Please confirm so we can close this trip."
        : "Please Confirm Your Safety";

      socketService.emit("sendcustompushnotification", {
        userId: target.userId,
        title: pushTitle,
        message: pushMsg,
        action: "wellbeingcheck",
        escortId: target.escortId,
        tripId: targetTripId,
        userName: target.name,
        userPhone: target.phone,
        adminName: adminName,
        source: "escort",
        fromEscort: "true",
        vibrate: true,
      });

      // 3. Update Firebase Realtime Database immediately with tripId scoping
      if (target.userId) {
        try {
          const db = getDatabase();
          update(ref(db, `users/${target.userId}`), {
            Wellbeing_Response: "Awaiting Response",
            wellbeingResponse: "Awaiting Response",
            wellbeingCheckSource: "Sent by Escort Staff",
            wellbeingSentAt: nowMs,
            wellbeingTripId: targetTripId,
            wellbeingPushStatus: "Delivered",
            userFBK: null,
            userFeedback: null,
            feedback: null,
          });
        } catch (fbErr) {
          console.warn("Direct Firebase update notice:", fbErr?.message);
        }

        setFirebaseUsers((prev) => ({
          ...prev,
          [target.userId]: {
            ...(prev[target.userId] || {}),
            Wellbeing_Response: "Awaiting Response",
            wellbeingResponse: "Awaiting Response",
            wellbeingCheckSource: "Sent by Escort Staff",
            wellbeingSentAt: nowMs,
            wellbeingTripId: targetTripId,
            wellbeingPushStatus: "Delivered",
            userFBK: null,
            userFeedback: null,
            feedback: null,
          },
        }));

        setAllData((prev) =>
          prev.map((item) => {
            const itemTripId = String(item.actualTripId || item._id || item.id || item.escortDocumentId || item.tripData?._id || "");
            const itemUserId = String(item.userId || item.tripData?.userId || "");
            if (item.isTripActive && (itemTripId === targetTripId || itemUserId === String(target.userId))) {
              return {
                ...item,
                wellbeing: "Awaiting Response",
                wellbeingSentAtMs: nowMs,
                userFBK: null,
                tripData: {
                  ...item.tripData,
                  Wellbeing_Response: "Awaiting Response",
                  wellbeingResponse: "Awaiting Response",
                  wellbeingSentAt: nowMs,
                  userFBK: null,
                  userFeedback: null,
                  feedback: null,
                },
                userData: {
                  ...item.userData,
                  Wellbeing_Response: "Awaiting Response",
                  wellbeingResponse: "Awaiting Response",
                  wellbeingSentAt: nowMs,
                  userFBK: null,
                  userFeedback: null,
                  feedback: null,
                },
              };
            }
            return item;
          })
        );
      }

      setStoredAwaitingResponse(targetTripId, nowMs);
      setStoredWellbeingSentAt(targetTripId, nowMs);
      setStoredResponseDuration(targetTripId, null);
      if (target.userId) {
        setStoredAwaitingResponse(String(target.userId), null);
        setStoredWellbeingSentAt(String(target.userId), null);
        setStoredResponseDuration(String(target.userId), null);
      }

      // 4. Optional REST API call with catch to prevent blocking if endpoint 404s
      if (target.escortId) {
        apiClient
          .post(`/virtual-escort/admin/manual-check/${target.escortId}`, {
            userId: target.userId,
            state: target.state,
            tripId: targetTripId,
          })
          .catch((err) => {
            console.warn("HTTP manual-check fallback notice (socket was sent):", err?.message);
          });
      }

      toast.success(`Wellbeing check sent to ${target.name}. Response timer started.`);
      getData();
    } catch (e) {
      console.error("Manual check error:", e);
      toast.error("Failed to send wellbeing check");
    }
  };

  const handleResolve = async (row) => {
    const target = row || selectedRow;
    if (!target) return;
    try {
      if (target.escortId) {
        await apiClient.put(`/virtual-escort/admin/resolve/${target.escortId}`, {
          resolutionNote,
        });
      }

      if (target.userId) {
        try {
          const db = getDatabase();
          update(ref(db, `users/${target.userId}`), {
            Wellbeing_Response: "Not Triggered",
            wellbeingCheckSource: null,
            wellbeingSentAt: null,
            wellbeingTripId: null,
            wellbeingPushStatus: null,
            userFBK: null,
            stoppedAt: null,
          });
        } catch {}

        const resolveKey = target.actualTripId || target.escortId || target.userId;
        delete userStoppedMapRef.current[resolveKey];
        delete userStoppedMapRef.current[target.userId];
        setStoredStoppedAt(resolveKey, null);
        setStoredStoppedAt(target.userId, null);
        setStoredWellbeingSentAt(resolveKey, null);
      }

      stopVoiceNotification(target.escortId || target.userId || target.actualTripId || target.name);
      toast.success(`Escort marked as Resolved for ${target.name}`);
      setMonitoringOpen(false);
      getData();
    } catch (e) {
      toast.error("Failed to mark as resolved");
    }
  };

  const handleAction = async (action) => {
    handleMenuClose();
    if (!selectedRow) return;

    switch (action) {
      case "monitoring":
        openMonitoring(selectedRow);
        break;
      case "check-arrival":
        handleManualCheck(selectedRow);
        break;
      case "call-user":
        window.open(`tel:${selectedRow.phone}`);
        break;
      case "view-map":
        navigate(`/LiveTracking/${selectedRow.userId || selectedRow.actualTripId || selectedRow.escortId}`, {
          state: {
            escort: selectedRow,
            tripData: selectedRow?.tripData || selectedRow,
            pickupLocation: selectedRow?.originCity || selectedRow?.pickupLocation,
            deliveryLocation: selectedRow?.destinationCity || selectedRow?.deliveryLocation,
            isTripActive: selectedRow?.isTripActive,
            status: selectedRow?.tripStatus,
            wellbeing: selectedRow?.wellbeing,
            userFBK: selectedRow?.userFBK,
          },
        });
        break;
      case "open-evidence":
        if (selectedRow?.evidence) {
          setSelectedEvidenceRow(selectedRow);
          setEvidenceFileUrl(selectedRow.evidenceUrl || "");
          setEvidenceTab(selectedRow.videos?.length > 0 ? "submissions" : "vehicle");
          setImageRotation(0);
          setEvidenceModalOpen(true);
        } else {
          toast.info("No evidence file or submissions attached");
        }
        break;
      case "push":
        setOpenPushModal(true);
        break;
      case "view-notifications":
        openNotificationHistory(selectedRow);
        break;
      case "notify-emergency":
        setNotifyEmergencyOpen(true);
        break;
      case "dispatch-security":
        socketService.emit("sendalert", {
          payload: {
            alert_type: "Emergency",
            alert_message: "Emergency service dispatched by admin",
            userId: selectedRow.userId,
            escortId: selectedRow.escortId,
            source: "escort",
            fromEscort: "true",
            vibrate: true,
          },
          isBroadcast: false,
        });
        toast.info(`Security dispatched for ${selectedRow.name}`);
        break;
      case "send-email":
      case "send-message":
        setOpenMessage(true);
        break;
      case "lets-chat":
        dispatch(addChatWindow({ userId: selectedRow.userId, name: selectedRow.name }));
        break;
      case "resolve":
        handleResolve(selectedRow);
        break;
      case "delete": {
        const deleteId =
          selectedRow.actualTripId ||
          selectedRow.tripData?._id ||
          selectedRow.tripData?.id ||
          selectedRow.tripData?.key ||
          selectedRow.escortId ||
          selectedRow.userId;

        if (!deleteId || deleteId === "undefined") {
          toast.error("Cannot delete escort: identifier not found");
          break;
        }

        setRowToDelete(selectedRow);
        setDeleteConfirmOpen(true);
        break;
      }
      case "close-trip":
        handleCloseTrip(selectedRow);
        break;
      default:
        break;
    }
  };

  const executeTripCompletion = async (target) => {
    if (!target) return;
    console.log("[executeTripCompletion] Starting trip completion...", { target });
    const targetTripId =
      target.actualTripId ||
      target.tripData?._id ||
      target.tripData?.id ||
      target.escortId ||
      target.userId;

    try {
      setLoading(true);
      const isDriver = Boolean(
        target.type === "Driver Escort" ||
        target.type === "Drivers Escort" ||
        target.escortCategory === "Drivers Escort" ||
        target._escortKind === "Driver Escort" ||
        target.tripData?.driverId
      );

      if (isDriver && (target.actualTripId || target.tripData?._id)) {
        const dTripId = target.actualTripId || target.tripData?._id;
        await apiClient.put(`/escort-trip/complete/${encodeURIComponent(dTripId)}`, {
          wellbeingStatus: "SAFE",
          wellbeingResponse: "Responded",
          userFBK: "Yes, I'm safe",
        }).catch(() => {});
        apiClient.delete(`/escort-trip/delete/${encodeURIComponent(dTripId)}`).catch(() => {});
      }

      await apiClient.delete(`/users/admin/escort/${encodeURIComponent(targetTripId)}`, {
        params: {
          type: target.type || target.escortCategory || (isDriver ? "Driver Escort" : undefined),
          userId: target.userId || target.tripData?.userObjectKey,
          tripId: target.actualTripId || target.tripData?._id,
        },
      }).catch(() => {});

      const uId = target.userId || target.tripData?.userObjectKey;
      if (uId) {
        // 1. Update ShortDistance_Escort in backend MongoDB
        try {
          const userResponse = await apiClient.get(`/users/${uId}`);
          const userData = userResponse?.data || userResponse?.data?.data || userResponse || {};
          const rawEscorts = userData?.ShortDistance_Escort;
          const escortEntries = Array.isArray(rawEscorts)
            ? rawEscorts
            : rawEscorts && typeof rawEscorts === "object"
            ? Object.values(rawEscorts)
            : [];
          if (escortEntries.length > 0) {
            const latestIdx = escortEntries.length - 1;
            const updatedEscortEntries = [...escortEntries];
            const endedAt = Date.now();
            const adminInfo = JSON.parse(localStorage.getItem("user") || "{}");
            updatedEscortEntries[latestIdx] = {
              ...updatedEscortEntries[latestIdx],
              Trip_Status: "Ended",
              escortStatus: "ended",
              escortActive: false,
              ended: true,
              tripendtime: endedAt,
              escortEndedAt: endedAt,
              endedByAdminId: adminInfo?.userId || adminInfo?.id || adminInfo?._id?.toString?.() || null,
              endedByAdminName: adminInfo?.name || adminInfo?.userName || "Admin",
            };
            await apiClient.put(`/users/${uId}`, {
              ShortDistance_Escort: updatedEscortEntries,
              latestEscort: updatedEscortEntries[latestIdx],
            }).catch(() => {});
          }
        } catch (_) {}

        // 2. Update Firebase Realtime Database
        try {
          const db = getDatabase();
          update(ref(db, `users/${uId}`), {
            Wellbeing_Response: "Responded",
            wellbeingResponse: "Responded",
            userFBK: "Yes, I'm safe",
            Trip_Status: "Completed",
            status: "completed",
            escortInProgress: false,
            driverEnded: false,
            tracking: false,
            IsMoving: false,
            is_moving: false,
          }).catch(() => {});
        } catch (_) {}

        // 3. Emit socket event to mobile app to finish & close active trip screen
        try {
          socketService.emit("endescort", {
            userId: uId,
            userObjectKey: uId,
            action: "endescort",
          });
        } catch (_) {}
      }

      // Cleanup pending close
      const tripKey = String(target.actualTripId || target.escortId || target.userId || "");
      delete pendingCloseTripsRef.current[tripKey];
      if (target.userId) {
        delete pendingCloseTripsRef.current[String(target.userId)];
      }

      stopVoiceNotification(targetTripId || target.userId);
      toast.success(`Trip closed successfully for ${target.name || "user"}`);
      setSelectedRow(null);
      getData();
    } catch (err) {
      console.error("[Escort] close trip error:", err);
      toast.error(err?.message || "Failed to close trip");
    } finally {
      setLoading(false);
    }
  };

  executeTripCompletionRef.current = executeTripCompletion;

  const sendCloseTripSafetyCheck = async (target) => {
    if (!target) return;
    const targetTripId = String(target.actualTripId || target.id || target.escortDocumentId || target.escortId || "");
    const nowMs = Date.now();
    const targetUserId = target.userId;

    console.log("[sendCloseTripSafetyCheck] User not verified safe yet. Sending push notification & modal to user app...", {
      targetUserId,
      targetTripId,
      targetName: target?.name,
      title: "Escort ended",
      message: "Are you safe? Please confirm so we can close this trip.",
    });

    try {
      // 1. Sockets: pushnotificationevent & manual_wellbeing_check & sendcustompushnotification
      socketService.emit("pushnotificationevent", {
        userId: targetUserId,
        action: "wellbeingcheck",
      });

      socketService.emit("manual_wellbeing_check", {
        escortId: target.escortId,
        tripId: targetTripId,
        userId: targetUserId,
        state: target.state,
        name: target.name,
        phone: target.phone,
        userName: target.name,
        userPhone: target.phone,
        adminName: adminName,
        source: "escort",
        fromEscort: "true",
        vibrate: true,
      });

      // Emits custom push notification matching Image 1:
      // Title: "Escort ended"
      // Message: "Are you safe? Please confirm so we can close this trip."
      socketService.emit("sendcustompushnotification", {
        userId: targetUserId,
        title: "Escort ended",
        message: "Are you safe? Please confirm so we can close this trip.",
        action: "wellbeingcheck",
        escortId: target.escortId,
        tripId: targetTripId,
        userName: target.name,
        userPhone: target.phone,
        adminName: adminName,
        source: "escort",
        fromEscort: "true",
        vibrate: true,
      });

      // 2. REST API for background notification
      apiClient
        .post("/virtual-escort/admin/send-push", {
          userId: targetUserId,
          escortId: target.escortId,
          tripId: targetTripId,
          action: "wellbeingcheck",
          title: "Escort ended",
          message: "Are you safe? Please confirm so we can close this trip.",
          userName: target.name,
          userPhone: target.phone,
          adminName: adminName,
        })
        .catch(() => {});

      // 3. Update Firebase Realtime Database with driverEnded: true to pop the safety modal in the app
      if (targetUserId) {
        try {
          const db = getDatabase();
          update(ref(db, `users/${targetUserId}`), {
            Wellbeing_Response: "Awaiting Response",
            wellbeingResponse: "Awaiting Response",
            wellbeingCheckSource: "Close Trip Safety Verification",
            wellbeingSentAt: nowMs,
            wellbeingTripId: targetTripId,
            wellbeingPushStatus: "Delivered",
            driverEnded: true,
            userFBK: null,
            userFeedback: null,
            feedback: null,
          });
        } catch (fbErr) {
          console.warn("Direct Firebase update notice:", fbErr?.message);
        }

        setFirebaseUsers((prev) => ({
          ...prev,
          [targetUserId]: {
            ...(prev[targetUserId] || {}),
            Wellbeing_Response: "Awaiting Response",
            wellbeingResponse: "Awaiting Response",
            wellbeingCheckSource: "Close Trip Safety Verification",
            wellbeingSentAt: nowMs,
            wellbeingTripId: targetTripId,
            wellbeingPushStatus: "Delivered",
            driverEnded: true,
            userFBK: null,
            userFeedback: null,
            feedback: null,
          },
        }));

        setAllData((prev) =>
          prev.map((item) => {
            const itemTripId = String(item.actualTripId || item._id || item.id || item.escortDocumentId || item.tripData?._id || "");
            const itemUserId = String(item.userId || item.tripData?.userId || "");
            if (item.isTripActive && (itemTripId === targetTripId || itemUserId === String(targetUserId))) {
              return {
                ...item,
                wellbeing: "Awaiting Response",
                wellbeingSentAtMs: nowMs,
                userFBK: null,
                driverEnded: true,
                tripData: {
                  ...item.tripData,
                  Wellbeing_Response: "Awaiting Response",
                  wellbeingResponse: "Awaiting Response",
                  wellbeingSentAt: nowMs,
                  driverEnded: true,
                  userFBK: null,
                  userFeedback: null,
                  feedback: null,
                },
                userData: {
                  ...item.userData,
                  Wellbeing_Response: "Awaiting Response",
                  wellbeingResponse: "Awaiting Response",
                  wellbeingSentAt: nowMs,
                  driverEnded: true,
                  userFBK: null,
                  userFeedback: null,
                  feedback: null,
                },
              };
            }
            return item;
          })
        );
      }

      setStoredAwaitingResponse(targetTripId, nowMs);
      setStoredWellbeingSentAt(targetTripId, nowMs);
      setStoredResponseDuration(targetTripId, null);

      toast.info(`Safety verification sent to ${target.name || "user"}. Trip will close once user confirms "Yes, I'm safe".`);
    } catch (e) {
      console.error("[Escort] close trip safety check error:", e);
      toast.error("Failed to send safety verification to user");
    }
  };

  const handleCloseTrip = async (row, force = false) => {
    const target = row || selectedRow;
    console.log("[handleCloseTrip] Invoked with:", { row, selectedRow, target, force });
    if (!target) {
      console.warn("[handleCloseTrip] No target row/selectedRow found, aborting.");
      return;
    }

    // Check if user already confirmed "safe"
    const fbUser = target.userId ? firebaseUsersRef.current[target.userId] : null;
    const currentFeedback = String(
      target.userFBK ||
      fbUser?.userFBK ||
      fbUser?.userFeedback ||
      target.tripData?.userFBK ||
      ""
    ).trim().toLowerCase();

    const isConfirmedSafe =
      currentFeedback.includes("safe") ||
      currentFeedback === "yes, i'm safe" ||
      currentFeedback === "i am safe";

    console.log("[handleCloseTrip] Safety check status:", {
      currentFeedback,
      isConfirmedSafe,
      force,
    });

    if (!force && !isConfirmedSafe) {
      console.log("[handleCloseTrip] Safe confirmation not found yet. Registering pending close and sending modal to user.");
      const tripKey = String(target.actualTripId || target.escortId || target.userId || "");
      pendingCloseTripsRef.current[tripKey] = target;
      if (target.userId) {
        pendingCloseTripsRef.current[String(target.userId)] = target;
      }

      await sendCloseTripSafetyCheck(target);
      return;
    }

    console.log("[handleCloseTrip] User is safe (or forced). Proceeding to executeTripCompletion.");
    await executeTripCompletion(target);
  };

  const handleConfirmDelete = async () => {
    const target = rowToDelete || selectedRow;
    if (!target) return;

    const targetTripId =
      target.actualTripId ||
      target.tripData?._id ||
      target.tripData?.id ||
      target.tripData?.key ||
      target.escortId ||
      target.userId;

    const deleteId = targetTripId;

    if (!deleteId || deleteId === "undefined") {
      toast.error("Cannot delete escort: identifier not found");
      setDeleteConfirmOpen(false);
      setRowToDelete(null);
      return;
    }

    setIsDeleting(true);
    try {
      const isDriver = Boolean(
        target.type === "Driver Escort" ||
        target.type === "Drivers Escort" ||
        target.escortCategory === "Drivers Escort" ||
        target._escortKind === "Driver Escort" ||
        target.tripData?.driverId
      );

      // If it's a driver escort, invoke /escort-trip/delete/:id as well to ensure immediate database deletion
      if (isDriver && (target.actualTripId || target.tripData?._id)) {
        const dTripId = target.actualTripId || target.tripData?._id;
        apiClient.delete(`/escort-trip/delete/${encodeURIComponent(dTripId)}`).catch(() => {});
      }

      await apiClient.delete(`/users/admin/escort/${encodeURIComponent(deleteId)}`, {
        params: {
          type: target.type || target.escortCategory || (isDriver ? "Driver Escort" : undefined),
          userId: target.userId || target.tripData?.userObjectKey,
          tripKey: target.tripData?.key || (deleteId !== target.userId ? deleteId : undefined),
          tripId: target.actualTripId || target.tripData?._id,
          userObjectKey: target.tripData?.userObjectKey || target.userId,
        },
      });
      setAllData((prev) =>
        prev.filter((u) => {
          const docId = String(u?.escortDocumentId || u?._id || u?.id || "");
          const tripId = String(
            u?.tripData?._id ||
            u?.tripData?.id ||
            u?.tripData?.key ||
            u?.actualTripId ||
            ""
          );
          const tripKey = String(u?.tripData?.payload?.key || u?.tripData?.virtaulGuardKey || "");
          const dId = String(deleteId);
          const selTripKey = String(target.tripData?.key || target.actualTripId || "");

          const matchTrip =
            (docId && docId === dId) ||
            (tripId && tripId === dId) ||
            (targetTripId && (docId === String(targetTripId) || tripId === String(targetTripId))) ||
            (tripKey && (tripKey === dId || (selTripKey && tripKey === selTripKey))) ||
            (selTripKey && (docId === selTripKey || tripId === selTripKey));

          const matchUser =
            u?.userId &&
            String(u.userId) === String(target.userId) &&
            (!u?.tripData || (!docId && !tripId));

          return !matchTrip && !matchUser;
        })
      );
      const uId = target.userId || target.tripData?.userObjectKey;
      if (uId) {
        try {
          const db = getDatabase();
          update(ref(db, `users/${uId}`), {
            Trip_Status: "Completed",
            status: "completed",
            escortInProgress: false,
            driverEnded: false,
            tracking: false,
            IsMoving: false,
            is_moving: false,
          }).catch(() => {});
        } catch (_) {}

        try {
          socketService.emit("endescort", {
            userObjectKey: uId,
            action: "endescort",
          });
        } catch (_) {}
      }

      delete userStoppedMapRef.current[deleteId];
      delete userStoppedMapRef.current[target.actualTripId];
      setStoredStoppedAt(deleteId, null);
      setStoredStoppedAt(target.actualTripId, null);
      setStoredWellbeingSentAt(deleteId, null);
      setStoredWellbeingSentAt(target.actualTripId, null);
      stopVoiceNotification(deleteId || target.actualTripId || target.userId);
      toast.success("Escort deleted successfully");
      setSelectedRow(null);
      getData();
      setDeleteConfirmOpen(false);
      setRowToDelete(null);
    } catch (err) {
      console.error("[Escort] delete error", err);
      toast.error(err?.message || "Failed to delete escort");
    } finally {
      setIsDeleting(false);
    }
  };

  const unresolvedCount = stats.critical + stats.needsAttention;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        {/* ── 1. PAGE TITLE & REFRESH ── */}
        <Box
          sx={{
            mb: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.65rem",
                color: "#1e293b",
                letterSpacing: "-0.01em",
              }}
            >
              All-State Escort Management Dashboard
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: "0.9rem", mt: 0.3 }}>
              Real-time monitoring of all user, driver and cargo escorts across Nigeria
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Tooltip title={selectedTab === 0 ? "Refresh active escort data" : "Refresh escort trip history"}>
              <IconButton
                onClick={() => {
                  if (selectedTab === 0) {
                    getActiveData();
                  } else {
                    getHistoryData();
                  }
                }}
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  p: 1,
                  "&:hover": { bgcolor: "#f1f5f9" },
                }}
              >
                <RefreshIcon fontSize="small" sx={{ color: "#475569" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>


        {/* ── 2. TABS (Active Escorts vs History) ── */}
        <Box sx={{ borderBottom: "1px solid #e2e8f0", mb: 2.5 }}>
          <Tabs
            value={selectedTab}
            onChange={(_, val) => {
              setSelectedTab(val);
              setPage(0);
              setPriorityCardFilter("all");
              setHistoryStatusFilter("all");
              if (val === 1) {
                getHistoryData(historyLoadedRef.current);
              } else {
                getActiveData(true);
              }
            }}
            sx={{
              "& .MuiTabs-indicator": {
                bgcolor: "#1565c0",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                minWidth: 150,
                color: "#64748b",
                py: 1.5,
                "&.Mui-selected": {
                  color: "#1565c0",
                },
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>Active Escorts</span>
                  <Chip
                    label={stats.tracked}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      bgcolor: selectedTab === 0 ? "#1565c0" : "#e2e8f0",
                      color: selectedTab === 0 ? "#fff" : "#475569",
                      borderRadius: "6px",
                    }}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>History</span>
                  <Chip
                    label={historyLoading ? "..." : stats.completed}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      bgcolor: selectedTab === 1 ? "#1565c0" : "#e2e8f0",
                      color: selectedTab === 1 ? "#fff" : "#475569",
                      borderRadius: "6px",
                    }}
                  />
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* ── 3. SUMMARY CARDS (Clickable Filters) ── */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          {selectedTab === 0 ? (
            <>
              {/* Card 1: Tracked now */}
              <Grid item xs={12} md={4}>
                <Box
                  onClick={() => setPriorityCardFilter("all")}
                  sx={{
                    bgcolor: "#fff",
                    border: priorityCardFilter === "all" ? "2px solid #1565c0" : "1px solid #e2e8f0",
                    borderRadius: "10px",
                    p: 2.5,
                    cursor: "pointer",
                    transition: "all 0.15s ease-in-out",
                    boxShadow: priorityCardFilter === "all" ? "0 4px 12px rgba(21,101,192,0.12)" : "0 1px 3px rgba(0,0,0,0.04)",
                    "&:hover": { borderColor: "#1565c0" },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "2.4rem", color: "#1565c0", lineHeight: 1 }}>
                    {stats.tracked}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.92rem", fontWeight: 600, mt: 0.8 }}>
                    Tracked now
                  </Typography>
                </Box>
              </Grid>

              {/* Card 2: Critical */}
              <Grid item xs={12} md={4}>
                <Box
                  onClick={() => setPriorityCardFilter(priorityCardFilter === "Critical" ? "all" : "Critical")}
                  sx={{
                    bgcolor: "#fff",
                    border: priorityCardFilter === "Critical" ? "2px solid #dc2626" : "1px solid #e2e8f0",
                    borderRadius: "10px",
                    p: 2.5,
                    cursor: "pointer",
                    transition: "all 0.15s ease-in-out",
                    boxShadow: priorityCardFilter === "Critical" ? "0 4px 12px rgba(220,38,38,0.12)" : "0 1px 3px rgba(0,0,0,0.04)",
                    "&:hover": { borderColor: "#dc2626" },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "2.4rem", color: "#dc2626", lineHeight: 1 }}>
                    {stats.critical}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.92rem", fontWeight: 600, mt: 0.8 }}>
                    Critical
                  </Typography>
                </Box>
              </Grid>

              {/* Card 3: Needs Attention */}
              <Grid item xs={12} md={4}>
                <Box
                  onClick={() => setPriorityCardFilter(priorityCardFilter === "Needs Attention" ? "all" : "Needs Attention")}
                  sx={{
                    bgcolor: "#fff",
                    border: priorityCardFilter === "Needs Attention" ? "2px solid #d97706" : "1px solid #e2e8f0",
                    borderRadius: "10px",
                    p: 2.5,
                    cursor: "pointer",
                    transition: "all 0.15s ease-in-out",
                    boxShadow: priorityCardFilter === "Needs Attention" ? "0 4px 12px rgba(217,119,6,0.12)" : "0 1px 3px rgba(0,0,0,0.04)",
                    "&:hover": { borderColor: "#d97706" },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "2.4rem", color: "#d97706", lineHeight: 1 }}>
                    {stats.needsAttention}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.92rem", fontWeight: 600, mt: 0.8 }}>
                    Needs Attention
                  </Typography>
                </Box>
              </Grid>
            </>
          ) : (
            <>
              {/* Card 1: Total Completed */}
              <Grid item xs={12} md={4}>
                <Box
                  onClick={() => setHistoryStatusFilter("all")}
                  sx={{
                    bgcolor: "#fff",
                    border: historyStatusFilter === "all" ? "2px solid #1565c0" : "1px solid #e2e8f0",
                    borderRadius: "10px",
                    p: 2.5,
                    cursor: "pointer",
                    transition: "all 0.15s ease-in-out",
                    boxShadow: historyStatusFilter === "all" ? "0 4px 12px rgba(21,101,192,0.12)" : "0 1px 3px rgba(0,0,0,0.04)",
                    "&:hover": { borderColor: "#1565c0" },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "2.4rem", color: "#1565c0", lineHeight: 1 }}>
                    {stats.completed}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.92rem", fontWeight: 600, mt: 0.8 }}>
                    Total Completed Trips
                  </Typography>
                </Box>
              </Grid>

              {/* Card 2: Resolved */}
              <Grid item xs={12} md={4}>
                <Box
                  onClick={() => setHistoryStatusFilter(historyStatusFilter === "Resolved" ? "all" : "Resolved")}
                  sx={{
                    bgcolor: "#fff",
                    border: historyStatusFilter === "Resolved" ? "2px solid #16a34a" : "1px solid #e2e8f0",
                    borderRadius: "10px",
                    p: 2.5,
                    cursor: "pointer",
                    transition: "all 0.15s ease-in-out",
                    boxShadow: historyStatusFilter === "Resolved" ? "0 4px 12px rgba(22,163,74,0.12)" : "0 1px 3px rgba(0,0,0,0.04)",
                    "&:hover": { borderColor: "#16a34a" },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "2.4rem", color: "#16a34a", lineHeight: 1 }}>
                    {stats.resolved}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.92rem", fontWeight: 600, mt: 0.8 }}>
                    Resolved Trips
                  </Typography>
                </Box>
              </Grid>

              {/* Card 3: Cancelled */}
              <Grid item xs={12} md={4}>
                <Box
                  onClick={() => setHistoryStatusFilter(historyStatusFilter === "Cancelled" ? "all" : "Cancelled")}
                  sx={{
                    bgcolor: "#fff",
                    border: historyStatusFilter === "Cancelled" ? "2px solid #64748b" : "1px solid #e2e8f0",
                    borderRadius: "10px",
                    p: 2.5,
                    cursor: "pointer",
                    transition: "all 0.15s ease-in-out",
                    boxShadow: historyStatusFilter === "Cancelled" ? "0 4px 12px rgba(100,116,139,0.12)" : "0 1px 3px rgba(0,0,0,0.04)",
                    "&:hover": { borderColor: "#64748b" },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "2.4rem", color: "#64748b", lineHeight: 1 }}>
                    {stats.cancelled}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.92rem", fontWeight: 600, mt: 0.8 }}>
                    Cancelled Trips
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
        </Grid>

        {/* ── 4. VOICE NOTIFICATION BAR (BROADCAST & USER RESPONSES) ── */}
        <Box sx={{ mb: 2 }}>
          <VoiceNotificationBar
            incomingAlert={lastVoiceAlert}
            isMuted={isVoiceMuted}
            onToggleMute={toggleMute}
            stopVoiceTrigger={voiceStopTrigger}
            hasActiveTrips={Boolean(stats?.tracked && stats.tracked > 0)}
            activeEscortCount={stats?.tracked || 0}
          />
        </Box>

        {/* ── 5. CONTROLS & FILTER TOOLBAR ── */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 2.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Search Box */}
          <TextField
            placeholder="Search name, mobile, Escort ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 260,
              bgcolor: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                height: 44,
                borderRadius: "8px",
              },
            }}
          />

          {/* Escort Type Selector */}
          <FormControl sx={{ minWidth: 190 }}>
            <Select
              value={escortTypeFilter}
              onChange={(e) => setEscortTypeFilter(e.target.value)}
              sx={{
                bgcolor: "#fff",
                height: 44,
                borderRadius: "8px",
                fontWeight: 500,
                color: "#334155",
                "& .MuiSelect-select": { py: "10px" },
              }}
            >
              <MenuItem value="All escort types">All escort types</MenuItem>
              <MenuItem value="User Escort">User Escort</MenuItem>
              <MenuItem value="Virtual Travel Guard">Virtual Travel Guard</MenuItem>
              <MenuItem value="Drivers Escort">Drivers Escort</MenuItem>
              <MenuItem value="Cargo Escort">Cargo Escort</MenuItem>
            </Select>
          </FormControl>

          {/* Voice Sound Toggle Button */}
          <Button
            variant="outlined"
            onClick={toggleMute}
            startIcon={isVoiceMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
            sx={{
              height: 44,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.84rem",
              borderColor: isVoiceMuted ? "#d1d5db" : "#3b82f6",
              color: isVoiceMuted ? "#6b7280" : "#1d4ed8",
              bgcolor: isVoiceMuted ? "#f9fafb" : "#eff6ff",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#2563eb",
                bgcolor: "#dbeafe",
              },
            }}
          >
            {isVoiceMuted ? "Sound Muted" : "Voice Alerts On"}
          </Button>

          {/* Urgent Count Pill if any */}
          {unresolvedCount > 0 && (
            <Chip
              color="error"
              icon={<NotificationsNoneOutlinedIcon />}
              label={`${unresolvedCount} Urgent`}
              sx={{ fontWeight: 700, height: 42, px: 0.5, borderRadius: "8px" }}
            />
          )}
        </Box>

        {/* ── 6. TABLE TITLE & TRIP COUNT ── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, px: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b" }}>
            {selectedTab === 0 ? "Active Escort Trips" : "Escort Trip History"}
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: "0.85rem", fontWeight: 600 }}>
            {selectedTab === 0
              ? `Showing ${filteredRows.length} active ${filteredRows.length === 1 ? "trip" : "trips"}`
              : filteredRows.length === 0
              ? "0 completed trips"
              : `Showing ${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredRows.length)} of ${filteredRows.length} completed trips`}
          </Typography>
        </Box>

        {/* ── 7. TABLE SECTION ── */}
        <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
              <Table
                sx={{
                  minWidth: 1620,
                  width: "100%",
                  tableLayout: "fixed",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <TableHead sx={{ display: "table-header-group !important", bgcolor: "#f8fafc" }}>
                  <TableRow sx={{ display: "table-row !important" }}>
                    <TableCell
                      sx={{
                        width: 220,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 2,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      User / Driver / Cargo
                    </TableCell>
                    <TableCell
                      sx={{
                        width: 250,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 2,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Escort Category & Details
                    </TableCell>
                    <TableCell
                      sx={{
                        width: 190,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 2,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Location & Destination
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: 125,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: 95,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 1,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Battery
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: 140,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Movement
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: 110,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Connection
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: 110,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Heartbeat
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: 165,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Wellbeing Response
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: selectedTab === 0 ? 80 : 140,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 1,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedTab === 0 ? "GSM" : "Date & Time"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: 105,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Evidence
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: 70,
                        fontWeight: 700,
                        color: "#475569",
                        fontSize: "0.75rem",
                        py: 1.5,
                        px: 1,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: "2px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ display: "table-row-group !important" }}>
                  {(loading && selectedTab === 0) || (historyLoading && selectedTab === 1) ? (
                    <TableRow sx={{ display: "table-row !important" }}>
                      <TableCell colSpan={12} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                        <CircularProgress size={32} sx={{ color: "#1565c0", mb: 1.5 }} />
                        <Typography sx={{ fontSize: "0.95rem", color: "#64748b", fontWeight: 600 }}>
                          {selectedTab === 0 ? "Loading real-time escort data..." : "Loading escort trip history..."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredRows.length === 0 ? (
                    <TableRow sx={{ display: "table-row !important" }}>
                      <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#64748b" }}>
                          {selectedTab === 0 ? "No active escorts found" : "No escort history found"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.82rem", color: "#94a3b8", mt: 0.5 }}>
                          {selectedTab === 0
                            ? "There are currently no active escort trips being tracked."
                            : "No completed escort trips found matching your filters."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row, idx) => {
                      const isCriticalRow = selectedTab === 0 && row.priority === "Critical";
                      const isCancelled = row.tripStatus === "canceled" || row.tripStatus === "cancelled" || row.activity === "Cancelled";
                      const isResolved = row.tripStatus === "resolved" || row.activity === "Resolved";
                      const rowBg = isCriticalRow ? "#fff5f5" : "#ffffff";
                      const movementInfo = getMovementInfo(row);

                      const tripKey = String(row.actualTripId || row.id || row.userId);
                      const resolvedStart =
                        resolvedStartPlaces[tripKey] ||
                        resolvedStartPlaces[row.userId] ||
                        resolvedStartPlaces[row.actualTripId] ||
                        resolvedStartPlaces[String(row.escortDocumentId || "")];

                      const userHomeAddr = cleanLocationName(row.userData?.address || row.address);
                      const rawPickupCandidate =
                        (row.cleanPickup && row.cleanPickup.toLowerCase() !== userHomeAddr?.toLowerCase())
                          ? row.cleanPickup
                          : "";
                      const rawOriginCandidate =
                        (row.originCity && row.originCity.toLowerCase() !== userHomeAddr?.toLowerCase())
                          ? row.originCity
                          : "";

                      const originCity =
                        resolvedStart ||
                        rawPickupCandidate ||
                        rawOriginCandidate ||
                        (row.isTripActive ? "Current Location" : "Start Location");
                      const destinationCity = row.destinationCity || "";

                      // Per-user device heartbeat & connection evaluation
                      const userTripKey = String(row.actualTripId || row.escortId || row.userId || "");
                      const userHb = heartbeatMap[userTripKey] || heartbeatMap[row.userId] || heartbeatMap[row.actualTripId] || heartbeatMap[row.escortId];
                      const userLastPing = userHb?.server_received_at || (userHb?.timestamp ? userHb.timestamp * 1000 : null) || (row.isTripActive ? (row.effectiveTripTimeMs || row.startedAtMs) : null);
                      const userElapsedSec = userLastPing ? Math.max(0, Math.floor((Date.now() - userLastPing) / 1000)) : null;
                      const isUserActivePing = row.isTripActive && userElapsedSec !== null && userElapsedSec < 90;
                      const isUserDelayedPing = row.isTripActive && userElapsedSec !== null && userElapsedSec >= 90 && userElapsedSec < 10 * 60;

                      return (
                        <TableRow
                          key={`${row.id}_${idx}`}
                          hover
                          sx={{
                            display: "table-row !important",
                            bgcolor: rowBg,
                            "&:hover": { bgcolor: isCriticalRow ? "#fee2e2" : "#f8fafc" },
                            transition: "background-color 0.1s ease",
                          }}
                        >
                          {/* 1. USER / DRIVER / CARGO */}
                          <TableCell sx={{ py: 1.8, px: 2, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 38,
                                  height: 38,
                                  bgcolor: "#f1f5f9",
                                  color: "#475569",
                                  border: "1px solid #e2e8f0",
                                  flexShrink: 0,
                                }}
                              >
                                <PersonIcon sx={{ fontSize: "1.3rem" }} />
                              </Avatar>
                              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <Typography
                                  onClick={() => openMonitoring(row)}
                                  sx={{
                                    fontWeight: 700,
                                    color: "#0f172a",
                                    fontSize: "0.88rem",
                                    cursor: "pointer",
                                    lineHeight: 1.25,
                                    "&:hover": { color: "#1d4ed8", textDecoration: "underline" },
                                  }}
                                >
                                  {row.name}
                                </Typography>
                                <Typography sx={{ color: "#64748b", fontSize: "0.78rem", mt: 0.3, lineHeight: 1.2 }}>
                                  {row.phone}
                                </Typography>
                                <Typography sx={{ color: "#64748b", fontSize: "0.75rem", mt: 0.2, fontWeight: 500, letterSpacing: "0.02em" }}>
                                  {row.escortId}
                                </Typography>
                                {(row.requestTimeMs || row.startedAtMs || row.effectiveTripTimeMs) ? (
                                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.35 }}>
                                    <AccessTimeOutlinedIcon sx={{ fontSize: "0.78rem", color: "#64748b" }} />
                                    <Typography sx={{ color: "#334155", fontSize: "0.72rem", fontWeight: 600 }}>
                                      {fmtDateTime(row.requestTimeMs || row.startedAtMs || row.effectiveTripTimeMs)}
                                    </Typography>
                                  </Box>
                                ) : null}
                                <Box sx={{ mt: 0.6 }}>
                                  <Chip
                                    label={
                                      row.escortCategory === "Virtual Travel Guard"
                                        ? "Traveller"
                                        : row.escortCategory === "Drivers Escort"
                                        ? "Driver"
                                        : row.escortCategory === "Cargo Escort"
                                        ? "Cargo"
                                        : "User"
                                    }
                                    size="small"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 20,
                                      borderRadius: "12px",
                                      bgcolor:
                                        row.escortCategory === "Virtual Travel Guard"
                                          ? "#f3e8ff"
                                          : row.escortCategory === "Drivers Escort"
                                          ? "#ffedd5"
                                          : row.escortCategory === "Cargo Escort"
                                          ? "#ecfdf5"
                                          : "#e0f2fe",
                                      color:
                                        row.escortCategory === "Virtual Travel Guard"
                                          ? "#7e22ce"
                                          : row.escortCategory === "Drivers Escort"
                                          ? "#c2410c"
                                          : row.escortCategory === "Cargo Escort"
                                          ? "#047857"
                                          : "#0284c7",
                                      border: "none",
                                    }}
                                  />
                                  {/* Individual User Connection Status */}
                                  <Chip
                                    icon={
                                      <Box
                                        sx={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: "50%",
                                          bgcolor: isUserActivePing ? "#16a34a" : isUserDelayedPing ? "#d97706" : "#94a3b8",
                                          ml: 0.6,
                                        }}
                                      />
                                    }
                                    label={
                                      !row.isTripActive
                                        ? "Offline"
                                        : isUserActivePing
                                        ? "Connected"
                                        : isUserDelayedPing
                                        ? "Delayed"
                                        : "Offline"
                                    }
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "0.68rem",
                                      height: 20,
                                      borderRadius: "12px",
                                      bgcolor: !row.isTripActive
                                        ? "#f1f5f9"
                                        : isUserActivePing
                                        ? "#f0fdf4"
                                        : isUserDelayedPing
                                        ? "#fefce8"
                                        : "#fef2f2",
                                      color: !row.isTripActive
                                        ? "#64748b"
                                        : isUserActivePing
                                        ? "#15803d"
                                        : isUserDelayedPing
                                        ? "#a16207"
                                        : "#b91c1c",
                                      border: `1px solid ${
                                        !row.isTripActive
                                          ? "#e2e8f0"
                                          : isUserActivePing
                                          ? "#bbf7d0"
                                          : isUserDelayedPing
                                          ? "#fef08a"
                                          : "#fca5a5"
                                      }`,
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* 2. ESCORT CATEGORY & DETAILS */}
                          <TableCell sx={{ py: 1.8, px: 2, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.88rem",
                                color: "#1d4ed8",
                                lineHeight: 1.25,
                              }}
                            >
                              {row.escortCategory}
                            </Typography>

                            {/* Badges */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap", mt: 0.6, mb: 0.6 }}>
                              {row.escortCategory === "User Escort" && (
                                <>
                                  <Chip
                                    label={
                                      row.requestType === "Long Distance" || row.tripData?.escortType === "Long Escort"
                                        ? "Long Escort"
                                        : "Short Escort"
                                    }
                                    size="small"
                                    sx={{
                                      bgcolor: "#e0f2fe",
                                      color: "#0284c7",
                                      borderRadius: "12px",
                                      height: 22,
                                      fontSize: "0.72rem",
                                      fontWeight: 600,
                                      border: "none",
                                    }}
                                  />
                                  <Chip
                                    icon={
                                      row.movementMode === "Walking" ? (
                                        <DirectionsWalkOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#475569 !important" }} />
                                      ) : (
                                        <DirectionsCarOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#475569 !important" }} />
                                      )
                                    }
                                    label={row.movementMode === "Walking" ? "Walking" : "Vehicle"}
                                    size="small"
                                    sx={{
                                      bgcolor: "#f1f5f9",
                                      color: "#334155",
                                      borderRadius: "12px",
                                      height: 22,
                                      fontSize: "0.72rem",
                                      fontWeight: 600,
                                      border: "none",
                                    }}
                                  />
                                </>
                              )}

                              {row.escortCategory === "Virtual Travel Guard" && (() => {
                                const vtgMode = getVTGTravelMode(row.tripData, row.userData);
                                let icon = <DirectionsBusOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#0284c7 !important" }} />;
                                let label = vtgMode;

                                if (vtgMode === "Train") {
                                  icon = <TrainOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#7c3aed !important" }} />;
                                  label = "Train";
                                } else if (vtgMode === "Own Transport") {
                                  icon = <DirectionsCarOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#0284c7 !important" }} />;
                                  label = "Own Transport";
                                } else {
                                  label = row.tripData?.TransportType || row.tripData?.TransportCompany || "Public Transport";
                                }

                                return (
                                  <>
                                    <Chip
                                      icon={icon}
                                      label={label}
                                      size="small"
                                      sx={{
                                        bgcolor: vtgMode === "Train" ? "#f5f3ff" : "#e0f2fe",
                                        color: vtgMode === "Train" ? "#7c3aed" : "#0284c7",
                                        borderRadius: "12px",
                                        height: 22,
                                        fontSize: "0.72rem",
                                        fontWeight: 600,
                                        border: "none",
                                      }}
                                    />
                                    {vtgMode === "Train" && (row.tripData?.DepartStation || row.tripData?.ArrivalStation) && (
                                      <Chip
                                        label={`${row.tripData?.DepartStation || "Depart"} → ${row.tripData?.ArrivalStation || "Arrival"}`}
                                        size="small"
                                        sx={{
                                          bgcolor: "#ede9fe",
                                          color: "#6d28d9",
                                          borderRadius: "12px",
                                          height: 22,
                                          fontSize: "0.72rem",
                                          fontWeight: 600,
                                          border: "none",
                                        }}
                                      />
                                    )}
                                    {vtgMode === "Own Transport" && (row.tripData?.VehicleNumber || row.tripData?.vehicleReg) && (
                                      <Chip
                                        label={row.tripData?.VehicleNumber || row.tripData?.vehicleReg}
                                        size="small"
                                        sx={{
                                          bgcolor: "#f1f5f9",
                                          color: "#475569",
                                          borderRadius: "12px",
                                          height: 22,
                                          fontSize: "0.72rem",
                                          fontWeight: 600,
                                          border: "none",
                                        }}
                                      />
                                    )}
                                    {vtgMode === "Public Transport" && (row.tripData?.TransportCompany || row.tripData?.VehicleReg) && (
                                      <Chip
                                        label={row.tripData?.TransportCompany || row.tripData?.VehicleReg}
                                        size="small"
                                        sx={{
                                          bgcolor: "#f1f5f9",
                                          color: "#475569",
                                          borderRadius: "12px",
                                          height: 22,
                                          fontSize: "0.72rem",
                                          fontWeight: 600,
                                          border: "none",
                                        }}
                                      />
                                    )}
                                  </>
                                );
                              })()}

                              {row.escortCategory === "Drivers Escort" && (
                                <>
                                  <Chip
                                    icon={<DirectionsCarOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#0369a1 !important" }} />}
                                    label="Driver Escort"
                                    size="small"
                                    sx={{
                                      bgcolor: "#e0f2fe",
                                      color: "#0369a1",
                                      borderRadius: "12px",
                                      height: 22,
                                      fontSize: "0.72rem",
                                      fontWeight: 600,
                                      border: "none",
                                    }}
                                  />
                                  {(row.tripData?.VehicleNumber || row.tripData?.vehicleReg) && (
                                    <Chip
                                      label={row.tripData?.VehicleNumber || row.tripData?.vehicleReg}
                                      size="small"
                                      sx={{
                                        bgcolor: "#f1f5f9",
                                        color: "#334155",
                                        borderRadius: "12px",
                                        height: 22,
                                        fontSize: "0.72rem",
                                        fontWeight: 600,
                                      }}
                                    />
                                  )}
                                </>
                              )}

                              {row.escortCategory === "Cargo Escort" && (
                                <Chip
                                  icon={<LocalShippingOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#92400e !important" }} />}
                                  label={row.tripData?.cargoType || "Cargo Escort"}
                                  size="small"
                                  sx={{
                                    bgcolor: "#fef3c7",
                                    color: "#92400e",
                                    borderRadius: "12px",
                                    height: 22,
                                    fontSize: "0.72rem",
                                    fontWeight: 600,
                                    border: "none",
                                  }}
                                />
                              )}
                            </Box>

                            {/* Location Route */}
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.6, mt: 0.6 }}>
                              <PlaceIcon sx={{ fontSize: "0.95rem", color: "#16a34a", mt: 0.1, flexShrink: 0 }} />
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography sx={{ fontSize: "0.75rem", color: "#1e293b", fontWeight: 600, lineHeight: 1.3 }}>
                                  {originCity}
                                </Typography>
                                {destinationCity && (
                                  <Typography sx={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 500, lineHeight: 1.3, mt: 0.2 }}>
                                    → {destinationCity}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>

                          {/* 3. LOCATION & DESTINATION */}
                          <TableCell sx={{ py: 1.8, px: 2, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                              {/* Starting Location */}
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.8 }}>
                                <Box
                                  sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    bgcolor: "#16a34a",
                                    mt: 0.55,
                                    flexShrink: 0,
                                    boxShadow: "0 0 0 3px rgba(22, 163, 74, 0.15)",
                                  }}
                                />
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                  <Typography sx={{ fontSize: "0.68rem", color: "#16a34a", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.1 }}>
                                    Start Location
                                  </Typography>
                                  <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: "#0f172a", lineHeight: 1.3, mt: 0.2 }}>
                                    {originCity}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Destination */}
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.8 }}>
                                <Box
                                  sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    bgcolor: destinationCity ? "#dc2626" : "#94a3b8",
                                    mt: 0.55,
                                    flexShrink: 0,
                                    boxShadow: destinationCity ? "0 0 0 3px rgba(220, 38, 38, 0.15)" : "none",
                                  }}
                                />
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                  <Typography sx={{ fontSize: "0.68rem", color: destinationCity ? "#dc2626" : "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.1 }}>
                                    Destination
                                  </Typography>
                                  <Typography sx={{ color: destinationCity ? "#334155" : "#64748b", fontSize: "0.80rem", fontWeight: 500, lineHeight: 1.3, mt: 0.2 }}>
                                    {destinationCity || (selectedTab === 0 ? "En Route / Local Escort" : "Open Escort / Local")}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* 4. STATUS */}
                          <TableCell align="center" sx={{ py: 1.8, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            {selectedTab === 0 ? (
                              row.isMoving && movementInfo.speedKmH > 0 ? (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    bgcolor:
                                      row.escortCategory === "Virtual Travel Guard" || row.escortCategory === "Cargo Escort"
                                        ? "#eff6ff"
                                        : "#ecfdf5",
                                    color:
                                      row.escortCategory === "Virtual Travel Guard" || row.escortCategory === "Cargo Escort"
                                        ? "#0284c7"
                                        : "#16a34a",
                                    border: `1px solid ${
                                      row.escortCategory === "Virtual Travel Guard" || row.escortCategory === "Cargo Escort"
                                        ? "#bae6fd"
                                        : "#bbf7d0"
                                    }`,
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    px: 1.3,
                                    py: 0.5,
                                    borderRadius: "6px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <NorthEastIcon sx={{ fontSize: "0.85rem !important" }} />
                                  <span>
                                    {row.escortCategory === "Virtual Travel Guard" || row.escortCategory === "Cargo Escort"
                                      ? "In Transit"
                                      : "Moving"}
                                  </span>
                                </Box>
                              ) : row.activity === "SOS Triggered" ? (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    bgcolor: "#fee2e2",
                                    color: "#dc2626",
                                    border: "1px solid #fca5a5",
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    px: 1.3,
                                    py: 0.5,
                                    borderRadius: "6px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <span>⚠</span>
                                  <span>SOS Alert</span>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.2,
                                    bgcolor: "#fee2e2",
                                    color: "#b91c1c",
                                    border: "1px solid #fca5a5",
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    px: 1.3,
                                    py: 0.5,
                                    borderRadius: "6px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
                                    <span>⏸</span>
                                    <span>Stopped</span>
                                  </Box>
                                  {row.stoppedSinceMs && (
                                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35, mt: 0.1 }}>
                                      <AccessTimeOutlinedIcon sx={{ fontSize: "0.82rem !important", color: "#dc2626" }} />
                                      <LiveTimer startMs={row.stoppedSinceMs} color="#b91c1c" fontSize="0.75rem" />
                                    </Box>
                                  )}
                                </Box>
                              )
                            ) : (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  bgcolor: isCancelled ? "#f1f5f9" : "#dcfce7",
                                  color: isCancelled ? "#64748b" : "#15803d",
                                  border: `1px solid ${isCancelled ? "#cbd5e1" : "#86efac"}`,
                                  fontWeight: 700,
                                  fontSize: "0.78rem",
                                  px: 1.2,
                                  py: 0.45,
                                  borderRadius: "6px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <span>{isCancelled ? "✕" : "✓"}</span>
                                <span>{isResolved ? "Resolved" : isCancelled ? "Cancelled" : "Completed"}</span>
                              </Box>
                            )}
                          </TableCell>

                          {/* 5. BATTERY */}
                          <TableCell align="center" sx={{ py: 1.8, px: 1, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, justifyContent: "center" }}>
                              {row.battery === null || row.battery === undefined ? (
                                <Typography sx={{ color: "#94a3b8", fontSize: "0.86rem", fontWeight: 600 }}>
                                  —
                                </Typography>
                              ) : (
                                <>
                                  {row.isCharging ? (
                                    <Tooltip title="Mobile Charging">
                                      <BatteryChargingFullIcon sx={{ color: "#16a34a", fontSize: "1.15rem" }} />
                                    </Tooltip>
                                  ) : row.battery <= 15 ? (
                                    <BatteryAlertOutlinedIcon sx={{ color: "#dc2626", fontSize: "1.15rem" }} />
                                  ) : (
                                    <BatteryFullOutlinedIcon
                                      sx={{ color: row.battery <= 35 ? "#d97706" : "#16a34a", fontSize: "1.15rem" }}
                                    />
                                  )}
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "0.86rem",
                                      color: row.isCharging ? "#16a34a" : row.battery <= 15 ? "#dc2626" : row.battery <= 35 ? "#d97706" : "#16a34a",
                                    }}
                                  >
                                    {row.battery}%
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </TableCell>

                          {/* 6. MOVEMENT */}
                          <TableCell align="center" sx={{ py: 1.8, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.9, textAlign: "left" }}>
                              {movementInfo.modeIcon}
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: "#1e293b", lineHeight: 1.2 }}>
                                  {movementInfo.modeLabel}
                                </Typography>
                                <Typography sx={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 500, mt: 0.2, lineHeight: 1.2 }}>
                                  ({movementInfo.speedKmH} km/h)
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* 6B. INDIVIDUAL USER CONNECTION */}
                          <TableCell align="center" sx={{ py: 1.8, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            <ConnectionCell row={row} heartbeatMap={heartbeatMap} />
                          </TableCell>

                          {/* 6C. INDIVIDUAL USER HEARTBEAT (60s Telemetry Cycle) */}
                          <TableCell align="center" sx={{ py: 1.8, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            <HeartbeatCell row={row} heartbeatMap={heartbeatMap} />
                          </TableCell>

                          {/* 7. WELLBEING RESPONSE */}
                          <TableCell align="center" sx={{ py: 1.8, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            <WellbeingResponseCell row={row} />
                          </TableCell>

                          {/* 8. GSM (Active) / Date & Time (History) */}
                          <TableCell align="center" sx={{ py: 1.8, px: 1, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            {selectedTab === 0 ? (
                              row.isGsmMode ? (
                                <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                  <CheckIcon sx={{ color: "#16a34a", fontSize: "1.2rem", fontWeight: 800 }} />
                                  <Typography sx={{ color: "#64748b", fontSize: "0.72rem", fontWeight: 600, mt: 0.1 }}>
                                    (On)
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography sx={{ color: "#9ca3af", fontSize: "0.95rem", fontWeight: 500 }}>
                                  —
                                </Typography>
                              )
                            ) : (
                              <Box sx={{ display: "inline-block", textAlign: "center" }}>
                                <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.25 }}>
                                  {fmtDateTime(row.effectiveTripTimeMs || row.endedAtMs || row.startedAtMs)}
                                </Typography>
                                {row.tripData?.duration ? (
                                  <Typography sx={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600, mt: 0.3 }}>
                                    Duration: {row.tripData.duration}
                                  </Typography>
                                ) : row.startedAtMs && row.endedAtMs && row.endedAtMs > row.startedAtMs ? (
                                  <Typography sx={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600, mt: 0.3 }}>
                                    Duration: {fmtDuration(row.endedAtMs - row.startedAtMs)}
                                  </Typography>
                                ) : row.startedAtMs && (row.effectiveTripTimeMs !== row.startedAtMs) ? (
                                  <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", mt: 0.2 }}>
                                    Started: {fmtDateTime(row.startedAtMs)}
                                  </Typography>
                                ) : null}
                              </Box>
                            )}
                          </TableCell>

                          {/* 9. EVIDENCE */}
                          <TableCell align="center" sx={{ py: 1.8, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                            <Box
                              onClick={() => {
                                if (row.evidence) {
                                  setSelectedEvidenceRow(row);
                                  setEvidenceFileUrl(row.evidenceUrl || "");
                                  setEvidenceTab(row.videos?.length > 0 ? "submissions" : "vehicle");
                                  setImageRotation(0);
                                  setEvidenceModalOpen(true);
                                }
                              }}
                              sx={{
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                py: 0.6,
                                px: 1.2,
                                minWidth: 68,
                                cursor: row.evidence ? "pointer" : "default",
                                transition: "all 0.15s ease",
                                "&:hover": row.evidence ? { bgcolor: "#f8fafc", borderColor: "#cbd5e1" } : {},
                              }}
                            >
                              <DescriptionOutlinedIcon sx={{ color: "#475569", fontSize: "1.15rem", mb: 0.2 }} />
                              <Typography
                                sx={{
                                  color: row.evidence ? "#1e293b" : "#64748b",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.evidence ? (row.videos?.length > 0 ? `Files (${row.videos.length})` : "Has File") : "No File"}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* 10. ACTIONS */}
                          <TableCell align="center" sx={{ py: 1.8, px: 1, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                            {selectedTab === 0 && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  console.log("[Close Trip Button Clicked]", { row });
                                  handleCloseTrip(row);
                                }}
                                sx={{
                                  bgcolor: "#16a34a !important",
                                  color: "#ffffff !important",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  fontSize: "0.78rem",
                                  borderRadius: "8px",
                                  py: 0.4,
                                  px: 1.2,
                                  mr: 0.8,
                                  boxShadow: "0 2px 6px rgba(22, 163, 74, 0.35)",
                                  "&:hover": { bgcolor: "#15803d !important" },
                                }}
                              >
                                Close Trip
                              </Button>
                            )}

                            <IconButton
                              onClick={(e) => handleMenuOpen(e, row)}
                              size="small"
                              sx={{
                                color: "#334155",
                                p: 0.6,
                                "&:hover": { bgcolor: "#f1f5f9" },
                              }}
                            >
                              <MoreVertOutlinedIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination controls for Escort History */}
            {selectedTab === 1 && filteredRows.length > 0 && (
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: "1px solid #e2e8f0",
                  bgcolor: "#fff",
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "#64748b",
                  },
                }}
              />
            )}
          </Box>
      </MDBox>

      {/* ── ACTION ⋮ MENU ── */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ sx: { px: 1, py: 0.5, borderRadius: 2, minWidth: 240, boxShadow: "0 4px 16px rgba(0,0,0,0.12)" } }}
      >
        <MenuItem onClick={() => handleAction("monitoring")} sx={{ fontWeight: 700, color: "#1565c0", fontSize: "0.88rem" }}>
          <WarningAmberRoundedIcon fontSize="small" sx={{ mr: 1.2 }} /> Open Monitoring Screen
        </MenuItem>
        {selectedRow?.isTripActive && (
          <MenuItem onClick={() => handleAction("close-trip")} sx={{ fontWeight: 700, color: "#16a34a", fontSize: "0.88rem" }}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1.2, color: "#16a34a" }} /> Close & Complete Trip
          </MenuItem>
        )}
        {selectedRow?.isTripActive && (
          <MenuItem onClick={() => handleAction("check-arrival")} sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
            <CheckCircleOutlineRoundedIcon fontSize="small" sx={{ mr: 1.2 }} /> Send Manual Wellbeing Check
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction("call-user")} sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
          <PhoneOutlinedIcon fontSize="small" sx={{ mr: 1.2 }} /> Call User
        </MenuItem>
        <MenuItem onClick={() => handleAction("view-map")} sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
          <MapOutlinedIcon fontSize="small" sx={{ mr: 1.2 }} /> View Live Map
        </MenuItem>
        <MenuItem onClick={() => handleAction("open-evidence")} sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
          <DirectionsCarOutlinedIcon fontSize="small" sx={{ mr: 1.2 }} /> Open Evidence / Plate
        </MenuItem>
        <MenuItem onClick={() => handleAction("push")} sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
          <NotificationsNoneOutlinedIcon fontSize="small" sx={{ mr: 1.2 }} /> Send Push Notification
        </MenuItem>
        <MenuItem onClick={() => handleAction("view-notifications")} sx={{ fontWeight: 600, fontSize: "0.88rem", color: "#0369a1" }}>
          <NotificationsActiveIcon fontSize="small" sx={{ mr: 1.2 }} /> Notification & Response History
        </MenuItem>
        {selectedRow?.isTripActive && (
          <MenuItem
            onClick={() => handleAction("notify-emergency")}
            sx={{
              fontWeight: 700,
              color: "#dc2626",
              bgcolor: "rgba(220,38,38,0.06)",
              borderRadius: 1,
              my: 0.5,
              fontSize: "0.88rem",
            }}
          >
            <PeopleAltIcon fontSize="small" sx={{ mr: 1.2 }} /> Notify Emergency Contact
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction("dispatch-security")} sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
          <SecurityOutlinedIcon fontSize="small" sx={{ mr: 1.2 }} /> Dispatch to Service Agent
        </MenuItem>
        <MenuItem onClick={() => handleAction("send-email")} sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
          <EmailOutlinedIcon fontSize="small" sx={{ mr: 1.2 }} /> Send E-Mail
        </MenuItem>
        <MenuItem onClick={() => handleAction("lets-chat")} sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
          <ChatOutlinedIcon fontSize="small" sx={{ mr: 1.2 }} /> Let's Chat
        </MenuItem>
        {selectedRow?.isTripActive && (
          <MenuItem onClick={() => handleAction("resolve")} sx={{ fontWeight: 700, color: "#16a34a", fontSize: "0.88rem" }}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1.2 }} /> Mark as Resolved
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => handleAction("delete")} sx={{ fontWeight: 700, color: "#dc2626", fontSize: "0.88rem" }}>
          <CloseRoundedIcon fontSize="small" sx={{ mr: 1.2 }} /> Delete Escort
        </MenuItem>
      </Menu>

      {/* ── 8. ESCORT MONITORING SCREEN MODAL (Section 14) ── */}
      <Dialog
        open={monitoringOpen}
        onClose={() => setMonitoringOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 3,
            bgcolor: "#f8fafc",
            maxHeight: "92vh",
          },
        }}
      >
        {selectedRow && (
          <Box>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
              <Box>
                <Typography sx={{ color: "#1565c0", fontSize: "1.45rem", fontWeight: 800 }}>
                  Escort Live Monitoring
                </Typography>
                <Typography sx={{ color: "#6b7280", fontSize: "0.85rem" }}>
                  Escort ID: {selectedRow.escortId} · User ID: {selectedRow.userId}
                </Typography>
              </Box>
              <IconButton onClick={() => setMonitoringOpen(false)} sx={{ bgcolor: "#e2e8f0" }}>
                <CloseRoundedIcon />
              </IconButton>
            </Box>

            {/* 4 Top Tiles */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              {/* Tile 1: Movement */}
              <Grid item xs={6} md={3}>
                <Box sx={{ bgcolor: "#fff", p: 2, borderRadius: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase" }}>
                    Movement
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.2rem",
                      color: !selectedRow.isTripActive ? "#64748b" : selectedRow.isMoving ? "#16a34a" : "#dc2626",
                      mt: 0.5,
                    }}
                  >
                    {!selectedRow.isTripActive ? (selectedRow.tripStatus === "resolved" ? "Resolved" : "Completed") : selectedRow.movement}
                  </Typography>
                  {!selectedRow.isMoving && selectedRow.stoppedSinceMs && selectedRow.isTripActive && (
                    <Box sx={{ mt: 0.3 }}>
                      <LiveTimer startMs={selectedRow.stoppedSinceMs} color="#dc2626" fontSize="0.88rem" />
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Tile 2: Battery */}
              <Grid item xs={6} md={3}>
                <Box sx={{ bgcolor: "#fff", p: 2, borderRadius: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase" }}>
                    Battery {selectedRow.isCharging ? "(Charging)" : ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mt: 0.5 }}>
                    {selectedRow.isCharging && <FlashOnIcon sx={{ color: "#16a34a", fontSize: "1.2rem" }} />}
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "1.2rem",
                        color:
                          selectedRow.battery === null || selectedRow.battery === undefined
                            ? "#64748b"
                            : selectedRow.isCharging
                            ? "#16a34a"
                            : selectedRow.battery <= 15
                            ? "#dc2626"
                            : "#1f2937",
                      }}
                    >
                      {selectedRow.battery === null || selectedRow.battery === undefined ? "—" : `${selectedRow.battery}%`}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color:
                        selectedRow.battery === null || selectedRow.battery === undefined
                          ? "#94a3b8"
                          : selectedRow.isCharging
                          ? "#16a34a"
                          : selectedRow.battery <= 15
                          ? "#dc2626"
                          : "#16a34a",
                      mt: 0.3,
                    }}
                  >
                    {selectedRow.battery === null || selectedRow.battery === undefined
                      ? "No Snapshot"
                      : selectedRow.isCharging
                      ? "Mobile Charging"
                      : selectedRow.battery <= 15
                      ? "Low Battery Alert"
                      : "Healthy Battery"}
                  </Typography>
                </Box>
              </Grid>

              {/* Tile 3: Phone */}
              <Grid item xs={6} md={3}>
                <Box sx={{ bgcolor: "#fff", p: 2, borderRadius: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase" }}>
                    Phone Status
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.2rem",
                      color: selectedRow.activity.includes("Phone Switched Off") ? "#dc2626" : "#16a34a",
                      mt: 0.5,
                    }}
                  >
                    {selectedRow.activity.includes("Phone Switched Off") ? "Offline" : "Online"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#6b7280", mt: 0.3 }}>
                    {selectedRow.activity.includes("Lost on Radar") ? "Lost on Radar" : "Active Radar"}
                  </Typography>
                </Box>
              </Grid>

              {/* Tile 4: Started Timer */}
              <Grid item xs={6} md={3}>
                <Box sx={{ bgcolor: "#fff", p: 2, borderRadius: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase" }}>
                    Escort Started
                  </Typography>
                  <Box sx={{ mt: 0.8 }}>
                    <LiveTimer startMs={selectedRow.startedAtMs} color="#1565c0" fontSize="1.2rem" />
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "#6b7280", mt: 0.3 }}>
                    {new Date(selectedRow.startedAtMs).toLocaleTimeString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Wellbeing Check Panel with Timer Bars */}
            <Box sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0", mb: 2.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#1f2937", mb: 1.5 }}>
                Wellbeing Check Engine
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Status</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: selectedRow.wellbeing === "No Response" ? "#dc2626" : "#1f2937" }}>
                    {selectedRow.wellbeing}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Source Label</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    {selectedRow.wellbeingSource || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Push Status</Typography>
                  <Chip
                    label={selectedRow.pushStatus || "Pending"}
                    size="small"
                    color={selectedRow.pushStatus === "Delivered" ? "success" : "warning"}
                    sx={{ fontWeight: 700, mt: 0.3 }}
                  />
                </Grid>
              </Grid>

              {/* Progress Timeline Bars */}
              <Box sx={{ mt: 2.5 }}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6b7280", mb: 0.5 }}>
                  Response Timer Thresholds: Needs Attention (2m) · Critical (5m)
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={selectedRow.wellbeing === "No Response" ? 100 : selectedRow.wellbeing === "Awaiting Response" ? 60 : 10}
                  color={selectedRow.wellbeing === "No Response" ? "error" : "warning"}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Box>

            {/* Details Grid */}
            <Box sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0", mb: 2.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#1f2937", mb: 1.5 }}>
                Escort & Driver Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>User Name</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{selectedRow.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Phone Number</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{selectedRow.phone}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>State / Area</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{selectedRow.state}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Escort Type</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{selectedRow.type}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Request Type</Typography>
                  <Chip
                    label={selectedRow.requestType}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: selectedRow.requestType === "Long Distance" ? "#e0e7ff" : "#f1f5f9",
                      color: selectedRow.requestType === "Long Distance" ? "#3730a3" : "#334155",
                      border: `1px solid ${selectedRow.requestType === "Long Distance" ? "#c7d2fe" : "#e2e8f0"}`,
                      mt: 0.3,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>
                    {selectedRow.escortCategory === "Virtual Travel Guard" ? "Mode of Transport" : "Movement Mode"}
                  </Typography>
                  {selectedRow.escortCategory === "Virtual Travel Guard" ? (() => {
                    const vtgMode = getVTGTravelMode(selectedRow.tripData, selectedRow.userData);
                    let icon = <DirectionsBusOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#0369a1 !important" }} />;
                    let bg = "#e0f2fe";
                    let col = "#0369a1";
                    let bdr = "#bae6fd";
                    let label = vtgMode;

                    if (vtgMode === "Train") {
                      icon = <TrainOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#6d28d9 !important" }} />;
                      bg = "#f5f3ff";
                      col = "#6d28d9";
                      bdr = "#ddd6fe";
                      label = "Train";
                    } else if (vtgMode === "Own Transport") {
                      icon = <DirectionsCarOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#0369a1 !important" }} />;
                      label = "Own Transport";
                    } else {
                      label = selectedRow.tripData?.TransportType || selectedRow.tripData?.TransportCompany || "Public Transport";
                    }

                    return (
                      <Chip
                        icon={icon}
                        label={label}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: bg,
                          color: col,
                          border: `1px solid ${bdr}`,
                          mt: 0.3,
                        }}
                      />
                    );
                  })() : (
                    <Chip
                      icon={
                        selectedRow.movementMode === "Walking" && selectedRow.requestType !== "Driver Escort" && selectedRow.type !== "Driver Escort" ? (
                          <DirectionsWalkOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#b45309 !important" }} />
                        ) : (
                          <DirectionsCarOutlinedIcon sx={{ fontSize: "0.95rem !important", color: "#0369a1 !important" }} />
                        )
                      }
                      label={
                        (selectedRow.requestType === "Driver Escort" || selectedRow.type === "Driver Escort") && selectedRow.movementMode === "Walking"
                          ? "Vehicle"
                          : selectedRow.movementMode
                      }
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor:
                          selectedRow.movementMode === "Walking" && selectedRow.requestType !== "Driver Escort" && selectedRow.type !== "Driver Escort"
                            ? "#fef3c7"
                            : "#e0f2fe",
                        color:
                          selectedRow.movementMode === "Walking" && selectedRow.requestType !== "Driver Escort" && selectedRow.type !== "Driver Escort"
                            ? "#92400e"
                            : "#0369a1",
                        border: `1px solid ${
                          selectedRow.movementMode === "Walking" && selectedRow.requestType !== "Driver Escort" && selectedRow.type !== "Driver Escort"
                            ? "#fde68a"
                            : "#bae6fd"
                        }`,
                        mt: 0.3,
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>GSM Mode</Typography>
                  <Chip
                    label={selectedRow.isGsmMode ? "GSM Mode Used" : "Standard Mode"}
                    size="small"
                    color={selectedRow.isGsmMode ? "success" : "default"}
                    sx={{ fontWeight: 600, mt: 0.3 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Priority Status</Typography>
                  <Chip
                    label={selectedRow.priority}
                    size="small"
                    color={selectedRow.priority === "Critical" ? "error" : selectedRow.priority === "Needs Attention" ? "warning" : "success"}
                    sx={{ fontWeight: 700, mt: 0.3 }}
                  />
                </Grid>
                {(selectedRow.tripData?.VehicleNumber || selectedRow.tripData?.vehicleReg) && (
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Vehicle Number / Plate</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#0f172a" }}>
                      {selectedRow.tripData?.VehicleNumber || selectedRow.tripData?.vehicleReg}
                    </Typography>
                  </Grid>
                )}
                {(selectedRow.tripData?.ArrivalDate || selectedRow.tripData?.ArrivalTime) && (
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Est. Arrival</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#334155" }}>
                      {[selectedRow.tripData?.ArrivalDate, selectedRow.tripData?.ArrivalTime].filter(Boolean).join(" · ")}
                    </Typography>
                  </Grid>
                )}
                {selectedRow.escortCategory === "Virtual Travel Guard" && (
                  <>
                    {(selectedRow.tripData?.DepartStation || selectedRow.tripData?.ArrivalStation) && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Train Departure Station</Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#6d28d9" }}>
                            {selectedRow.tripData?.DepartStation || "---"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Train Arrival Station</Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#6d28d9" }}>
                            {selectedRow.tripData?.ArrivalStation || "---"}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {selectedRow.tripData?.TransportCompany && (
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Transport Company</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#0f172a" }}>
                          {selectedRow.tripData?.TransportCompany}
                        </Typography>
                      </Grid>
                    )}
                    {selectedRow.tripData?.TransportType && (
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Transport Vehicle Type</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#0f172a" }}>
                          {selectedRow.tripData?.TransportType}
                        </Typography>
                      </Grid>
                    )}
                    {(selectedRow.tripData?.DepartureTime || selectedRow.tripData?.DepartTime) && (
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>Est. Departure Time</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#334155" }}>
                          {selectedRow.tripData?.DepartureTime || selectedRow.tripData?.DepartTime}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
                {(selectedRow.tripData?.currentLocTxt || selectedRow.tripData?.destLocTxt) && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                      <Typography sx={{ color: "#64748b", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase" }}>
                        Trip Route & Destination
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mt: 0.8 }}>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#16a34a", minWidth: 55 }}>
                            FROM:
                          </Typography>
                          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1e293b" }}>
                            {selectedRow.tripData?.currentLocTxt || "---"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#1565c0", minWidth: 55 }}>
                            TO:
                          </Typography>
                          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1565c0" }}>
                            {selectedRow.tripData?.destLocTxt || "---"}
                          </Typography>
                        </Box>
                        {selectedRow.tripData?.DepartStation && (
                          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#7c3aed", minWidth: 55 }}>
                              TRAIN:
                            </Typography>
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#6d28d9" }}>
                              {selectedRow.tripData.DepartStation} → {selectedRow.tripData.ArrivalStation || "Destination"}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                )}
                {selectedRow.userFBK && (
                  <Grid item xs={12}>
                    <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>User Response / Feedback</Typography>
                    <Box
                      sx={{
                        p: 1.2,
                        borderRadius: "8px",
                        bgcolor: selectedRow.wellbeing === "Danger" ? "#fee2e2" : "#dcfce7",
                        color: selectedRow.wellbeing === "Danger" ? "#b91c1c" : "#15803d",
                        border: `1px solid ${selectedRow.wellbeing === "Danger" ? "#f87171" : "#86efac"}`,
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        mt: 0.3,
                      }}
                    >
                      "{selectedRow.userFBK}"
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Push Notifications & Responses History in Monitoring Modal */}
            <Box sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0", mb: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <NotificationsActiveIcon sx={{ color: "#1565c0", fontSize: "1.2rem" }} />
                  <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#1f2937" }}>
                    Push Notifications & Responses History ({notificationLogs.length})
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => openNotificationHistory(selectedRow)}
                  startIcon={<HistoryIcon fontSize="small" />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    borderColor: "#cbd5e1",
                    color: "#0369a1",
                  }}
                >
                  View Full Logs
                </Button>
              </Box>

              {loadingNotifications ? (
                <Box sx={{ py: 2, textAlign: "center" }}>
                  <LinearProgress sx={{ maxWidth: 150, mx: "auto", mb: 1 }} />
                  <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>Loading history...</Typography>
                </Box>
              ) : notificationLogs.length === 0 ? (
                <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", textAlign: "center", py: 1.5 }}>
                  No push notifications sent yet for this escort trip.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
                  {notificationLogs.slice(0, 5).map((log, i) => {
                    const isDanger = log.responseStatus === "Danger" || String(log.userResponse).toLowerCase().includes("danger");
                    const isAwaiting = log.status === "AWAITING_RESPONSE" || log.status === "SENT";
                    return (
                      <Box
                        key={log._id || log.notificationId || i}
                        sx={{
                          p: 1.2,
                          borderRadius: "8px",
                          bgcolor: "#f8fafc",
                          border: `1px solid ${isDanger ? "#fecaca" : "#e2e8f0"}`,
                          borderLeft: `4px solid ${isDanger ? "#dc2626" : isAwaiting ? "#f59e0b" : "#16a34a"}`,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 200 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1e293b" }}>
                              {log.title || "Wellbeing Check"}
                            </Typography>
                            <Chip
                              label={isDanger ? "🚨 Danger" : isAwaiting ? "⏳ Awaiting" : "✓ Responded"}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                bgcolor: isDanger ? "#fee2e2" : isAwaiting ? "#fef3c7" : "#dcfce7",
                                color: isDanger ? "#b91c1c" : isAwaiting ? "#b45309" : "#15803d",
                              }}
                            />
                          </Box>
                          <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                            "{log.message}" · {fmtDateTime(log.sentAt)}
                          </Typography>
                        </Box>

                        <Box sx={{ textAlign: "right" }}>
                          {log.userResponse ? (
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: isDanger ? "#dc2626" : "#16a34a" }}>
                              User: "{log.userResponse}" {log.responseTimeSeconds !== null && log.responseTimeSeconds !== undefined ? `(${log.responseTimeSeconds}s)` : ""}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: "0.75rem", color: "#d97706", fontWeight: 600 }}>
                              Awaiting reply...
                            </Typography>
                          )}
                          {log.respondedAt && (
                            <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                              {fmtDateTime(log.respondedAt)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

              {/* Admin Resolution Note & Actions */}
              <Box sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#1f2937", mb: 1 }}>
                  Resolution & Actions
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Type resolution note before marking resolved..."
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  sx={{
                    mb: 2.5,
                    bgcolor: "#f8fafc",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "0.88rem",
                    },
                  }}
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                    gap: 1.5,
                    alignItems: "stretch",
                  }}
                >
                  <Button
                    onClick={() => handleResolve(selectedRow)}
                    disabled={!selectedRow.isTripActive}
                    startIcon={<CheckCircleIcon sx={{ fontSize: "1.1rem !important" }} />}
                    sx={{
                      height: 44,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      bgcolor: selectedRow.isTripActive ? "#16a34a" : "#f1f5f9",
                      color: selectedRow.isTripActive ? "#ffffff !important" : "#94a3b8 !important",
                      boxShadow: selectedRow.isTripActive ? "0 2px 8px rgba(22, 163, 74, 0.25)" : "none",
                      border: selectedRow.isTripActive ? "none" : "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                      px: 1.5,
                      "&:hover": {
                        bgcolor: selectedRow.isTripActive ? "#15803d" : "#f1f5f9",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "#f1f5f9",
                        color: "#94a3b8 !important",
                      },
                    }}
                  >
                    {selectedRow.isTripActive ? "Mark as Resolved" : "Already Resolved"}
                  </Button>

                  <Button
                    onClick={() => handleManualCheck(selectedRow)}
                    disabled={!selectedRow.isTripActive}
                    startIcon={<CheckCircleOutlineRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
                    sx={{
                      height: 44,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      bgcolor: "#fffbeb",
                      color: "#b45309 !important",
                      border: "1.5px solid #fde68a",
                      whiteSpace: "nowrap",
                      px: 1.5,
                      "&:hover": {
                        bgcolor: "#fef3c7",
                        borderColor: "#f59e0b",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "#f8fafc",
                        color: "#cbd5e1 !important",
                        borderColor: "#e2e8f0",
                      },
                    }}
                  >
                    Wellbeing Check
                  </Button>

                  <Button
                    onClick={() =>
                      navigate(`/LiveTracking/${selectedRow.userId}`, {
                        state: {
                          escort: selectedRow,
                          tripData: selectedRow?.tripData,
                          isTripActive: selectedRow?.isTripActive,
                          status: selectedRow?.tripStatus,
                          wellbeing: selectedRow?.wellbeing,
                          userFBK: selectedRow?.userFBK,
                        },
                      })
                    }
                    startIcon={<MapOutlinedIcon sx={{ fontSize: "1.1rem !important" }} />}
                    sx={{
                      height: 44,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      bgcolor: "#eff6ff",
                      color: "#1d4ed8 !important",
                      border: "1.5px solid #bfdbfe",
                      whiteSpace: "nowrap",
                      px: 1.5,
                      "&:hover": {
                        bgcolor: "#dbeafe",
                        borderColor: "#3b82f6",
                      },
                    }}
                  >
                    View Live Map
                  </Button>

                  <Button
                    onClick={() => window.open(`tel:${selectedRow.phone}`)}
                    startIcon={<PhoneOutlinedIcon sx={{ fontSize: "1.1rem !important" }} />}
                    sx={{
                      height: 44,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      bgcolor: "#f0fdf4",
                      color: "#15803d !important",
                      border: "1.5px solid #bbf7d0",
                      whiteSpace: "nowrap",
                      px: 1.5,
                      "&:hover": {
                        bgcolor: "#dcfce7",
                        borderColor: "#22c55e",
                      },
                    }}
                  >
                    Call User
                  </Button>
                </Box>
              </Box>
          </Box>
        )}
      </Dialog>

      {/* ── 9. EVIDENCE & WELLBEING SUBMISSIONS MODAL ── */}
      <Dialog
        open={evidenceModalOpen}
        onClose={() => {
          setEvidenceModalOpen(false);
          setSelectedEvidenceRow(null);
          setImageRotation(0);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ color: "#0f172a" }}>
              {selectedEvidenceRow?.videos?.length > 0 && evidenceFileUrl
                ? "Evidence & Wellbeing Submissions"
                : selectedEvidenceRow?.videos?.length > 0
                ? "Wellbeing Submissions"
                : "Vehicle / Evidence File"}
            </Typography>
            {selectedEvidenceRow?.name && (
              <Chip
                label={selectedEvidenceRow.name}
                size="small"
                sx={{ bgcolor: "#f1f5f9", fontWeight: 700, color: "#334155" }}
              />
            )}
          </Box>
          <IconButton
            onClick={() => {
              setEvidenceModalOpen(false);
              setSelectedEvidenceRow(null);
              setImageRotation(0);
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        {/* Tab Selection if both vehicle image and wellbeing submissions exist */}
        {selectedEvidenceRow?.videos?.length > 0 && evidenceFileUrl && (
          <Box sx={{ px: 3, pt: 1, borderBottom: "1px solid #e2e8f0" }}>
            <Tabs
              value={evidenceTab}
              onChange={(e, val) => {
                setEvidenceTab(val);
                setImageRotation(0);
              }}
              textColor="primary"
              indicatorColor="primary"
              sx={{ minHeight: "40px" }}
            >
              <Tab
                label={`Wellbeing Submissions (${selectedEvidenceRow.videos.length})`}
                value="submissions"
                sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.85rem" }}
              />
              <Tab
                label="Vehicle Evidence File"
                value="vehicle"
                sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.85rem" }}
              />
            </Tabs>
          </Box>
        )}

        <DialogContent dividers sx={{ p: 2 }}>
          {/* Submissions Tab (Videos, Audios, Saved Locations) */}
          {(evidenceTab === "submissions" || !evidenceFileUrl) && selectedEvidenceRow?.videos?.length > 0 ? (
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
              <Table aria-label="submissions table">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Type
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Latitude
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Longitude
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Address / Map
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Media / Content
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Time
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEvidenceRow.videos.map((item, index) => {
                    const lat = item?.newCurrentLat ?? item?.latitude ?? item?.lat ?? "—";
                    const lng = item?.newCurrentLong ?? item?.longitude ?? item?.lng ?? "—";
                    const mediaUrl = item?.videoUrl || item?.audioUrl || item?.url || "";
                    const type = getSubmissionType(item);
                    const hasCoords = lat !== "—" && lng !== "—";
                    const mapUrl = hasCoords ? `https://www.google.com/maps?q=${lat},${lng}` : null;
                    const dateStr = item?.createdAt || item?.timestamp
                      ? new Date(item.createdAt || item.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                      : "—";

                    return (
                      <TableRow key={index} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        {/* Type Chip */}
                        <TableCell align="center" sx={{ padding: "10px" }}>
                          {type === "location" ? (
                            <Chip
                              icon={<PlaceIcon fontSize="small" />}
                              label="Location"
                              size="small"
                              sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700 }}
                            />
                          ) : type === "audio" ? (
                            <Chip
                              icon={<AudiotrackIcon fontSize="small" />}
                              label="Audio"
                              size="small"
                              sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 700 }}
                            />
                          ) : (
                            <Chip
                              icon={<VideocamIcon fontSize="small" />}
                              label="Video"
                              size="small"
                              sx={{ bgcolor: "#fee2e2", color: "#b91c1c", fontWeight: 700 }}
                            />
                          )}
                        </TableCell>

                        {/* Latitude */}
                        <TableCell align="center" sx={{ padding: "10px", fontWeight: 600 }}>
                          {lat}
                        </TableCell>

                        {/* Longitude */}
                        <TableCell align="center" sx={{ padding: "10px", fontWeight: 600 }}>
                          {lng}
                        </TableCell>

                        {/* Address / Google Maps */}
                        <TableCell align="center" sx={{ padding: "10px" }}>
                          {mapUrl ? (
                            <Button
                              variant="outlined"
                              size="small"
                              href={mapUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              endIcon={<OpenInNewIcon fontSize="small" />}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                py: 0.3,
                                px: 1.2,
                                borderColor: "#cbd5e1",
                                color: "#1e40af",
                              }}
                            >
                              Open in Maps
                            </Button>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        {/* Media / Content */}
                        <TableCell align="center" sx={{ padding: "10px" }}>
                          {type === "audio" && mediaUrl ? (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                              <audio controls src={mediaUrl} style={{ width: "190px", height: "32px" }} />
                              <a
                                href={mediaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: "0.72rem", color: "#1e40af", textDecoration: "underline" }}
                              >
                                Direct Audio Link
                              </a>
                            </Box>
                          ) : type === "video" && mediaUrl ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                              <a href={mediaUrl} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={videoImg}
                                  alt="video-thumbnail"
                                  style={{
                                    width: "42px",
                                    height: "42px",
                                    cursor: "pointer",
                                    borderRadius: "4px",
                                  }}
                                />
                              </a>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => window.open(mediaUrl, "_blank")}
                                sx={{ textTransform: "none", fontSize: "0.75rem", color: "#dc2626", fontWeight: 600 }}
                              >
                                Play
                              </Button>
                            </Box>
                          ) : type === "location" ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                              <PlaceIcon sx={{ color: "#0284c7", fontSize: "1.2rem" }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "#0369a1" }}>
                                {item?.title || item?.feedback || "Location Shared"}
                              </Typography>
                            </Box>
                          ) : mediaUrl ? (
                            <a
                              href={mediaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#1e40af", fontWeight: 600, fontSize: "0.8rem" }}
                            >
                              View Media
                            </a>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        {/* Timestamp */}
                        <TableCell align="center" sx={{ padding: "10px", fontSize: "0.75rem", color: "#64748b" }}>
                          {dateStr}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : evidenceFileUrl ? (
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
              {/* Image Controls toolbar */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "8px",
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                  Vehicle / Evidence Photo
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title="Rotate Left (-90°)">
                    <IconButton
                      size="small"
                      onClick={() => setImageRotation((prev) => (prev - 90 + 360) % 360)}
                      sx={{ color: "#334155", bgcolor: "#ffffff", border: "1px solid #cbd5e1", "&:hover": { bgcolor: "#f1f5f9" } }}
                    >
                      <RotateLeftIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Rotate Right (+90°)">
                    <IconButton
                      size="small"
                      onClick={() => setImageRotation((prev) => (prev + 90) % 360)}
                      sx={{ color: "#334155", bgcolor: "#ffffff", border: "1px solid #cbd5e1", "&:hover": { bgcolor: "#f1f5f9" } }}
                    >
                      <RotateRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {imageRotation !== 0 && (
                    <Button
                      size="small"
                      onClick={() => setImageRotation(0)}
                      sx={{
                        fontSize: "0.75rem",
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#475569",
                        minWidth: "auto",
                        px: 1,
                      }}
                    >
                      Reset
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Image Container */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 280,
                  maxHeight: "52vh",
                  overflow: "hidden",
                  borderRadius: "8px",
                  bgcolor: "#f1f5f9",
                  p: 1.5,
                  border: "1px solid #e2e8f0",
                }}
              >
                <Box
                  component="img"
                  src={evidenceFileUrl}
                  alt="Plate / Evidence"
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "48vh",
                    borderRadius: "6px",
                    objectFit: "contain",
                    transform: `rotate(${imageRotation}deg)`,
                    transition: "transform 0.25s ease-in-out",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Typography sx={{ color: "#9ca3af", textAlign: "center", py: 3 }}>
              No evidence file or submissions attached
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
          {selectedEvidenceRow ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<MapOutlinedIcon fontSize="small" />}
              onClick={() => {
                navigate(`/LiveTracking/${selectedEvidenceRow.userId || selectedEvidenceRow.actualTripId || selectedEvidenceRow.escortId}`, {
                  state: {
                    escort: selectedEvidenceRow,
                    tripData: selectedEvidenceRow?.tripData || selectedEvidenceRow,
                    isTripActive: selectedEvidenceRow?.isTripActive,
                    wellbeing: selectedEvidenceRow?.wellbeing,
                    userFBK: selectedEvidenceRow?.userFBK,
                  },
                });
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.82rem",
                color: "#1d4ed8 !important",
                borderColor: "#93c5fd !important",
                bgcolor: "#eff6ff",
                px: 1.8,
                py: 0.6,
                "&:hover": {
                  bgcolor: "#dbeafe",
                  borderColor: "#3b82f6 !important",
                },
              }}
            >
              Open in Live Tracking
            </Button>
          ) : <Box />}
          <Box sx={{ display: "flex", gap: 1 }}>
            {evidenceFileUrl && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon fontSize="small" />}
                onClick={() => window.open(evidenceFileUrl, "_blank")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  color: "#334155 !important",
                  borderColor: "#cbd5e1 !important",
                  bgcolor: "#ffffff",
                  px: 1.8,
                  py: 0.6,
                  "&:hover": {
                    bgcolor: "#f1f5f9",
                    borderColor: "#94a3b8 !important",
                  },
                }}
              >
                Open Vehicle Image in New Tab
              </Button>
            )}
            <Button
              onClick={() => {
                setEvidenceModalOpen(false);
                setSelectedEvidenceRow(null);
                setImageRotation(0);
              }}
              variant="contained"
              sx={{
                color: "#ffffff !important",
                bgcolor: "#334155 !important",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.82rem",
                px: 2.4,
                py: 0.6,
                "&:hover": {
                  bgcolor: "#1e293b !important",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── 10. PUSH NOTIFICATION MODAL ── */}
      {openPushModal && (
        <Dialog
          open={openPushModal}
          onClose={() => {
            setOpenPushModal(false);
            setSelectedRow(null);
          }}
          fullWidth
        >
          <DialogTitle>Send Push Notification to User</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Select Notification Template</InputLabel>
              <Select value={dropdownValue} label="Select Notification Template" onChange={(e) => setDropdownValue(e.target.value)}>
                {pushNotificationOptions.map((opt, i) => (
                  <MenuItem key={opt.id || i} value={opt.id || opt.title || opt.action}>
                    {opt.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenPushModal(false);
                setSelectedRow(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ color: "#fff" }}
              onClick={() => {
                const opt = pushNotificationOptions.find((o) => (o.id && o.id === dropdownValue) || o.title === dropdownValue || o.action === dropdownValue) || {};
                const targetUserId = selectedRow?.userId;
                const actionName = opt.action || "notification";
                const titleName = opt.title || "Notification";
                const msgName = opt.body || opt.message || "Notification Alert";

                const tripIdVal = selectedRow?.actualTripId || selectedRow?.id || selectedRow?.escortDocumentId;

                // 1. Emit push notification event with complete escort metadata
                socketService.emit("sendcustompushnotification", {
                  userId: targetUserId,
                  action: actionName,
                  title: titleName,
                  message: msgName,
                  escortId: selectedRow?.escortId,
                  tripId: tripIdVal,
                  userName: selectedRow?.name,
                  userPhone: selectedRow?.phone,
                  adminName: adminName,
                  source: "escort",
                  fromEscort: "true",
                  vibrate: true,
                });

                // 2. Also save to Database via REST API endpoint
                apiClient
                  .post("/virtual-escort/admin/send-push", {
                    userId: targetUserId,
                    escortId: selectedRow?.escortId,
                    tripId: tripIdVal,
                    action: actionName,
                    title: titleName,
                    message: msgName,
                    userName: selectedRow?.name,
                    userPhone: selectedRow?.phone,
                    adminName: adminName,
                  })
                  .catch((err) => {
                    console.warn("HTTP send-push notice (socket was sent):", err?.message);
                  });

                // 3. ONLY if the action is a Wellbeing Check, trigger manual wellbeing check & timer
                const isWellbeingAction = actionName === "wellbeingcheck" || String(actionName).toLowerCase().includes("wellbeing");
                if (isWellbeingAction) {
                  const nowMs = Date.now();
                  socketService.emit("manual_wellbeing_check", {
                    escortId: selectedRow?.escortId,
                    tripId: tripIdVal,
                    userId: targetUserId,
                    state: selectedRow?.state,
                    name: selectedRow?.name,
                    phone: selectedRow?.phone,
                    userName: selectedRow?.name,
                    userPhone: selectedRow?.phone,
                    adminName: adminName,
                    source: "escort",
                    fromEscort: "true",
                    vibrate: true,
                    action: actionName,
                    title: titleName,
                    message: msgName,
                  });

                  if (targetUserId) {
                    try {
                      const db = getDatabase();
                      update(ref(db, `users/${targetUserId}`), {
                        Wellbeing_Response: "Awaiting Response",
                        wellbeingResponse: "Awaiting Response",
                        wellbeingCheckSource: "Sent by Escort Staff",
                        wellbeingSentAt: nowMs,
                        wellbeingTripId: String(tripIdVal || ""),
                        wellbeingPushStatus: "Delivered",
                        userFBK: null,
                        userFeedback: null,
                        feedback: null,
                      });
                    } catch (e) {
                      console.warn("Firebase update notice:", e);
                    }

                    // Update local state immediately so timer starts right away
                    setFirebaseUsers((prev) => ({
                      ...prev,
                      [targetUserId]: {
                        ...(prev[targetUserId] || {}),
                        Wellbeing_Response: "Awaiting Response",
                        wellbeingResponse: "Awaiting Response",
                        wellbeingCheckSource: "Sent by Escort Staff",
                        wellbeingSentAt: nowMs,
                        wellbeingTripId: String(tripIdVal || ""),
                        wellbeingPushStatus: "Delivered",
                        userFBK: null,
                        userFeedback: null,
                        feedback: null,
                      },
                    }));

                    // Update allData to clear old feedback and set Awaiting Response
                    setAllData((prev) =>
                      prev.map((item) => {
                        const itemTripId = String(item.actualTripId || item._id || item.id || item.escortDocumentId || item.tripData?._id || "");
                        const itemUserId = String(item.userId || item.tripData?.userId || "");
                        if (item.isTripActive && (itemTripId === String(tripIdVal) || itemUserId === String(targetUserId))) {
                          return {
                            ...item,
                            wellbeing: "Awaiting Response",
                            wellbeingSentAtMs: nowMs,
                            userFBK: null,
                            tripData: {
                              ...item.tripData,
                              Wellbeing_Response: "Awaiting Response",
                              wellbeingResponse: "Awaiting Response",
                              wellbeingSentAt: nowMs,
                              userFBK: null,
                              userFeedback: null,
                              feedback: null,
                            },
                            userData: {
                              ...item.userData,
                              Wellbeing_Response: "Awaiting Response",
                              wellbeingResponse: "Awaiting Response",
                              wellbeingSentAt: nowMs,
                              userFBK: null,
                              userFeedback: null,
                              feedback: null,
                            },
                          };
                        }
                        return item;
                      })
                    );
                  }

                  if (tripIdVal) {
                    setStoredAwaitingResponse(String(tripIdVal), nowMs);
                    setStoredWellbeingSentAt(String(tripIdVal), nowMs);
                    setStoredResponseDuration(String(tripIdVal), null);
                  }
                  if (targetUserId) {
                    setStoredAwaitingResponse(String(targetUserId), null);
                    setStoredWellbeingSentAt(String(targetUserId), null);
                    setStoredResponseDuration(String(targetUserId), null);
                  }
                }

                toast.success(isWellbeingAction ? "Wellbeing check sent and response timer started" : "Push notification sent successfully");
                setOpenPushModal(false);
                setSelectedRow(null);
              }}
            >
              Send Push
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── 11. SEND MESSAGE & EMERGENCY MODALS ── */}
      {openSendMessage && (
        <SendMessageModal
          open={openSendMessage}
          setOpen={(v) => {
            setOpenMessage(v);
            if (!v) setSelectedRow(null);
          }}
          userId={selectedRow?.userId}
        />
      )}
      <NotifyEmergencyContactModal
        open={notifyEmergencyOpen}
        handleClose={() => {
          setNotifyEmergencyOpen(false);
          setSelectedRow(null);
        }}
        selectedRow={selectedRow}
      />

      {/* ── 12. ESCORT PUSH NOTIFICATION & RESPONSE HISTORY MODAL ── */}
      <Dialog
        open={notificationHistoryOpen}
        onClose={() => {
          setNotificationHistoryOpen(false);
          setSelectedNotificationRow(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 2,
            bgcolor: "#f8fafc",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: "#0f172a", display: "flex", alignItems: "center", gap: 1 }}>
              <NotificationsActiveIcon sx={{ color: "#1565c0" }} />
              Push Notification & Response History
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {selectedNotificationRow?.name} ({selectedNotificationRow?.phone || "N/A"}) · Escort ID: {selectedNotificationRow?.escortId || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Refresh Logs">
              <IconButton
                size="small"
                onClick={() => fetchNotificationLogs(selectedNotificationRow)}
                disabled={loadingNotifications}
                sx={{ bgcolor: "#ffffff", border: "1px solid #cbd5e1" }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => {
                setNotificationHistoryOpen(false);
                setSelectedNotificationRow(null);
              }}
              sx={{ bgcolor: "#e2e8f0" }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {loadingNotifications ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <LinearProgress sx={{ maxWidth: 200, mx: "auto", mb: 2 }} />
              <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>Loading notification logs from database...</Typography>
            </Box>
          ) : notificationLogs.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.95rem" }}>
                No Push Notifications Recorded
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: "0.82rem", mt: 0.5 }}>
                There are currently no push notifications or user responses logged in the database for this escort.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {notificationLogs.map((log, index) => {
                const isAwaiting = log.status === "AWAITING_RESPONSE" || log.status === "SENT";
                const isDanger = log.responseStatus === "Danger" || String(log.userResponse).toLowerCase().includes("danger");
                const hasResponded = Boolean(log.userResponse || log.respondedAt || log.status === "RESPONDED");

                let statusBadgeBg = "#dcfce7";
                let statusBadgeColor = "#15803d";
                let statusBadgeBorder = "#86efac";
                let statusBadgeLabel = "Responded";

                if (isDanger) {
                  statusBadgeBg = "#fee2e2";
                  statusBadgeColor = "#b91c1c";
                  statusBadgeBorder = "#fca5a5";
                  statusBadgeLabel = "🚨 In Danger";
                } else if (isAwaiting) {
                  statusBadgeBg = "#fef3c7";
                  statusBadgeColor = "#b45309";
                  statusBadgeBorder = "#fde68a";
                  statusBadgeLabel = "⏳ Awaiting Response";
                } else if (log.status === "NO_RESPONSE") {
                  statusBadgeBg = "#fee2e2";
                  statusBadgeColor = "#b91c1c";
                  statusBadgeBorder = "#fca5a5";
                  statusBadgeLabel = "✕ No Response";
                } else if (hasResponded) {
                  statusBadgeBg = "#dcfce7";
                  statusBadgeColor = "#15803d";
                  statusBadgeBorder = "#86efac";
                  statusBadgeLabel = "✓ Safe / Responded";
                }

                return (
                  <Paper
                    key={log._id || log.notificationId || index}
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: "#ffffff",
                      borderRadius: "12px",
                      border: `1.5px solid ${isDanger ? "#fca5a5" : "#e2e8f0"}`,
                      borderLeft: `5px solid ${isDanger ? "#dc2626" : isAwaiting ? "#f59e0b" : "#16a34a"}`,
                    }}
                  >
                    {/* Header Row */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1, mb: 1.2 }}>
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#1e293b" }}>
                            {log.title || "Wellbeing Check"}
                          </Typography>
                          <Chip
                            label={statusBadgeLabel}
                            size="small"
                            sx={{
                              bgcolor: statusBadgeBg,
                              color: statusBadgeColor,
                              border: `1px solid ${statusBadgeBorder}`,
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              height: 22,
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: "0.78rem", color: "#64748b", mt: 0.3 }}>
                          Sent by: <strong style={{ color: "#334155" }}>{log.adminName || "Escort Staff"}</strong> · {fmtDateTime(log.sentAt)}
                        </Typography>
                      </Box>

                      {log.notificationId && (
                        <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "monospace", fontSize: "0.72rem" }}>
                          ID: {log.notificationId}
                        </Typography>
                      )}
                    </Box>

                    {/* Message Body */}
                    <Box sx={{ p: 1.2, bgcolor: "#f8fafc", borderRadius: "8px", border: "1px solid #f1f5f9", mb: 1.5 }}>
                      <Typography sx={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                        Push Notification Message
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem", color: "#334155", fontWeight: 500, mt: 0.2 }}>
                        "{log.message || "Please Confirm Your Safety"}"
                      </Typography>
                    </Box>

                    {/* User Response Section */}
                    {hasResponded ? (
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: "10px",
                          bgcolor: isDanger ? "#fff5f5" : "#f0fdf4",
                          border: `1px solid ${isDanger ? "#fecaca" : "#bbf7d0"}`,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.6,
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: isDanger ? "#b91c1c" : "#15803d", textTransform: "uppercase" }}>
                            User Response Received:
                          </Typography>
                          {log.responseTimeSeconds !== null && log.responseTimeSeconds !== undefined && (
                            <Chip
                              label={`Response latency: ${log.responseTimeSeconds}s`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                bgcolor: "rgba(0,0,0,0.05)",
                                color: "#475569",
                              }}
                            />
                          )}
                        </Box>

                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: isDanger ? "#dc2626" : "#16a34a" }}>
                          "{log.userResponse || "Safe"}"
                        </Typography>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 0.4 }}>
                          <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                            Responded at: {fmtDateTime(log.respondedAt)}
                          </Typography>

                          {(log.latitude || log.longitude) && (
                            <Button
                              size="small"
                              variant="outlined"
                              href={`https://www.google.com/maps?q=${log.latitude},${log.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<PlaceIcon fontSize="small" />}
                              sx={{
                                py: 0.2,
                                px: 1,
                                fontSize: "0.72rem",
                                textTransform: "none",
                                fontWeight: 600,
                              }}
                            >
                              Location: {Number(log.latitude).toFixed(4)}, {Number(log.longitude).toFixed(4)}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: "8px",
                          bgcolor: "#fffbeb",
                          border: "1px solid #fde68a",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.82rem", color: "#92400e", fontWeight: 600 }}>
                          ⏳ Waiting for user response...
                        </Typography>
                        {log.sentAt && (
                          <Typography sx={{ fontSize: "0.75rem", color: "#b45309" }}>
                            Sent {Math.round((Date.now() - new Date(log.sentAt).getTime()) / 60000)}m ago
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: "#f8fafc" }}>
          <Button
            onClick={() => {
              setNotificationHistoryOpen(false);
              setSelectedNotificationRow(null);
            }}
            variant="contained"
            sx={{ color: "#ffffff !important", bgcolor: "#334155 !important" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 12. CONFIRM DELETE ESCORT MODAL ── */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteConfirmOpen(false);
            setRowToDelete(null);
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            border: "1px solid #fee2e2",
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: "center", position: "relative", overflowY: "auto" }}>
          {/* Close button */}
          <IconButton
            onClick={() => {
              if (!isDeleting) {
                setDeleteConfirmOpen(false);
                setRowToDelete(null);
              }
            }}
            disabled={isDeleting}
            size="small"
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              color: "#94a3b8",
              "&:hover": { color: "#475569", bgcolor: "#f1f5f9" },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>

          {/* Delete Icon Badge */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "#fee2e2",
              border: "6px solid #fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px auto",
              color: "#dc2626",
            }}
          >
            <DeleteForeverRoundedIcon sx={{ fontSize: 34 }} />
          </Box>

          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
            Delete Escort Trip?
          </Typography>

          <Typography sx={{ fontSize: "0.88rem", color: "#64748b", mb: 2, px: 1 }}>
            Are you sure you want to delete{" "}
            <Typography
              component="span"
              sx={{ fontWeight: 700, color: "#1e293b", fontSize: "inherit" }}
            >
              {rowToDelete?.name || selectedRow?.name || "this user"}
            </Typography>
            &apos;s escort trip?
          </Typography>

          {/* Trip Summary Card */}
          {(rowToDelete || selectedRow) && (
            <Box
              sx={{
                bgcolor: "#f8fafc",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                p: 2,
                mb: 2,
                textAlign: "left",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8, alignItems: "center" }}>
                <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                  Escort ID
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "#334155", fontWeight: 700, fontFamily: "monospace" }}>
                  {rowToDelete?.escortId || rowToDelete?.actualTripId || selectedRow?.escortId || "—"}
                </Typography>
              </Box>

              {(rowToDelete?.type || selectedRow?.type) && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8, alignItems: "center" }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                    Category
                  </Typography>
                  <Chip
                    label={rowToDelete?.type || selectedRow?.type}
                    size="small"
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      height: 22,
                      bgcolor: "#e0f2fe",
                      color: "#0369a1",
                    }}
                  />
                </Box>
              )}

              {(rowToDelete?.destinationCity || rowToDelete?.deliveryLocation || selectedRow?.destinationCity || selectedRow?.deliveryLocation) && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                    Destination
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "#334155",
                      fontWeight: 600,
                      maxWidth: "180px",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {rowToDelete?.destinationCity || rowToDelete?.deliveryLocation || selectedRow?.destinationCity || selectedRow?.deliveryLocation}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Wellbeing Check Status & Guard Banner */}
          {(() => {
            const targetDel = rowToDelete || selectedRow;
            const delWb = String(
              targetDel?.Wellbeing_Response ||
              targetDel?.wellbeingResponse ||
              targetDel?.tripData?.wellbeingResponse ||
              targetDel?.tripData?.Wellbeing_Response ||
              ""
            ).trim();
            const delFbk = String(
              targetDel?.userFBK ||
              targetDel?.userFeedback ||
              targetDel?.tripData?.userFBK ||
              ""
            ).trim();
            const isWellbeingReceived = Boolean(
              delWb === "Responded" ||
              delWb === "Danger" ||
              delWb === "Safe" ||
              delWb === "Verified" ||
              targetDel?.tripData?.wellbeingStatus === "SAFE" ||
              targetDel?.tripData?.wellbeingStatus === "UNSAFE" ||
              (delFbk && delFbk !== "No Feedback" && delFbk !== "null" && delFbk !== "undefined" && delFbk !== "false")
            );

            if (!isWellbeingReceived) {
              return (
                <Box
                  sx={{
                    bgcolor: "#fffbeb",
                    border: "1.5px solid #fcd34d",
                    borderRadius: "12px",
                    p: 2,
                    mb: 2.5,
                    textAlign: "left",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                    <WarningAmberRoundedIcon sx={{ color: "#d97706", fontSize: 22 }} />
                    <Typography sx={{ fontWeight: 700, color: "#92400e", fontSize: "0.88rem" }}>
                      Wellbeing Check Pending
                    </Typography>
                  </Box>
                  <Typography sx={{ color: "#78350f", fontSize: "0.8rem", lineHeight: 1.4, mb: 1.5 }}>
                    The user has not yet submitted safety confirmation. You can prompt them with a push notification, or end/delete the trip directly as an administrator below.
                  </Typography>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    onClick={() => handleManualCheck(targetDel)}
                    sx={{
                      borderColor: "#d97706 !important",
                      color: "#b45309 !important",
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "8px",
                      fontSize: "0.82rem",
                      py: 0.8,
                      "&:hover": { bgcolor: "#fef3c7 !important", borderColor: "#b45309 !important" },
                    }}
                  >
                    📱 Send Push Notification for Safety Confirmation
                  </Button>
                </Box>
              );
            }

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "10px",
                  p: 1.4,
                  mb: 2,
                  gap: 1.2,
                }}
              >
                <CheckCircleOutlineRoundedIcon sx={{ color: "#16a34a", fontSize: 22, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.8rem", color: "#166534", textAlign: "left", lineHeight: 1.3 }}>
                  <strong>Wellbeing check confirmed:</strong> {delFbk || delWb || "Safe"}. Safety status verified.
                </Typography>
              </Box>
            );
          })()}

          {/* Warning Banner */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "#fff1f2",
              border: "1px dashed #fecdd3",
              borderRadius: "10px",
              p: 1.2,
              mb: 3,
              gap: 1,
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: "#e11d48", fontSize: 20, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "0.76rem", color: "#9f1239", textAlign: "left", lineHeight: 1.3 }}>
              This will end or delete the escort record and stop all active monitoring.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
            <Box sx={{ display: "flex", gap: 1.2 }}>
              <Button
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setRowToDelete(null);
                }}
                disabled={isDeleting}
                variant="outlined"
                fullWidth
                sx={{
                  borderRadius: "10px",
                  borderColor: "#e2e8f0",
                  color: "#475569",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  py: 1,
                  "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "10px",
                  bgcolor: "#dc2626 !important",
                  color: "#ffffff !important",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  py: 1,
                  boxShadow: "0 4px 12px rgba(220, 38, 38, 0.35)",
                  "&:hover": { bgcolor: "#b91c1c !important" },
                  "&.Mui-disabled": { bgcolor: "#fca5a5 !important", color: "#fff !important" },
                }}
              >
                {isDeleting ? (
                  <>
                    <CircularProgress size={18} sx={{ color: "#ffffff", mr: 1 }} />
                    Deleting...
                  </>
                ) : (
                  "Delete Escort"
                )}
              </Button>
            </Box>

            <Button
              onClick={async () => {
                setDeleteConfirmOpen(false);
                await handleCloseTrip(rowToDelete || selectedRow);
                setRowToDelete(null);
              }}
              disabled={isDeleting}
              variant="contained"
              fullWidth
              sx={{
                borderRadius: "10px",
                bgcolor: "#16a34a !important",
                color: "#ffffff !important",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.88rem",
                py: 1,
                boxShadow: "0 4px 12px rgba(22, 163, 74, 0.35)",
                "&:hover": { bgcolor: "#15803d !important" },
              }}
            >
              ✓ End & Complete Trip
            </Button>
          </Box>
        </Box>
      </Dialog>
    </DashboardLayout>
  );
};

export default React.memo(Escort);
